// src/ui/ChatWindow.jsx — the conversation pane: header, message list, all message
// type renderers, and the composer. This is where E2EE decryption meets the UI.
import React, { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback, useContext, useSyncExternalStore } from 'react';
import SupporterBadge from './SupporterBadge';   // 💛 the Supporter badge, next to the name
import { bindDrafts, getDraft, setDraft } from '../services/drafts';   // 📝 unsent text, kept per chat
import { Back, Send, Smile, Paperclip, Lock, Forward, Reply, Play, X, Alert, Camera, Download, CloudOff, Verified, MapPin, Slash, Mic, Poll, DocText,
  Pencil, PersonAdd, PersonRemove, Group as GroupIcon, Gear, Timer, Info, Logout, Mail, ShieldCheck, ShieldLock, Image as ImageIcon,
  Menu, Star, StarFill, Trash, Search as SearchIcon, Check, Sparkles, Heart, Comment, ArrowRight, Link as LinkIcon, ChevronRight } from './icons';
import EmojiPicker from 'emoji-picker-react';
import { watchMessages, otherParticipant, clearedAtMs, isNoteToSelf, isDeletedFor, readUpToMs, countsAsUnread } from '../services/chats';
import { fetchFloorMs, joinedMsOf } from '../services/joinFloor';   // 👥 a member added later starts at their join
import { decryptMessage, sendTextMessage, loadBatchItem } from '../services/messages';
import { reportDelivered, reportSeen, markChatRead } from '../services/receipts';
import { watchReadReceipts, OFF_UNKNOWN } from '../services/readReceipts';
import { watchVibeGate } from '../services/vibes';
import SafeImg from './SafeImg';
import PrivateCover, { useCovered } from './PrivateCover';
// relockAll isn't imported: privateMedia wires it to visibilitychange/blur itself.
import { watchPrivateMedia, isCovered as pmCovered } from '../services/privateMedia';
import { acceptGroupInvite, declineGroupInvite, inviteStatusOf } from '../services/groupCreate';
import { isMediaCleared } from '../services/chatClear';
import { mergeChatHistory, isExportedEntry, retainLive, forgetRetained } from '../services/historySync';
import { mirrorOut, MIRROR_MAX_AGE_MS } from '../services/mirrorOut';
import { normalizeSynced, mediaSig, hasCloudSource, fullyResolvedMedia, hasResolvedMedia, mergeResolved } from '../services/mediaResolve';
/* 🖼 a dead object URL is healed from the store before it is treated as a
   failure — see crypto/media.js and services/mediaStore.js */
import { remintMedia, mediaFailure, GONE } from '../crypto/media';
import { getIdentity } from '../crypto/identity';
import { getUserProfile, watchPresence, watchUserProfile } from '../services/users';
import { derivePresenceVisibility, isViewerInTargetContacts } from '../services/presenceVisibility';
import { timeLabel, dayLabel, lastSeenLabel } from './format';
import Avatar, { resolveName, groupTitle } from './Avatar';
import BubbleTail from './BubbleTail';
/* 🖼 the box a photo gets before its bytes land — and the shape it turned out to
   be, remembered, so re-opening a chat never draws a vertical photo squat and
   then snaps it upright. See services/mediaAspect.js. */
import { fitBox, rememberAspect, recallAspect } from '../services/mediaAspect';
/* 👍 an announcement's reactions belong to everybody who got it, and a broadcast
   is N COPIES of one message — see services/broadcastReactions.js */
import { subscribeBroadcastCounts, peekBroadcastCounts, reactionDisplay } from '../services/broadcastReactions';
/* 👍 who reacted, and which reaction each person holds — generated from the phone's
   services/reactionRoster.ts so the two clients cannot disagree about the tie-break */
import { rosterFor, rowsForTab, reactionTapIntent } from '../services/reactionRoster';
import { toggleReaction, REACTIONS } from '../services/guestActions';
import MessageTicks, { deriveReceiptStatus } from './MessageTicks';
import { tsOf } from '../services/historySync';
import { prepareFile, localPreviewUrl, durationLabel, isImageFile, isVideoFile } from '../services/mediaTools';
import { sendMediaMessage, newTempId } from '../services/mediaUpload';
import { sendLocationMessage, mapsUrlFor } from '../services/location';
import { useLiveFix } from '../services/liveFollow';
import { liveAge, liveExpired, toMillis as liveToMillis } from '../services/liveRules';
import StaticMap from './StaticMap';
import LocationPicker from './LocationPicker';
import VoiceRecorder from './VoiceRecorder';
import VoiceBubble from './VoiceBubble';
import { sendVoiceMessage, canRecordVoice } from '../services/voice';
import { subscribeCalls, getCallState, startCall, pullCallHere } from '../services/calls';
import { startGroupCall, joinGroupCall, groupCallBlockedReason, getGroupCallState } from '../services/groupCalls';
import CallChatHistory from './CallChatHistory';
import { loadCallThread, peekCallThread, subscribeCallThreads } from '../services/callThreads';
import { getCallRows, subscribeCallRows } from '../services/localCallRows';
import { nameColor } from './nameColor';
import { orderMessages, showsLateSendTime } from '../services/messageOrder';
import PollCreator from './PollCreator';
import PollBubble from './PollBubble';
import { createPoll, isPollSideEffect } from '../services/polls';
import { renderCallBubble, renderGroupCallBubble, callInputs, isCallMessage } from '../services/callRender';
import TypingBubble from './TypingBubble';
import { watchTyping, publishTyping, stopTyping, clearTypingFor } from '../services/typing';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneOff, VideoOff, Video, ChevronsDown, Smartphone } from './icons';
import { originBadge } from '../services/messageOrigin'; // 🖥📱 a phone on what I sent from the app
import { isGuestChat, guestChatExpired, timeLeftLabel as guestLeftLabel, watchGuestLinkForChat } from '../services/guestLinks';
import { isOfficialChat, openSupport, isOfficialUid } from '../services/support';
/* ©️ the author's copyright dispute in this thread — a text message wearing a card */
import { DisputeCard } from './RightsMeta';
import SupportPanel from './SupportPanel';
import GuestCard from './GuestCard';
import DocBubble from './DocBubble';
import { buildMediaRuns, newRunVisit, admitRows, runKindInVisit, albumVisibleIndices, isSettledIn, runReplySource, albumItemReplySource, batchPages, isWaitingPage } from '../services/mediaRuns';
import { settledFor, settleRuns } from '../services/runSettled'; // 🧩 single photos/videos/docs drawn together; album items one by one
import FlightCard from './FlightCard';
import FlightPreview from './FlightPreview';
import ReplyQuote from './ReplyQuote';
import ReplyComposer from './ReplyComposer';
import LinkCard from './LinkCard';
import { firstUrl, cachedLink, fetchLink, backfillLink, watchLinkUpdates, normalizeLink } from '../services/links';
import { buildReplyFields, hydrateReplyTo, quoteFrom } from '../services/replies';
import { loadAboutFor, aboutReplyTarget } from '../services/about';
import { forwardedLabel, canForward } from '../services/forward';
import {
  detectFlight, cachedFlight, lookupFlight, lookupWithDeadline,
  sendFlightMessage, patchFlightData,
} from '../services/flights';
import { sendDocumentMessage, isDocFile, docStyle, humanBytes } from '../services/documents';
/* ⭐ what a shared Vibe SAYS it is — generated from the phone's own
   services/vibeChatCard.ts, so one share renders the same on both clients. */
import { cardAuthor, cardPoll, cardMedia } from '../services/vibeChatCard';
/* ⭐ …and the words on it, in the app's ten languages. See cardStrings.js for
   why this card was the last all-English surface in the chat. */
import { ct } from '../services/cardStrings';
import SecurityCard from './SecurityCard';
import {
  isSystemMessage, isHiddenSystem, systemText, systemIcon,
  canSendMessages, groupSubtitle,
} from '../services/systemText';
import { watchStarred, toggleStar } from '../services/stars';
import { blockUser, unblockUser } from '../services/blocking';
import {
  canDeleteForEveryone, deleteForMe, deleteForEveryone, eraseCompletely, tombstonePreview, removeAlbumItem, writeReactionPreview,
} from '../services/messageActions';
import { hiddenIdsFor, hideLocally as hideLocallyShared } from '../services/hiddenMessages';
import ChatTools, { DeleteSheet } from './ChatTools';
import AppearancePanel from './AppearancePanel';
import ChatInfoPane from './ChatInfoPane';
import {
  watchTranslation, shouldTranslate, translateText, cachedTranslation, setCachedTranslation,
} from '../services/translate';
import {
  loadAppearance, saveAppearance, applyToAllChats, resetAppearance, apClasses, apStyle,
} from '../services/appearance';
/* ✦ Allo AI, in a chat. One trigger, one send path, one marker — all shared
   with the phone so a question asked in either client behaves the same way. */
import { AI_TRIGGER, isAiAsk } from '../services/ai';
/* ✦ "@" IN A GROUP — one module, four surfaces, two clients; see services/mentions */
import {
  activeMentionQuery, mentionPickerState, applyMention, collectMentions, segmentMentions,
  withEveryone, canMentionEveryone, mentionsEveryone as textMentionsEveryone, MENTION_EVERYONE,
} from '../services/mentions';
import MentionPicker, { useLiveMentionMembers } from './MentionPicker';
import { askInChat } from '../services/aiChat';
import { aiPendingShow, AI_PENDING_GRACE_MS } from '../services/aiPending';
import { AiComposerCard, AiThinking } from './AiCard';
import '../ui/appearance.css';

import { sealMembers } from '../services/groupMembers';   // 🔐 active members only
const MAX_ATTACHMENTS = 10;
// 🟥 TYPES THAT NEED DECRYPTION BUT NOT A DOWNLOAD.
//    When media loading went lazy, everything that wasn't `text` was left to the
//    IntersectionObserver — which first asks hasCloudSource(). A location has no URL
//    (its coordinates live ONLY inside the sealed blob), so it failed that test, was
//    written off as "only on your phone", and decryptMessage never ran: every location
//    from the app rendered as the padlock. These are CPU-only unseals of a few dozen
//    bytes, so they belong in the immediate pass with text, not the fetch queue.
const INSTANT_TYPES = new Set(['text', 'location', 'contact', 'poll', 'sticker', 'gif', 'flight', 'security']);
const MEDIA_LANES = 3;          // concurrent decrypts — enough to feel instant, not a stampede
const MEDIA_PRELOAD_PX = 700;   // start fetching this far outside the viewport

// 🟢 One channel for everything the media bodies need from the chat: register with the
//    shared IntersectionObserver, ask for a decrypt, force-load a video. Passing these
//    down as props would have meant threading four more through Bubble → MessageBody →
//    every body, for something only the media ones use.
const MediaCtx = React.createContext(null);

/** Register this bubble with the chat's single IntersectionObserver, so its media is
 *  fetched when it is about to come on screen — and never if the user scrolls past it. */
function useMediaGate(id) {
  const mc = useContext(MediaCtx);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !mc || !id) return undefined;
    mc.observe(el, id);
    return () => mc.unobserve(el);
  }, [id, mc]);
  return [ref, mc];
}

// ── ticks: the app's exact component; status derived from the chat-doc watermarks ──
function Ticks({ msg, chat, myUid, receiptsOn = true }) {
  /* 🟥 MUTUAL, BUT ONLY ON A TEMPORARY CHAT. Turning read receipts off should
     cost you the other person's as well — that is the bargain, and it is what
     the app's own reciprocity rule already does for Last Seen. It is applied
     here ONLY to guest chats, deliberately: between two Allo accounts the PHONE
     gates only what it sends and still shows what it receives, so making the web
     hide them would put the two clients out of step on the same conversation.
     A guest has no privacy switch of their own to reciprocate with, which is
     exactly why mutuality is the only way the protection can exist for them. */
  const status = deriveReceiptStatus(msg, chat, myUid);
  return (
    <MessageTicks
      status={(!receiptsOn && status === 'read') ? 'delivered' : status}
      size={16}
      color="var(--tick-out-muted)"
      readColor="var(--tick-out-read)"   /* 🔵 measured against your bubble (services/tickTone via theme.css / appearance.js) */
      style={{ marginLeft: 1 }}
    />
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MEDIA
// ══════════════════════════════════════════════════════════════════════════════

const dataUri = (b64) => (b64 ? `data:image/jpeg;base64,${b64}` : null);

/** Live viewport so media can be sized for the ACTUAL window — a laptop and a 27"
 *  monitor should not both get the phone's 300px bubble. */

/** Live viewport so media can be sized for the ACTUAL window — a laptop and a 27"
 *  monitor should not both get the phone's 300px bubble. */

/* 📐 ONE resize listener for the whole chat, not one per image bubble. Every media
   bubble used to add its own window listener and its own state, so a drag of the
   window edge re-rendered every bubble once per listener. A shared store: one
   listener, one rAF, and a snapshot object that only changes when the size does. */
const _vpStore = (() => {
  let snap = typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : { w: 0, h: 0 };
  const subs = new Set();
  let raf = 0;
  const on = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const w = window.innerWidth; const h = window.innerHeight;
      if (w === snap.w && h === snap.h) return;
      snap = { w, h };
      subs.forEach((f) => f());
    });
  };
  return {
    get: () => snap,
    subscribe: (f) => {
      if (!subs.size) window.addEventListener('resize', on);
      subs.add(f);
      return () => {
        subs.delete(f);
        if (!subs.size) { window.removeEventListener('resize', on); cancelAnimationFrame(raf); }
      };
    },
  };
})();
function useViewport() {
  return useSyncExternalStore(_vpStore.subscribe, _vpStore.get);
}

const clamp = (lo, v, hi) => Math.max(lo, Math.min(v, hi));

function albumBox(vp) {
  const width = Math.round(clamp(280, vp.w * 0.32, 520));
  return { width, height: Math.round(width * 0.93) };
}

function Blur({ b64 }) {
  const src = dataUri(b64);
  if (!src) return null;
  return <img className="mb-blur" src={src} alt="" aria-hidden />;
}

function Spinner({ label }) {
  return (
    <div className="mb-wait">
      <span className="mb-spin" />
      {label ? <span className="mb-wait-label">{label}</span> : null}
    </div>
  );
}

/* 🟥 A BUTTON THAT CANNOT WORK IS WORSE THAN NO BUTTON. Chat media is deleted
   from the bucket by an age-based lifecycle rule, and a 404 from that rule was
   rendered exactly like a CORS block or a dropped connection: "Media
   unavailable · Try again". So the reader was invited to retry the one failure
   that can never succeed, pressed it, watched it fail, and concluded the
   product was broken — while the transient failure, the one a second attempt
   fixes, was offered nothing at all.

   🟢 THE TWO SAY DIFFERENT THINGS NOW, and only one of them has a button. See
      classifyFailure in services/mediaPolicy.js. */
function Dead({ onRetry, gone }) {
  return (
    <div className="mb-dead">
      <Alert width={22} height={22} />
      <span>{gone ? 'This media has expired' : 'Media unavailable'}</span>
      {!gone && onRetry && <button className="mb-retry" onClick={(e) => { e.stopPropagation(); onRetry(); }}>Try again</button>}
    </div>
  );
}

/** 🟢 There is no cloud copy left to fetch — so DON'T offer "Try again", which can
 *  only fail again. This happens for media the phone received and already downloaded:
 *  store-and-forward deletes the 1:1 message doc once delivered, and the phone's own
 *  row then points at a local file, so the sealed export has no URL to hand over. The
 *  bytes exist, but only on the phone. */
function DeviceOnly({ kind }) {
  return (
    <div className="mb-dead device-only">
      <CloudOff width={22} height={22} />
      <span>{kind === 'video' ? 'This video is only on your phone' : 'Only on your phone'}</span>
    </div>
  );
}

/** One tile inside an album / stack card: blur base → real image → chrome. */
function Tile({ item, blurB64, onClick, badge, dim = 0, deleted = false, resolved = false }) {
  const unreachable = resolved && !item?.url && !item?.needsLoad && !item?.loading;
  return (
    <div className="mb-tile" onClick={onClick}>
      <Blur b64={blurB64} />
      {deleted || unreachable ? (
        <span className="mb-tile-gone"><CloudOff width={18} height={18} /></span>
      ) : item?.url ? (
        item.kind === 'video'
          // eslint-disable-next-line jsx-a11y/media-has-caption
          ? <><video className="mb-real" src={item.url} muted preload="metadata" /><span className="mb-play"><Play width={20} height={20} /></span></>
          : <img className="mb-real" src={item.url} alt="" />
      ) : item?.needsLoad ? (
        // a video inside an album: never auto-downloaded, one tap fetches it
        <span className="mb-tile-load">{item.loading ? <span className="mb-spin sm" /> : <><Play width={18} height={18} /><em>Load</em></>}</span>
      ) : (
        <span className="mb-tile-wait"><span className="mb-spin sm" /></span>
      )}
      {dim > 0 && <span className="mb-dim" style={{ background: `rgba(0,0,0,${dim})` }} />}
      {badge}
    </div>
  );
}

/** 🟢 Media the user purged in the phone's Storage & Data screen. That flag never
 *  reaches Firestore (it is per-device housekeeping, not chat state) — it rides in on
 *  the phone's sealed history export. Same wording the app's bubble uses. */
function PurgedBody({ m }) {
  const cap = m._plain || m._caption || m.caption || '';
  return (
    <div className="mb-purged">
      <CloudOff width={20} height={20} />
      <span>Media deleted from device storage</span>
      {cap && cap !== 'Media deleted from device storage.' ? <div className="mb-purged-cap">{cap}</div> : null}
    </div>
  );
}

function ImageBody({ m, mine, onOpen, chatId, members }) {
  const vp = useViewport();
  const [hostRef, mc] = useMediaGate(m.id);
  const covered = useCovered(chatId, m.id);
  /* 🖼 🟥 A PHOTO WITH NO DIMENSIONS WAS DRAWN LANDSCAPE AND THEN SNAPPED
     UPRIGHT. Every file sent to the Allo account used to lose its shape at the
     server's door, so this is not only old messages — and the box is computed
     BEFORE the bytes land precisely so the list does not jump. Learning the
     shape on first decode is what makes the second look right. */
  const [learned, setLearned] = useState(() => recallAspect(m.id));
  const box = fitBox(m.width, m.height, vp, learned);
  const cap = m._caption ?? m.caption ?? '';
  return (
    <>
      <div className="mb-single" ref={hostRef} style={box} onClick={() => !covered && m._media && onOpen(m, 0)}>
        <Blur b64={m.imageThumbnailBase64} />
        {m._media
          ? (
            <img
              className="mb-real"
              src={m._media}
              alt=""
              onLoad={(e) => {
                const a = rememberAspect(m.id, e.target.naturalWidth, e.target.naturalHeight);
                if (a && !learned) setLearned(a);
              }}
              /* 🟥 THERE WAS NO onError ANYWHERE ON DECRYPTED MEDIA, in this
                 file or any other — so the entire retry apparatus only ever
                 fired when the resolver had returned null. A `_media` string
                 that is no longer VALID reads as truthy, the browser paints its
                 torn-paper glyph, and nothing in the app ever notices. That is
                 the "always broken, stays broken" half of the report. */
              onError={() => mc?.healMedia(m.id)}
            />
          )
          : m._deviceOnly ? <DeviceOnly />
            : m._mediaResolved ? <Dead gone={m._mediaGone} onRetry={() => mc?.retryMedia(m.id)} /> : <Spinner />}
        {m.imageQuality === 'hd' && m._media && !covered && <span className="mb-hd">HD</span>}
        {m._uploadPct != null && m._uploadPct < 100 && <UploadRing pct={m._uploadPct} />}
        {covered && <PrivateCover messageId={m.id} label="Click to reveal photo" />}
      </div>
      {cap ? <div className={`mb-caption ${mine ? 'out' : ''}`}><MentionText text={cap} members={members} /></div> : null}
    </>
  );
}

/** 🟥 VIDEOS ARE NEVER AUTO-FETCHED. A single clip is tens of megabytes that has to come
 *  down the wire AND through secretbox before one frame exists — doing that for every
 *  video in a scrolled-past history is what made opening a chat feel like a download
 *  queue. The poster and duration come straight off the message doc (no decrypt needed),
 *  so the bubble is complete and honest; the bytes wait for a tap. */
function VideoBody({ m, mine, onOpen, chatId, members }) {
  const vp = useViewport();
  const [hostRef, mc] = useMediaGate(m.id);
  const covered = useCovered(chatId, m.id);
  /* 🖼 the same rule as a photo — a portrait clip must not open the chat as a
     letterbox and then rearrange itself */
  const [learned, setLearned] = useState(() => recallAspect(m.id));
  const box = fitBox(m.width, m.height, vp, learned);
  const cap = m._caption ?? m.caption ?? '';
  const failed = m._mediaResolved && !m._media && !m._needsLoad && !m._loading;
  return (
    <>
      <div className="mb-single" ref={hostRef} style={box} onClick={() => !covered && m._media && onOpen(m, 0)}>
        <Blur b64={m.videoThumbnailBase64} />
        {/* 🎬 …AND FOR THE ONE SENDER THAT CANNOT SEAL A BLUR, THE POSTER ITSELF.
            A phone bakes a tiny base64 still into the message; a broadcast has no
            key its thousands of recipients could share, so it uploads a real frame
            and sends the url. Without this the card under the "Load video" button
            was a flat black rectangle — the same complaint on the phone, the same
            missing picture, and it had been sitting in the cloud the whole time.
            Dropped the instant the real video mounts. */}
        {!m._media && typeof m.videoThumbUrl === 'string' && /^https?:/i.test(m.videoThumbUrl)
          ? <img className="mb-poster" src={m.videoThumbUrl} alt="" aria-hidden />
          : null}
        {m._media ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <>
            <video
              className="mb-real"
              src={m._media}
              preload="metadata"
              muted
              /* the same healing an image gets — a video with a dead handle is a
                 black rectangle, which says even less than a broken image does */
              onError={() => mc?.healMedia(m.id)}
              onLoadedMetadata={(e) => {
                const a = rememberAspect(m.id, e.target.videoWidth, e.target.videoHeight);
                if (a && !learned) setLearned(a);
              }}
            />
            <span className="mb-play big"><Play width={26} height={26} /></span>
          </>
        ) : m._deviceOnly ? (
          <DeviceOnly kind="video" />
        ) : failed ? (
          <Dead gone={m._mediaGone} onRetry={() => mc?.retryMedia(m.id)} />
        ) : m._loading ? (
          <Spinner label="Loading video…" />
        ) : (
          <button
            className="mb-loadbtn"
            onClick={(e) => { e.stopPropagation(); mc?.requestMedia(m.id, true); }}
          >
            <Download width={17} height={17} />
            <span>Load video</span>
          </button>
        )}
        {m.duration && !covered ? <span className="mb-dur">{durationLabel(m.duration * 1000)}</span> : null}
        {m.videoQuality === 'hd' && !covered && <span className="mb-hd">HD</span>}
        {m._uploadPct != null && m._uploadPct < 100 && <UploadRing pct={m._uploadPct} />}
        {covered && <PrivateCover messageId={m.id} label="Click to reveal video" />}
      </div>
      {cap ? <div className={`mb-caption ${mine ? 'out' : ''}`}><MentionText text={cap} members={members} /></div> : null}
    </>
  );
}

/* 🧩 A RUN — single photos/videos (a grid) or documents (a stack) sent within a few
   minutes of each other, drawn together but each still ITS OWN message: its own media
   gate (so each decrypts on its own), its own time and ticks, its own forward and
   delete on hover, its own place in the viewer. The grouping rule is the phone's
   (services/mediaRuns), so both clients draw the same runs. */
/* a reaction that lands on a run item while you look stays in sight on its tile
   (the item does not leave the run under your finger — services/mediaRuns rule 4) */
function runReactLabel(r) {
  if (!r || typeof r !== 'object' || Array.isArray(r)) return '';
  const rows = Object.entries(r).map(([e, u]) => [e, Array.isArray(u) ? u.length : (u ? 1 : 0)]).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return '';
  const total = rows.reduce((c, [, k]) => c + k, 0);
  return total > 1 ? `${rows[0][0]} ${total}` : rows[0][0];
}

function RunTile({ it, onOpen, mine, chat, myUid, receiptsOn, onForward, onDelete, onReply, onReact, more = 0 }) {
  const [hostRef, mc] = useMediaGate(it.id);
  const isVid = it.type === 'video';
  return (
    <div className="mb-tile run-tile" ref={hostRef} data-mid={it.id} onClick={() => { if (it._media) onOpen(it); else if (isVid && !it._loading) mc?.requestMedia(it.id, true); }}>
      <Blur b64={isVid ? it.videoThumbnailBase64 : it.imageThumbnailBase64} />
      {it._media ? (
        isVid
          // eslint-disable-next-line jsx-a11y/media-has-caption
          ? <><video className="mb-real" src={it._media} muted preload="metadata" /><span className="mb-play"><Play width={20} height={20} /></span></>
          : <img className="mb-real" src={it._media} alt="" onError={() => mc?.healMedia(it.id)} />
      ) : isVid ? (
        <span className="mb-tile-load">{it._loading ? <span className="mb-spin sm" /> : <><Play width={18} height={18} /><em>Load</em></>}</span>
      ) : (
        <span className="mb-tile-wait"><span className="mb-spin sm" /></span>
      )}
      {runReactLabel(it.reactions) ? <span className="run-react">{runReactLabel(it.reactions)}</span> : null}
      {more > 0 ? <span className="mb-dim" style={{ background: 'rgba(0,0,0,.5)' }} /> : null}
      {more > 0 ? <span className="run-more">+{more}</span> : (
        <span className="run-meta">
          {timeLabel(it.timestamp, it._lateSend)}
          {mine && <Ticks msg={it} chat={chat} myUid={myUid} receiptsOn={receiptsOn} />}
        </span>
      )}
      <span className="run-acts" onClick={(e) => e.stopPropagation()}>
        {onReply && <button className="ract" data-tip="Reply to this one" aria-label="Reply to this item" onClick={() => onReply(it)}><Reply width={14} height={14} /></button>}
        {onReact && <button className="ract" data-tip="React" aria-label="React to this item" onClick={() => onReact(it)}><Smile width={14} height={14} /></button>}
        {canForward(it) && <button className="ract" data-tip="Forward" aria-label="Forward" onClick={() => onForward(it)}><Forward width={14} height={14} /></button>}
        <button className="ract danger" data-tip="Delete" aria-label="Delete" onClick={() => onDelete(it)}><Trash width={14} height={14} /></button>
      </span>
    </div>
  );
}

function RunDoc({ it, mine, chat, myUid, receiptsOn, onForward, onDelete, onReply, onReact }) {
  const [hostRef] = useMediaGate(it.id);
  return (
    <div className="run-doc" data-mid={it.id}>
      <DocBubble m={it} hostRef={hostRef} />
      <div className="run-doc-meta">
        {runReactLabel(it.reactions) ? <span className="run-doc-react">{runReactLabel(it.reactions)}</span> : null}
        <span className="time">{timeLabel(it.timestamp, it._lateSend)}</span>
        {mine && <Ticks msg={it} chat={chat} myUid={myUid} receiptsOn={receiptsOn} />}
        <span className="run-doc-acts">
          {onReply && <button className="ract" aria-label="Reply to this document" onClick={() => onReply(it)}><Reply width={13} height={13} /></button>}
          {onReact && <button className="ract" aria-label="React to this document" onClick={() => onReact(it)}><Smile width={13} height={13} /></button>}
          {canForward(it) && <button className="ract" aria-label="Forward" onClick={() => onForward(it)}><Forward width={13} height={13} /></button>}
          <button className="ract danger" aria-label="Delete" onClick={() => onDelete(it)}><Trash width={13} height={13} /></button>
        </span>
      </div>
    </div>
  );
}

function RunRow({ run, mine, chat, myUid, receiptsOn, onOpenRun, onForward, onDelete, onReply, onReact, nameOf, flashId = null, groupPosition = 'single' }) {
  const vp = useViewport();
  const items = run.items || [];
  const groupIn = !mine && !!chat?.isGroup;
  const runFirst = groupPosition === 'single' || groupPosition === 'top';
  const runLast = groupPosition === 'single' || groupPosition === 'bottom';
  const senderName = groupIn ? (nameOf?.(run) || 'Allo user') : '';
  const props = { mine, chat, myUid, receiptsOn, onForward, onDelete, onReply, onReact };
  let body;
  if (run.runKind === 'doc') {
    body = <div className="run-docs">{items.map((it) => <RunDoc key={it.id} it={it} {...props} />)}</div>;
  } else {
    const box = albumBox(vp);
    /* 📦 a batch (picked together, each sent on its own) is drawn by its slots: one still on its way
       is a waiting tile, in its place — the app's MediaRunBubble, the same rows (services/mediaRuns) */
    const pages = run.batch ? batchPages(run) : items;
    const open = (it) => onOpenRun(run, items.indexOf(it));
    const tile = (i, more = 0) => {
      const it = pages[i];
      if (!it) return null;
      if (isWaitingPage(it)) return <div key={it.id} className="mb-tile run-tile"><span className="mb-tile-wait"><span className="mb-spin sm" /></span></div>;
      return <RunTile key={it.id} it={it} onOpen={open} more={more} {...props} />;
    };
    body = pages.length === 1 ? (
      <div className="mb-album" style={box}>{tile(0)}</div>
    ) : pages.length === 2 ? (
      <div className="mb-album two" style={box}>{tile(0)}{tile(1)}</div>
    ) : pages.length === 3 ? (
      <div className="mb-album three" style={box}>
        <div className="mb-col big">{tile(0)}</div>
        <div className="mb-col small">{tile(1)}{tile(2)}</div>
      </div>
    ) : (
      <div className="mb-album four" style={box}>
        {tile(0)}{tile(1)}{tile(2)}{tile(3, pages.length - 4)}
      </div>
    );
    /* 📦 a batch's caption (item 0's) under its grid */
    if (run.batch && run.caption) body = <>{body}<div className={`mb-caption ${mine ? 'out' : ''}`}><MentionText text={run.caption} /></div></>;
  }
  return (
    <div className={`row ${mine ? 'out' : 'in'} gp-${groupPosition} ${flashId && items.some((x) => x && x.id === flashId) ? 'flash' : ''} ${groupIn ? 'grp' : ''} has-media`} data-mid={run.id}>
      {groupIn && (
        <span className="row-av" aria-hidden={!runLast}>
          {runLast && <MemberAvatar chat={chat} uid={run.senderId} name={senderName} img={run.senderImage} />}
        </span>
      )}
      <div className={`bubble ${run.runKind === 'doc' ? '' : 'media'} run`} style={{ overflow: 'visible' }}>
        {groupIn && runFirst && (
          <div className="sender-row">
            <span className="sender-name" style={{ color: nameColor(run.senderId) }}>{senderName}</span>
          </div>
        )}
        {body}
        {/* 🧩 reply to ALL of it — each tile's own reply answers one item */}
        {onReply && (
          <div className="row-acts">
            <button className="ract" data-tip="Reply to all" aria-label="Reply to all of them" onClick={() => { const src = runReplySource(run); if (src) onReply(src); }}>
              <Reply width={15} height={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** 🟢 THE ALBUM. Layout rule, mirroring the app (MessageBubble.chooseBatchLayout):
 *    2  → split in half
 *    3  → SPLIT BUBBLE: one big tile + two stacked beside it
 *    4+ → STACK: a vertical deck of cards, swipe/drag the top one to cycle
 *  A grid with a "+N" badge hides items behind a number; the deck shows every one. */
function BatchBody({ m, mine, onOpen, chatId, members, myUid, onAlbumItem }) {
  const vp = useViewport();
  const [hostRef, mc] = useMediaGate(m.id);
  const box = albumBox(vp);
  /* 🧩 AN ALBUM'S ITEMS ARE ITS OWN. One removed for everyone, or deleted for me, is
     not drawn; every position below maps back to the item's ORIGINAL index (`vis`),
     which is what its blur, its tile load and its actions are addressed by. */
  const allItems = m._mediaBatch || [];
  const total = allItems.length || (m.images || []).length;
  const vis = useMemo(() => albumVisibleIndices({ ...m, images: Array.from({ length: total }) }, myUid), [m, total, myUid]);
  const items = vis.map((o) => allItems[o]);
  const covered = useCovered(chatId, m.id);
  const blurs = vis.map((o) => (m.batchThumbnails || [])[o]);
  const count = vis.length;
  // tiles the user purged in Storage & Data (per-slot; local-only on the phone, so it
  // reaches us through the sealed history export)
  const gone = useMemo(() => new Set(m.deletedImageIndices || []), [m.deletedImageIndices]);

  // deck state (4+)
  const [top, setTop] = useState(0);
  const [drag, setDrag] = useState(0);
  const dragRef = useRef(null);
  const movedRef = useRef(false);

  const cycle = useCallback((dir) => {
    setTop((t) => ((t + dir) % count + count) % count);
  }, [count]);

  if (!count) return <Spinner />;

  const tapTile = (i) => {
    if (covered) return;                    // the cover owns the tap while it's up
    const o = vis[i];
    if (gone.has(o)) return;
    const it = items[i];
    if (it?.url) { onOpen(m, o); return; }
    if (it?.needsLoad && !it.loading) mc?.loadTile(m, o); // album video → fetch just this one
  };
  /* 🧩 this item's own forward and delete, on hover */
  const acts = (i) => (onAlbumItem && !covered && !m._pending ? (
    <span className="run-acts" onClick={(e) => e.stopPropagation()}>
      <button className="ract" data-tip="Reply to this one" aria-label="Reply to this item" onClick={() => onAlbumItem('reply', m, vis[i])}><Reply width={14} height={14} /></button>
      <button className="ract" data-tip="Forward" aria-label="Forward this item" onClick={() => onAlbumItem('forward', m, vis[i])}><Forward width={14} height={14} /></button>
      <button className="ract danger" data-tip="Delete" aria-label="Delete this item" onClick={() => onAlbumItem('delete', m, vis[i])}><Trash width={14} height={14} /></button>
    </span>
  ) : null);

  const tileFor = (i, extra) => (
    <Tile
      key={vis[i]}
      item={items[i]}
      blurB64={blurs[i]}
      deleted={gone.has(vis[i])}
      resolved={!!m._mediaResolved}
      onClick={() => tapTile(i)}
      badge={<>{extra}{acts(i)}</>}
    />
  );

  let body;
  if (count === 2) {
    body = (
      <div className="mb-album two" ref={hostRef} style={box}>
        {tileFor(0)}{tileFor(1)}
      </div>
    );
  } else if (count === 3) {
    // ── split bubble ──
    body = (
      <div className="mb-album three" ref={hostRef} style={box}>
        <div className="mb-col big">{tileFor(0)}</div>
        <div className="mb-col small">{tileFor(1)}{tileFor(2)}</div>
      </div>
    );
  } else {
    // ── stack (4+) ──
    const CARD = { width: box.width, height: Math.round(box.width * 0.83) };
    const PEEK = 10;
    const depth = Math.min(count, 4);
    const cardTop = (depth - 1) * PEEK;
    const peeks = [];
    for (let d = depth - 1; d >= 1; d--) {
      const idx = (top + d) % count;
      const baseY = -d * PEEK;
      const nextY = -(d - 1) * PEEK;
      const t = Math.min(1, Math.abs(drag) / 300);
      peeks.push(
        <div
          key={`peek-${idx}`}
          className="mb-card peek"
          style={{ ...CARD, top: cardTop, transform: `translateY(${baseY + (nextY - baseY) * t}px)` }}
        >
          <Tile item={items[idx]} blurB64={blurs[idx]} dim={0.12 * d} deleted={gone.has(vis[idx])} />
        </div>
      );
    }

    const onDown = (e) => {
      dragRef.current = e.clientX; movedRef.current = false;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onMove = (e) => {
      if (dragRef.current == null) return;
      const dx = e.clientX - dragRef.current;
      if (Math.abs(dx) > 4) movedRef.current = true;
      setDrag(dx);
    };
    const onUp = () => {
      if (dragRef.current == null) return;
      const dx = drag;
      dragRef.current = null;
      setDrag(0);
      if (Math.abs(dx) >= 60) cycle(dx < 0 ? 1 : -1);
      else if (!movedRef.current) tapTile(top);
    };

    body = (
      <div className="mb-stack" ref={hostRef} style={{ width: CARD.width, height: cardTop + CARD.height }}>
        {peeks}
        <div
          className="mb-card front"
          style={{
            ...CARD, top: cardTop,
            transform: `translateX(${drag}px) rotate(${clamp(-4, drag / 75, 4)}deg)`,
            transition: dragRef.current == null ? 'transform .22s cubic-bezier(.22,1,.36,1)' : 'none',
          }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          <Tile item={items[top]} blurB64={blurs[top]} deleted={gone.has(vis[top])} resolved={!!m._mediaResolved} badge={acts(top)} />
          <span className="mb-deck-pill">▦ {top + 1}/{count}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* one cover over the whole album — unlocking reveals the set you tapped,
          which is what the app does too (the unlock is scoped to the message) */}
      <div className="mb-cover-host">
        {body}
        {covered && <PrivateCover messageId={m.id} label={`Click to reveal ${count} items`} />}
      </div>
      {m._uploadPct != null && m._uploadPct < 100 && (
        <div className="mb-album-progress"><span style={{ width: `${m._uploadPct}%` }} /></div>
      )}
      {m._caption ? <div className={`mb-caption ${mine ? 'out' : ''}`}><MentionText text={m._caption} members={members} /></div> : null}
    </>
  );
}

function UploadRing({ pct }) {
  const r = 20, c = 2 * Math.PI * r;
  return (
    <div className="mb-uploading">
      <svg width="52" height="52" viewBox="0 0 52 52">
        <circle cx="26" cy="26" r={r} className="ring-bg" />
        <circle cx="26" cy="26" r={r} className="ring-fg" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />
      </svg>
      <span>{Math.round(pct)}%</span>
    </div>
  );
}

// ── remaining per-type bodies (unchanged behaviour) ──
function PrePairNote() {
  // Messages sent BEFORE this browser was linked weren't sealed to it (same as
  // WhatsApp) — show a calm, honest note instead of a scary lock.
  return <span style={{ color: 'var(--text-3)', fontStyle: 'italic', fontSize: 13.5 }}><Lock /> Sent before this device was linked</span>;
}
/** the language glyph — icons.jsx has no translate mark */
const Translate = (p) => (
  <svg viewBox="0 0 24 24" width={p.width || 14} height={p.height || 14} fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5h10" /><path d="M9 3v2c0 4.4-2.2 7.5-5 9" />
    <path d="M6 12c1.6 2.3 3.9 4 6 5" />
    <path d="m12 20 4.5-10 4.5 10" /><path d="M14 17h5" />
  </svg>
);

/**
 * ✦ THE @ai MENTION, INSIDE THE SENTENCE IT WAS TYPED IN.
 *
 * 🟥 IT PICKS NO COLOUR, AND THAT IS THE POINT. The phone's version was a fixed
 *    blue on every bubble, which disappears on a blue theme and is barely there
 *    on a dark gradient — reported exactly that way. Any other fixed colour has
 *    the same fault on some other theme. A span with no colour inherits the
 *    bubble's own text colour, so the mention is exactly as legible as the
 *    sentence it belongs to, on every theme that exists and every one added
 *    later. Weight and an underline are what make it read as a mention.
 *
 * 🟢 AND THIS IS THE ONLY RICH TEXT IN A WEB BUBBLE. Deliberately: the body has
 *    always been a plain `<span>`, and turning it into a parser for the sake of
 *    one token would be a new class of bug in the hottest component here. It
 *    splits on the mention and renders strings either side.
 */
/* ══ 🤖 ONE RENDERER FOR BOTH KINDS OF "@" ════════════════════════════
   🟥 A MESSAGE CAN NAME A PERSON AND THE ASSISTANT IN ONE SENTENCE, and two
      renderers that each split the string their own way cannot both be right about
      it — the second one only ever sees what the first left behind. So the member
      mentions are segmented first (by the module, which knows that "@Ali Hassan" is
      one member and that "@Alison" is nobody), and each plain run between them is
      then split for the @ai chip exactly as before.
   🟢 A message with no member mention takes the identical path it always did. */
function MentionText({ text, onAsk, members }) {
  const src = String(text ?? '');
  const segs = (members && members.length) ? segmentMentions(src, members) : [{ text: src }];

  const withAi = (str, key) => {
    const parts = String(str).split(/(@ai)/gi);
    if (parts.length === 1) return <React.Fragment key={key}>{str}</React.Fragment>;
    return parts.map((p, i) => (p.toLowerCase() === '@ai'
      ? (
        <button
          key={`${key}-${i}`}
          type="button"
          className="ai-mention"
          onClick={(e) => { e.stopPropagation(); onAsk && onAsk(); }}
          title="Ask Allo AI in this chat"
        >{p}</button>
      )
      : <React.Fragment key={`${key}-${i}`}>{p}</React.Fragment>));
  };

  return (
    <span>
      {segs.map((seg, i) => (seg.uid
        ? <span key={i} className="ment-in-text">{seg.text}</span>
        : withAi(seg.text, i)))}
    </span>
  );
}

function TextBody({ m, onAsk, members }) {
  const meta = m.linkMetadata ? normalizeLink(m.linkMetadata) : null;
  // 🟢 TRANSLATED TEXT + the app's toggle: the badge flips between the translation
  //    and the original, per message, exactly like the phone's language glyph.
  const [orig, setOrig] = useState(false);
  if (m._plain == null && !meta) return <PrePairNote />;
  const body = m._translated && !orig ? m._translated : m._plain;
  return (
    <>
      {m._plain != null && <MentionText text={body} onAsk={onAsk} members={members} />}
      {m._translated && (
        <button
          className={`tx-badge ${orig ? 'orig' : ''}`}
          onClick={(e) => { e.stopPropagation(); setOrig(!orig); }}
          title={orig ? 'Show translation' : 'Show original'}
        >
          <Translate width={13} height={13} />
          {orig ? 'Original' : 'Translated'}
        </button>
      )}
      {meta && <LinkCard meta={meta} />}
    </>
  );
}

function AudioBody({ m, mine }) {
  // 🟥 THE MISSING PLAY BUTTON. Every other media body calls useMediaGate, which is what
  //    registers the bubble with the chat's IntersectionObserver and therefore what asks
  //    for the bytes. VoiceBubble was the one body that never did — so `_media` was never
  //    resolved, the <button> stayed disabled, and what looked like "no play button" was
  //    actually a memo that had never been fetched.
  const [hostRef] = useMediaGate(m.id);
  return <VoiceBubble m={m} mine={mine} hostRef={hostRef} />;
}

function DocBody({ m }) {
  const [hostRef] = useMediaGate(m.id);
  return <DocBubble m={m} hostRef={hostRef} />;
}

function StickerBody({ m, onOpen }) {
  if (!m._media) return null;
  return <img src={m._media} alt="sticker" style={{ maxWidth: 150, maxHeight: 150 }} onClick={() => onOpen(m, 0)} />;
}

/** 🟥 The sealed payload's keys are `lat`/`lng` — NOT latitude/longitude. This read the
 *  wrong names, so every location bubble pointed at `undefined,undefined`. (The legacy
 *  names stay as a fallback in case anything older ever wrote them.)
 *  The link target follows the platform the way the app does: Apple Maps on an Apple
 *  browser, Google Maps everywhere else. */
function LocationBody({ m }) {
  /* 🟥 A PLAINTEXT LOCATION IS NOT A LOCKED ONE, and reading only `_payload`
     said it was. `_payload` exists solely as the result of unsealing
     `locationPerDeviceKeys` (services/messages.js), so a location whose
     coordinates are on the document in the clear — which is what an official
     broadcast sends, because there is no key thousands of recipients could
     share — fell through to the padlock. The phone has always read
     `message.latitude` / `.longitude` directly and rendered it; this is the
     browser catching up, not a new shape.

     Sealed first, because for an ordinary person-to-person location the sealed
     copy is the authoritative one and the plaintext fields are absent. */
  const p = m._payload || {};
  const lat = Number(p.lat ?? p.latitude ?? m.latitude);
  const lng = Number(p.lng ?? p.longitude ?? m.longitude);
  /* 📍 a live session — followed here now (services/liveFollow.js), ended or not */
  if (m.liveLocationId) return <LiveLocationBody m={m} lat={lat} lng={lng} />;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return <span style={{ color: 'var(--text-3)' }}><Lock /> Location</span>;
  }
  const label = p.name || m.locationName || 'Shared location';

  return <LocationCard lat={lat} lng={lng} label={label} name={p.name || m.locationName} />;
}

const LIVE_AGE_TEXT = {
  live_updated_now: () => 'Updated just now',
  live_updated_min: (n) => `Updated ${n} min ago`,
  live_updated_hr: (n) => `Updated ${n} h ago`,
};

/** 📍 A live location, followed: the map moves with the sharer's newest fix, the bar
 *  says how old it is (amber once stale), and Refresh reads the server, not the cache. */
function LiveLocationBody({ m, lat, lng }) {
  const live = useLiveFix(m.liveLocationId, m.senderId, true);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const first = Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  const fix = live.fix || first;
  const active = live.active && !m.liveEnded && !liveExpired(liveToMillis(m.liveUntil), now);
  const age = liveAge(live.at || liveToMillis(m.timestamp), now);
  const ageText = live.busy ? 'Refreshing…'
    : live.failed ? "Couldn't refresh"
    : age ? LIVE_AGE_TEXT[age.key](age.n) : 'Waiting for update';
  if (!fix) {
    return <span style={{ color: 'var(--text-3)' }}><Lock /> Live location</span>;
  }
  return (
    <div className="loc-live-wrap">
      <LocationCard lat={fix.lat} lng={fix.lng} label={active ? 'Live location' : 'Live location ended'} />
      <div className={`loc-live-bar${active && (age?.stale || live.failed) ? ' stale' : ''}`}>
        {active ? <span className="loc-live-dot" /> : null}
        <span className="loc-live-age">{active ? ageText : 'Sharing ended'}</span>
        {active ? (
          <button type="button" className="loc-live-refresh" onClick={live.refresh} disabled={live.busy}>
            {live.busy ? 'Refreshing…' : 'Refresh'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Desktop-sized location card: the map fills the bubble's width, sized off the actual
 *  viewport like every other media body here, with the address bar sitting ON the map
 *  rather than as a separate strip underneath. */
function LocationCard({ lat, lng, label, name }) {
  const vp = useViewport();
  const width = Math.round(clamp(300, vp.w * 0.28, 470));
  const height = Math.round(width * 0.62);
  return (
    <a
      className="loc-bubble"
      style={{ width }}
      href={mapsUrlFor(lat, lng, name)}
      target="_blank"
      rel="noreferrer"
      title="Open in Maps"
    >
      <span className="loc-map">
        <StaticMap lat={lat} lng={lng} zoom={16} height={height} width={width} />
        <span className="loc-scrim" />
        <span className="loc-open">Open in Maps ↗</span>
      </span>
      <span className="loc-bubble-foot">
        <MapPin width={16} height={16} />
        <span className="loc-bubble-text">
          <b>{label}</b>
          <em>{lat.toFixed(5)}, {lng.toFixed(5)}</em>
        </span>
      </span>
    </a>
  );
}

/* ══ A VIBES POST SHARED INTO A CHAT ═══════════════════════════════════════
   The app's `shared_post` bubble, web edition — and the browser has room the
   phone doesn't, so this is the full card rather than a thumbnail strip:
   author header, the post's own words, its link card, the media at real size,
   and the engagement row, all inside a flat bubble so the card IS the surface.

   🟥 THE VIEWER'S GATE. MessageBubble locks this card on the RECIPIENT's own
      profile, never the sender's:

        isLocked = !mine && (no dateOfBirth || age < 16)

      Someone who never answered the Vibes questions, or who is under 16, gets
      "Post locked" with a route into the gate — "Setup Vibes to view", or
      "Available on <dob + 16>" with the countdown when it's an age block. Ported
      verbatim, including which half of the pair each message belongs to: your
      OWN shared post is never locked, because you already saw it to share it. */

const VSB_GATE_UNKNOWN = { ready: false, registered: false, underage: false, unlockAt: null };

/** The link fields a shared post carries, if it was a link post. */
function linkOf(m) {
  const url = m.linkUrl || m.postLinkUrl;
  if (!url) return null;
  let domain = m.linkDomain || m.postLinkDomain || '';
  if (!domain) { try { domain = new URL(url).hostname.replace(/^www\./, ''); } catch { domain = url; } }
  const image = m.linkImage || m.postLinkImage || null;
  const kind = (m.linkImageKind || m.postLinkImageKind) === 'photo' ? 'photo'
    : (m.linkImageKind || m.postLinkImageKind) === 'icon' ? 'icon'
      // the app's own heuristic: a favicon service URL is an icon, anything else a photo
      : (!image || /favicon|gstatic|\/s2\/favicons/i.test(image) ? 'icon' : 'photo');
  return { url, domain, image, kind, title: m.linkTitle || m.postLinkTitle || '' };
}

/** The post's link card — the same shape as LinkPostKit's LinkCard on the phone. */
function SharedLinkCard({ link }) {
  // an og:image or favicon that really is gone drops out and the title + domain
  // still stand — SafeImg retries first, so a mid-flight abort doesn't do it
  const [imgDead, setImgDead] = useState(false);
  const showImg = !!link.image && !imgDead;
  return (
    <a
      className={`vsb-link ${link.kind}`}
      href={link.url} target="_blank" rel="noreferrer noopener"
      onClick={(e) => e.stopPropagation()}
    >
      {link.kind === 'photo' && showImg && (
        <span className="vsb-link-banner">
          <SafeImg src={link.image} kind="inline" loading="lazy" onGone={() => setImgDead(true)} />
        </span>
      )}
      <span className="vsb-link-row">
        {link.kind === 'icon' && showImg && (
          <span className="vsb-link-ico">
            <SafeImg src={link.image} kind="inline" loading="lazy" onGone={() => setImgDead(true)} />
          </span>
        )}
        <span className="vsb-link-txt">
          {link.title ? <b>{link.title}</b> : null}
          <span className="vsb-link-domain"><LinkIcon width={11} height={11} />{link.domain}</span>
        </span>
      </span>
    </a>
  );
}

function SharedPostLocked({ m, gate }) {
  const underage = gate.registered && gate.underage;
  const when = underage && gate.unlockAt
    ? gate.unlockAt.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  const daysLeft = underage && gate.unlockAt
    ? Math.max(0, Math.ceil((gate.unlockAt.getTime() - Date.now()) / 864e5))
    : null;

  return (
    <button
      className="vsb vsb-locked"
      onClick={() => { try { window.dispatchEvent(new CustomEvent('allo:open-vibes')); } catch {} }}
      title={underage ? 'Vibes opens on your birthday' : 'Set up Vibes to view shared posts'}
    >
      {/* the blurred poster stays as texture — the app blurs the same image at 30 */}
      {m.postImageUrl && <span className="vsb-locked-bg" style={{ backgroundImage: `url(${m.postImageUrl})` }} />}
      <span className="vsb-locked-in">
        <span className="vsb-locked-ico"><Lock width={26} height={26} /></span>
        <b>{ct('post_locked')}</b>
        <i>
          {underage
            ? <><Timer width={13} height={13} /> {ct('vibe_card_available_on', { date: when })}</>
            : <><Alert width={13} height={13} /> {ct('vibe_card_setup_to_view')}</>}
        </i>
        <em>{underage ? ct('view_timer') : ct('setup_vibes')}</em>
        {underage && daysLeft != null && (
          <span className="vsb-locked-days">
            {daysLeft === 0 ? 'Opens today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} to go`}
          </span>
        )}
      </span>
    </button>
  );
}

function SharedPostBody({ m, mine, gate }) {
  /* 🟢 one reading of what the media IS — see vibeChatCard.cardMedia for why a
     field named postImageUrl could not be trusted to hold an image */
  const med = cardMedia(m);
  const isVid = med.isVideo;
  // 🟢 one reading of who posted and what the poll says — see vibeChatCard.js
  const author = cardAuthor(m);
  const poll = cardPoll(m);
  const link = linkOf(m);
  const extra = Math.max(0, med.count - 1);
  // 🟥 fails OPEN only for my own message, and only once the gate has loaded.
  //    Before the snapshot lands nothing is locked and nothing is unlocked — the
  //    card renders normally, and a verdict only ever downgrades it.
  const locked = !mine && gate.ready && (!gate.registered || gate.underage);
  const [showView, setShowView] = useState(false);
  // a post whose media was deleted from Storage must not leave a torn-glyph square
  // in the middle of a chat bubble — drop the tile, keep the card
  const [mediaDead, setMediaDead] = useState(false);

  if (locked) return <SharedPostLocked m={m} gate={gate} />;

  const openPost = () => {
    try {
      window.dispatchEvent(new CustomEvent('allo:open-post', {
        detail: { postId: String(m.postId || ''), video: isVid },
      }));
    } catch {}
  };

  return (
    /* the phone reveals a "View post" button on first tap and hides it after 10s
       (viewButtonVisible / viewButtonExpires); the browser has hover, so the
       button rides in on hover and a click anywhere still opens the post */
    <div
      className={`vsb ${mine ? 'mine' : ''}`}
      onMouseEnter={() => setShowView(true)}
      onMouseLeave={() => setShowView(false)}
      onClick={openPost}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPost(); } }}
    >
      {/* ══ 🤖 🟥 A GREY LETTER WAS ALL THIS COULD DRAW. The share carried the
          author's name and id and no picture, so every shared post from anybody
          showed the same disc with an initial on it — and the account that loses
          most by that is the one whose recognisability IS a picture and a blue
          mark. The mark could not be drawn either: nothing on the card said
          which page had posted.
          🟢 Both travel now. `verified` is pageId === OFFICIAL_PAGE_ID, never a
             name match — a mark handed out by display name is a mark anybody
             can take by renaming their page. */}
      <div className="vsb-head">
        {author.image
          ? <SafeImg className="vsb-av img" src={author.image} kind="inline" loading="lazy" alt="" />
          : <span className="vsb-av">{author.initial}</span>}
        <span className="vsb-who">
          <b>
            {author.name || ct('unknown_user')}
            {author.verified && <i className="vsb-tick" title="Allo"><Verified width={13} height={13} /></i>}
          </b>
          <i>{ct('suggested_post')}</i>
        </span>
        <span className="vsb-badge"><Sparkles width={13} height={13} /> Vibes</span>
      </div>

      {m.postContent ? <p className="vsb-text">{String(m.postContent)}</p> : null}

      {/* ══ 🤖 🟥 A SHARED POLL ARRIVED AS A FLOATING SENTENCE. A poll post has no
          picture, so the media block drew nothing, and the options were on no
          copy of the card at all — what landed was the question alone, with
          nothing to say it was even a poll.
          🟢 Read-only on purpose: these tallies are a SNAPSHOT taken when the
             post was shared, and a surface that cannot hear the live count must
             not accept a vote against a stale one. The card invites; the post
             decides. */}
      {poll && (
        <div className="vsb-poll">
          {poll.options.map((o) => (
            <span className="vsb-poll-row" key={o.id}>
              <span className="vsb-poll-fill" style={{ width: `${Math.max(0, Math.min(100, o.pct))}%` }} />
              <span className="vsb-poll-txt">{o.text}</span>
              <span className="vsb-poll-pct">{o.pct}%</span>
            </span>
          ))}
          {poll.extra > 0 && (
            <span className="vsb-poll-more">{ct('vibe_card_more_options', { count: poll.extra })}</span>
          )}
          <span className="vsb-poll-foot">
            {poll.total === 0
              ? ct('b12_poll_no_votes')
              : (poll.total === 1
                ? ct('b12_poll_vote_count_one')
                : ct('b12_poll_vote_count_other', { count: poll.total }))}
            {'  ·  '}
            {poll.closed ? ct('vibe_card_results_final') : ct('vibe_card_vote_on_post')}
          </span>
        </div>
      )}

      {link && <SharedLinkCard link={link} />}

      {/* ══ 🤖 🟥 THE BROWSER HAD THE SAME BUG AS THE PHONE: it drew `postImageUrl`
          through SafeImg, and for every admin-published Allo video that field held
          the mp4. An <img> pointed at a video fires `error`, SafeImg retried it,
          and the tile dropped out entirely — which is the "empty bubble" half of
          the report.
          🟢 A clip gets a <video preload="metadata"> with `#t=0.1` on it, which is
             how a browser is ASKED for a poster frame: it fetches only the header
             and the frame at 100ms and paints that, without downloading the clip
             and without playing anything. (100ms, not 0 — the first frame of a
             phone recording is often black while the camera fades up. The app's
             videoFrame.ts asks for exactly the same offset for exactly the same
             reason.) */}
      {med.video && !mediaDead ? (
        <div className="vsb-media">
          <video
            className="vsb-poster"
            src={`${med.video}#t=0.1`}
            preload="metadata"
            muted
            playsInline
            poster={med.image || undefined}
            onError={() => setMediaDead(true)}
          />
          <span className="vsb-play"><Play width={20} height={20} /></span>
        </div>
      ) : med.image && !mediaDead ? (
        <div className="vsb-media">
          <SafeImg src={med.image} kind="inline" loading="lazy" onGone={() => setMediaDead(true)} />
          {extra > 0 && <span className="vsb-more">+{extra}</span>}
        </div>
      ) : null}

      <div className="vsb-stats">
        <span><Heart width={17} height={17} /></span>
        <span><Comment width={16} height={16} /></span>
        <span><Forward width={17} height={17} /></span>
        <button
          className={`vsb-open ${showView ? 'on' : ''}`}
          onClick={(e) => { e.stopPropagation(); openPost(); }}
        >
          {ct('view_post')} <ArrowRight width={13} height={13} />
        </button>
      </div>
    </div>
  );
}

/** 🟢 "Delete for everyone" REWRITES the doc in place rather than removing it: the app
 *  sets type:'text', text:"Message deleted", isDeleted:true and deleteField()s every
 *  media field (ChatModal ~9803). Without this branch the web fell through to the TEXT
 *  renderer, found no perDeviceKeys to decrypt, and showed "Sent before this device was
 *  linked" — the one wrong thing it could possibly say about a deleted message. */
function DeletedBody() {
  return (
    <span className="msg-deleted">
      <Slash width={15} height={15} />
      <span>Message deleted</span>
    </span>
  );
}

/* 🟢 GROUP INVITATION — the private card the app sends when someone's groupAdd
   privacy says "ask me first". Same document, same two buttons, same writes:
   accepting moves you from pendingParticipants into participants and posts
   "joined via invite" in the group; declining just drops you from pending. */
function GroupInviteCard({ m, chatId, myUid, myName, myImage, onOpenChatId }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const status = inviteStatusOf(m);
  const me = { uid: myUid, name: myName || 'A new member', image: myImage || '' };
  const mine = m.senderId === myUid;

  const act = async (which) => {
    setBusy(which); setErr('');
    try {
      if (which === 'accept') {
        await acceptGroupInvite({ chatId, messageId: m.id, groupId: m.groupId, me });
      } else {
        await declineGroupInvite({ chatId, messageId: m.id, groupId: m.groupId, me });
      }
    } catch {
      setErr('That didn’t go through — try again.');
    }
    setBusy('');
  };

  return (
    <div className="gi-wrap">
      <div className="gi-card">
        <div className="gi-head">
          {m.groupImage
            ? <img className="gi-pic" src={m.groupImage} alt="" />
            : <span className="gi-pic ph"><GroupIcon width={20} height={20} /></span>}
          <span className="gi-who">
            <b>{m.groupName || 'A group'}</b>
            <i>{mine
              ? `You invited them to join`
              : `${m.senderName || 'Someone'} invited you to join`}</i>
          </span>
        </div>

        {mine ? (
          <div className="gi-state">
            {status === 'accepted' ? 'Invitation accepted'
              : status === 'declined' ? 'Invitation declined'
                : 'Waiting for their answer'}
          </div>
        ) : status === 'pending' ? (
          <div className="gi-acts">
            <button className="gi-btn ok" disabled={!!busy} onClick={() => act('accept')}>
              <Check width={14} height={14} /> {busy === 'accept' ? 'Joining…' : 'Accept'}
            </button>
            <button className="gi-btn" disabled={!!busy} onClick={() => act('decline')}>
              <X width={14} height={14} /> Decline
            </button>
          </div>
        ) : (
          <div className={`gi-state ${status}`}>
            {status === 'accepted' ? 'You joined this group' : 'You declined this invitation'}
            {status === 'accepted' && onOpenChatId && (
              <button className="gi-open" onClick={() => onOpenChatId(m.groupId)}>Open</button>
            )}
          </div>
        )}
        {err && <div className="gi-err">{err}</div>}
      </div>
    </div>
  );
}

/**
 * A message from the official Allo account.
 *
 * 🟢 `_plain` FIRST, `text` SECOND. The support routes write plaintext, so
 *    services/messages.js copies it straight into `_plain`; the raw `text` is
 *    the fallback for a row that arrived by some other path (a history sync, a
 *    cached copy written before this code existed).
 */
/**
 * The unencrypted attachments on an official-account message.
 *
 * 🟥 NOT THE ORDINARY MEDIA PATH, AND IT MUST NOT BE. `ImageBody` and friends
 *    fetch a ciphertext URL and decrypt it with a per-chat key. These URLs are
 *    plaintext — there is no key, because a staff member has to be able to open
 *    what a user sent. Sending them through the decrypt path produces a
 *    corrupt-media error rather than a picture.
 *
 * 🟢 A VIDEO OR DOCUMENT IS A LINK, NOT AN INLINE PLAYER. Autoplaying a file
 *    that Allo sent to everyone at once is a bandwidth decision made on the
 *    recipient's behalf; a picture is small enough to show, and everything else
 *    waits to be asked for.
 */
function OfficialMedia({ media, m, onOpen }) {
  if (!media?.length) return null;
  const LABEL = { image: 'Photo', video: 'Video', audio: 'Voice message', document: 'Document' };
  /* 🟥 A BARE <a href> IS A DOWNLOAD, NOT A VIEWER, and that is the report word
     for word: an image Allo sent "won't open in the image viewer as any normal
     user sent me, it will just download it". Two things made it so — the anchor
     itself, and the fact that these files are PUT to R2 as
     `application/octet-stream`, which tells the browser to save rather than
     render whatever the anchor says.
     🟢 SO A TAP CALLS THE PRODUCT'S OWN VIEWER, the same `openMedia` an
        ordinary photo uses. Everything NEW from the official account is a real
        typed message now and never reaches this component at all; this is what
        keeps the messages already written working the same way. */
  const open = (f, kind) => {
    if (!onOpen || !m) { window.open(f.url, '_blank', 'noopener'); return; }
    /* 🟢 THE REAL MESSAGE, WITH THE ITEMS SPELLED OUT. `openMedia` builds the
       payload from `_media` / `_mediaBatch`, which a legacy `media[]` message
       never has — but it also carries the sender's name, the timestamp and the
       private-media cover check, and none of those are worth re-deriving here.
       So it is given the message AND the one item to show. */
    onOpen(m, 0, [{ kind, url: f.url, fileName: f.name || null }]);
  };
  return (
    <div className="of-media">
      {media.map((f) => {
        if (!f?.url) return null;
        if (f.kind === 'image') {
          return (
            <button key={f.url} type="button" className="of-photo" onClick={() => open(f, 'image')}>
              <img src={f.url} alt={f.name || 'attachment'} />
            </button>
          );
        }
        /* 🟢 A CLIP IS A CARD WITH A PLAY BUTTON, not a line of text. There is
           no poster to draw — a plaintext video url is a video, and pulling a
           frame out of it means downloading the whole thing, which is a
           decision on somebody's connection made on their behalf. */
        if (f.kind === 'video') {
          return (
            <button key={f.url} type="button" className="of-vid" onClick={() => open(f, 'video')}>
              <span className="of-play"><Play width={18} height={18} /></span>
              <b>{f.name || LABEL.video}</b>
            </button>
          );
        }
        /* 🟢 A VOICE NOTE AND A DOCUMENT STAY LINKS. The viewer shows pictures
           and clips; there is nothing for it to do with either of these, and a
           lightbox that opens on a PDF and then offers a download button is a
           step added rather than removed. */
        const ext = String(f.name || '').split('.').pop();
        return (
          <a key={f.url} className="of-file" href={f.url} target="_blank" rel="noreferrer">
            <span className="of-file-ic">
              {f.kind === 'audio'
                ? <Mic width={17} height={17} />
                : <DocText width={17} height={17} />}
            </span>
            <span className="of-file-t">
              <b>{f.name || LABEL[f.kind] || 'Attachment'}</b>
              <i>{ext && ext.length <= 5 && f.kind !== 'audio' ? `${ext.toUpperCase()} · ${LABEL[f.kind]}` : LABEL[f.kind]}</i>
            </span>
            <Download width={15} height={15} />
          </a>
        );
      })}
    </div>
  );
}

function SupportBody({ m }) {
  const body = m._plain ?? m.text ?? '';
  if (!body) return null;
  return <span style={{ whiteSpace: 'pre-wrap' }}>{body}</span>;
}

function MessageBody({ m, mine, onOpen, onRetry, chat, myUid, myName, vibeGate, onAsk, onAlbumItem }) {
  const chatId = chat?.id;
  const _namedMembers = useMemo(() => withEveryone(
    (Array.isArray(m?.mentions) ? m.mentions : [])
      .map((uid) => ({ uid, name: String(chat?.participantNames?.[uid] || '').replace(/\s+/g, ' ').trim() }))
      .filter((x) => x.uid && x.name),
    /* 🟥 THE ROOM IS A ROW HERE AND A UID NOWHERE ELSE — `mentions` stays a list of
       real people, so nothing tries to look one up for "@everyone". */
    !!m?.mentionsEveryone,
  ), [m?.mentions, m?.mentionsEveryone, chat?.participantNames]);
  if (m.isDeleted || m.deleted) return <DeletedBody />;
  // 🟢 media cleared by me → the same placeholder a phone-side purge produces
  if (isMediaCleared(m, chat, myUid)) return <PurgedBody m={m} />;
  // purged in Storage & Data → the bytes are gone by the user's own choice, on every
  // linked device. Show the app's placeholder, never a broken tile or a spinner.
  if (m.mediaDeletedFromStorage) return <PurgedBody m={m} />;
  switch (m.type) {
    /* 💬 A message from the official Allo account can carry PLAINTEXT files —
       a staff reply, a broadcast, a screenshot someone attached to a ticket.
       They are the only unencrypted attachments in the product, which is why
       they render from `media` here instead of going through the decrypt path
       that every other image and document takes. */
    case 'text':
      /* ©️ COPYRIGHT DISPUTE — `type: 'text'` so an old client shows the sentence;
         this one draws the card. supportPayload.status is rewritten by the server
         as the ticket moves and arrives through the chat's own listener. */
      if (m.supportKind === 'dispute' && m.supportPayload) return <DisputeCard payload={m.supportPayload} text={m._plain ?? m.text} mine={mine} />;
      /* 🟥 THE MEMBERS THIS MESSAGE NAMED, NOT THE WHOLE GROUP. The document
         stores uids so a rename follows the mention instead of freezing the old
         spelling — so the name has to be looked up, and `participantNames` is the
         map every other group surface here already trusts for exactly that. */
      return (m.media?.length
      ? <><TextBody m={m} onAsk={onAsk} members={_namedMembers} /><OfficialMedia media={m.media} m={m} onOpen={onOpen} /></>
      : <TextBody m={m} onAsk={onAsk} members={_namedMembers} />);
    case 'image': return <ImageBody m={m} mine={mine} onOpen={onOpen} chatId={chatId} members={_namedMembers} />;
    case 'video': return <VideoBody m={m} mine={mine} onOpen={onOpen} chatId={chatId} members={_namedMembers} />;
    case 'images': return <BatchBody m={m} mine={mine} onOpen={onOpen} chatId={chatId} members={_namedMembers} myUid={myUid} onAlbumItem={onAlbumItem} />;
    case 'audio': return <AudioBody m={m} mine={mine} />;
    case 'document': return <DocBody m={m} />;
    case 'sticker': case 'gif': return <StickerBody m={m} onOpen={onOpen} />;
    case 'location': return <LocationBody m={m} />;
    case 'poll': return <PollBubble m={m} mine={mine} chat={chat} myUid={myUid} myName={myName} />;
    case 'flight': return <FlightCard m={m} mine={mine} />;
    case 'shared_post': return <SharedPostBody m={m} mine={mine} gate={vibeGate || VSB_GATE_UNKNOWN} />;
    case 'security': return <SecurityCard m={m} />;
    case 'system': return <span style={{ fontStyle: 'italic', color: 'var(--text-3)' }}>{m.text}</span>;
    /* 💬 the official Allo account. Its answers are written as several lines
       with bullets, so the whitespace is load-bearing — a plain <span> collapsed
       them into one paragraph. */
    case 'support': return (m.media?.length
      ? <><SupportBody m={m} /><OfficialMedia media={m.media} m={m} onOpen={onOpen} /></>
      : <SupportBody m={m} />);
    default: return <span style={{ color: 'var(--text-3)' }}>Unsupported message</span>;
  }
}

const SYS_ICON = {
  group: GroupIcon, pencil: Pencil, image: ImageIcon, 'person-add': PersonAdd,
  exit: Logout, 'person-remove': PersonRemove, mail: Mail, 'shield-check': ShieldCheck,
  shield: ShieldLock, gear: Gear, timer: Timer, camera: Camera, pin: MapPin, info: Info,
};

/** 🟢 A GROUP EVENT IS NOT SOMETHING ANYONE SAID. "Abdullah left the group", a promotion,
 *  a permission change — the app has always drawn these as a centred chip, and the web
 *  drew them as chat bubbles with the writer's raw English in them. Same shape as the day
 *  separator, so a transcript full of them still reads as a transcript. */
/* 🟢 THE SAME NAME COLOUR AS THE PHONE, from the same hash of the same uid — so a
   member who is teal in the app is teal here. (MessageBubble.tsx:3529.) */
/* 🟢 moved to ./nameColor so the member row and the tag preview can agree with
   the bubble about what colour a name is — see that module's note */

/** The member's picture, beside their bubble. Falls back to their initial on their own
 *  colour, so a member with no photo is still identifiable at a glance. */
/* 👥 🟥 A GROUP HAS NO "IS THIS THE OFFICIAL ACCOUNT" TO ASK. Everywhere else
   that question is answered by looking at who the chat is WITH — but the other
   party to a group is the group, and Allo is one voice inside it. So the answer
   comes off the MESSAGE: the server stamps `officialSender` on what the console
   sends, older announcements carry `isOfficial`, and the uid outlives both. The
   phone's MessageBubble makes the same three-way check for the same reason. */
const isOfficialSender = (m) => !!m
  && (m.officialSender === true || m.isOfficial === true || isOfficialUid(m.senderId));

/* 🟥 `participantImages` HAS NO ENTRY FOR ALLO, and never will — it is built
   from the people in the group, and the official account is not one of them. So
   Allo's announcement wore a coloured letter placeholder in the avatar column
   while carrying its real picture on the message the whole time. `img` is that
   picture; the participant map still wins for everybody who IS a member. */
function MemberAvatar({ chat, uid, name, img }) {
  const src = chat?.participantImages?.[uid]
    || (typeof img === 'string' && /^https?:/i.test(img) ? img : null);
  if (src) return <img className="row-av-img" src={src} alt="" draggable={false} />;
  const letter = (String(name || '').trim().charAt(0) || '?').toUpperCase();
  return <span className="row-av-ph" style={{ background: nameColor(uid) }}>{letter}</span>;
}

function SystemRow({ m, myUid }) {
  const txt = systemText(m, myUid);
  if (!txt) return null;
  const Icon = SYS_ICON[systemIcon(m)] || Info;
  return (
    <div className="sys-row">
      <span className="sys-chip">
        <Icon width={13} height={13} />
        <span>{txt}</span>
      </span>
    </div>
  );
}

const CALL_ICON = {
  'phone-outgoing': PhoneOutgoing,
  'phone-incoming': PhoneIncoming,
  'phone-missed': PhoneMissed,
  'phone-off': PhoneOff,
  'video-off': VideoOff,
  video: Video,
  phone: Phone,
};

/** 🟢 A call is an EVENT, not something anyone said — so it renders as a centred system
 *  row rather than a chat bubble, and it carries no ticks. The wording comes straight
 *  from the app's callBubbleRender table: the stored outcome is identical on both
 *  sides and only the perspective flips, so "Cancelled" for the caller is "Missed" for
 *  the callee — approximating that would make the web state something untrue about
 *  what the other person did. */
function CallRow({ m, myUid, chatId, onOpenCallChat }) {
  const inp = callInputs(m, myUid);
  const c = inp.isGroup ? renderGroupCallBubble(inp) : renderCallBubble(inp);
  const Icon = CALL_ICON[c.icon] || Phone;
  const red = c.statusColor;
  /* \U0001f7e2 A GROUP CALL THAT HAD A CHAT IS A DOOR. Anything said during a call
     is written to its own `call_threads` thread rather than into the
     conversation — deliberately, so it does not turn up in the chat afterwards —
     which leaves this row as the only way back to it. A call with no session id
     recorded had no thread, so it stays an ordinary row. */
  const sessionId = m?.callData?.callSessionId || null;
  const canOpen = !!(inp.isGroup && sessionId && onOpenCallChat);

  /* 💬 WHAT WAS SAID DURING THE CALL, PREVIEWED ON THE ROW ITSELF.
     The thread is invisible everywhere else by design, so a row that only says
     "started a video call" gives no reason to believe there is anything behind
     it — and nobody clicks a door they cannot tell is a door. Showing the last
     line and the face that said it is both the evidence and the invitation.
     Reads are memoised per call in services/callThreads, so a chat holding
     thirty finished calls costs thirty reads, not thirty per render. */
  const [, bumpThread] = useState(0);
  useEffect(() => {
    if (!canOpen || !chatId) return undefined;
    let live = true;
    loadCallThread(chatId, sessionId).then(() => { if (live) bumpThread((n) => n + 1); });
    const off = subscribeCallThreads(() => { if (live) bumpThread((n) => n + 1); });
    return () => { live = false; off(); };
  }, [canOpen, chatId, sessionId]);
  const thread = canOpen && chatId ? peekCallThread(chatId, sessionId) : null;
  const last = (thread && thread.last) || null;
  const threadCount = thread ? thread.count : 0;
  return (
    /* 🟢 IN A GROUP IT IS A MESSAGE BUBBLE, NOT A CENTRED NOTICE — the same
       shape and the same side the phone gives it, because whoever started the
       call is the person who "said" this and the reader locates it by side
       before reading a word of it. A 1:1 call stays a centred system row: there
       are only two people, so there is no side to learn. */
    <div className={`call-row ${inp.isGroup ? 'as-bubble' : ''} ${inp.isGroup && inp.isMyCall ? 'mine' : ''}`}>
      <span
        className={`call-pill ${canOpen ? 'openable' : ''} ${last ? 'has-thread' : ''}`}
        style={red ? { color: red } : undefined}
        role={canOpen ? 'button' : undefined}
        tabIndex={canOpen ? 0 : undefined}
        title={canOpen ? 'See what was said during this call' : undefined}
        onClick={canOpen ? () => onOpenCallChat(sessionId) : undefined}
        onKeyDown={canOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenCallChat(sessionId); } } : undefined}
      >
        <span className="call-main">
          <span className="call-ico" style={red ? { background: 'rgba(255,59,48,.14)' } : undefined}>
            <Icon width={15} height={15} />
          </span>
          <span className="call-txt">
            <b>{c.statusText}</b>
            {c.displayDuration ? <em>{c.displayDuration}</em> : null}
            {c.noteText ? <em className="call-note">{c.noteText}</em> : null}
          </span>
          <span className="call-time">{timeLabel(m.timestamp)}</span>
        </span>
        {last && (
          <span className="call-thread-wrap">
            <span className="call-thread">
              <Avatar
                profile={{ fullName: last.senderName, profileImage: last.senderImage }}
                size="sm"
              />
              <span className="call-thread-txt">
                <b>{last.senderId === myUid ? 'You' : (last.senderName || 'Allo user')}</b>
                <span>{last.text}</span>
                <i>{timeLabel(last.timestamp)}</i>
              </span>
            </span>
            {/* 🟢 THE INVITATION, SPELLED OUT — the phone's card says this, and it
                is the only thing on the row that admits there is more behind it.
                The message count sits beside it rather than replacing it: one
                says what will happen, the other says whether it is worth it. */}
            <span className="call-thread-open">
              <em>{thread.capped ? '20+' : threadCount} message{threadCount === 1 && !thread.capped ? '' : 's'}</em>
              <b>Tap to view chat <ChevronRight width={13} height={13} /></b>
            </span>
          </span>
        )}
      </span>
    </div>
  );
}

/** "3 unread messages" — a rule, a pill, a rule. Renders only above its anchor. */
function UnreadSep({ mark, id }) {
  if (!mark?.anchorId || mark.anchorId !== id) return null;
  return (
    <div className={`unread-sep ${mark.gone ? 'gone' : ''}`}>
      <span className="u-rule" />
      <span className="u-pill">
        {mark.count} unread message{mark.count === 1 ? '' : 's'}
      </span>
      <span className="u-rule" />
    </div>
  );
}

/**
 * 👍 The reactions on one message: this person's on an ordinary one, and
 * EVERYBODY's on a broadcast.
 *
 * 🟥 A BROADCAST IS N COPIES OF ONE MESSAGE, so `m.reactions` on the copy in this
 *    chat holds only what the owner of this account did. The running total across
 *    the whole audience lives in one public document per broadcast — see
 *    services/broadcastReactions.js — and the pill draws that when it exists.
 *
 * 🟢 IT RENDERS NOTHING WHEN THERE IS NOTHING, which is the overwhelmingly common
 *    case, so this costs an ordinary chat one function call per bubble and no DOM.
 */
function ReactionPill({ m, mine, onOpenReactions }) {
  const bid = m && m.broadcastId ? String(m.broadcastId) : '';
  const [agg, setAgg] = useState(() => peekBroadcastCounts(bid));
  useEffect(() => {
    if (!bid) { setAgg(null); return undefined; }
    setAgg(peekBroadcastCounts(bid));
    return subscribeBroadcastCounts(bid, setAgg);
  }, [bid]);

  const view = reactionDisplay(m && m.reactions, agg);
  if (!view.total) return null;
  /* 👍 CLICKING IT ASKS WHO. Reading the count was the half this client had; the
     other half is the roster behind it, and the phone had no answer either until
     now (its pill opened the emoji picker). Same sheet, same tie-break — see
     services/reactionRoster.js, generated from the phone's own module. */
  return (
    <button
      type="button"
      className={`rx-pill ${mine ? 'out' : 'in'}`}
      title={`${view.total} ${view.total === 1 ? 'reaction' : 'reactions'}`}
      onClick={() => onOpenReactions?.(m, agg)}
    >
      {view.emojis.map((e) => <span key={e} className="rx-emoji">{e}</span>)}
      {view.total > 1 && <span className="rx-count">{view.total}</span>}
    </button>
  );
}

/* 👍 WHO REACTED — the browser's half of the phone's ReactionDetailsSheet.
 *
 * 🟢 It slides up from the bottom on a narrow window and sits as a centred card
 *    on a wide one (one rule, in media.css) — there is no drag-to-close here
 *    because there is no thumb: Escape, the backdrop and the × are the three
 *    ways out a pointer expects.
 *
 * 🟢 A BROADCAST SAYS WHY THERE ARE NO NAMES rather than drawing an empty list,
 *    exactly as the phone does: each recipient reacted on their own private copy.
 */
function ReactionSheet({ open, m, agg, myUid, nameOf, onPick, onClose, onMore }) {
  const [tab, setTab] = useState(null);
  useEffect(() => { if (open) setTab(null); }, [open, m && m.id]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const roster = useMemo(() => rosterFor(m, myUid, agg), [m, myUid, agg]);
  useEffect(() => {
    if (tab != null && !roster.tabs.some((x) => x.emoji === tab)) setTab(null);
  }, [roster, tab]);

  if (!open || !m) return null;
  const rows = rowsForTab(roster, tab);

  return (
    <div className="rx-sheet-wrap" onClick={onClose} role="presentation">
      <div className="rx-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="rx-sheet-head">
          <span className="rx-sheet-title">Reactions</span>
          <button type="button" className="rx-sheet-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        {roster.tabs.length > 1 && (
          <div className="rx-tabs">
            {roster.tabs.map((x) => (
              <button
                type="button"
                key={x.emoji ?? '_all'}
                className={`rx-tab ${x.emoji === tab ? 'on' : ''}`}
                onClick={() => setTab(x.emoji)}
              >
                {x.emoji ? `${x.emoji} ${x.count}` : `All ${x.count}`}
              </button>
            ))}
          </div>
        )}
        {roster.namesKnown ? (
          <div className="rx-rows">
            {rows.map((r) => (
              <div className={`rx-row ${r.isMe ? 'me' : ''}`} key={`${r.uid}:${r.emoji}`}>
                <span className="rx-row-name">
                  {r.isMe ? 'You' : (nameOf?.({ senderId: r.uid }) || 'Allo user')}
                </span>
                {r.isMe && <span className="rx-row-hint">click your emoji below to remove</span>}
                <span className="rx-row-emoji">{r.emoji}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="rx-agg-note">
            Everyone who received this announcement can react. Their names are private.
          </p>
        )}
        {/* the quick strip scrolls UNDER the fixed "+" at the right (same as the app) */}
        <div className="rx-picker-wrap">
          <div className="rx-picker">
            {REACTIONS.map((e) => (
              <button
                type="button"
                key={e}
                className={`rx-pick ${roster.mine === e ? 'on' : ''}`}
                onClick={() => onPick?.(m, reactionTapIntent(roster.mine, e).emoji)}
              >
                {e}
              </button>
            ))}
          </div>
          {onMore && (
            <div className="rx-more-wrap">
              <button type="button" className="rx-more" onClick={() => onMore(m)} aria-label="More reactions" title="More reactions">+</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* 🟥 EVERY KEYSTROKE RE-RENDERED THE WHOLE HISTORY. The composer's text lives in
   ChatWindow, so typing one letter re-ran ChatWindow — and with it every bubble,
   twice (text + caret). A bubble now skips unless something it DRAWS changed:
   callbacks are ignored (they are re-created per render but never drawn), the
   chat document only matters to MY bubbles (their ticks read its receipts) or
   when the names/tags/pictures a bubble shows change. Rows keep their object
   identity across rebuilds (see `grouped`), so an unchanged message is `===`. */
/* 🔓 unsealed fields per message id (see the decrypt in the live-messages effect) */
const _plainCache = new Map();
const _RECEIPT_KEY = /seen|deliver|read|receipt|reaction|status|lastSeen|^_/i;
function decryptSig(m) {
  const o = {};
  for (const k of Object.keys(m || {})) if (!_RECEIPT_KEY.test(k)) o[k] = m[k];
  try { return JSON.stringify(o); } catch { return String(Math.random()); }
}

/** Two message rows that draw the same — every field identical, nested values by content. */
function sameRowShape(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a); const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    const x = a[k]; const y = b[k];
    if (x === y) continue;
    if (!x || !y || typeof x !== 'object' || typeof y !== 'object') return false;
    try { if (JSON.stringify(x) !== JSON.stringify(y)) return false; } catch { return false; }
  }
  return true;
}
const _CHAT_FIELDS_FOR_OTHERS = ['id', 'isGroup', 'participantNames', 'participantImages', 'participantTags'];
function bubblePropsEqual(prev, next) {
  for (const k of new Set([...Object.keys(prev), ...Object.keys(next)])) {
    const a = prev[k]; const b = next[k];
    if (a === b) continue;
    if (typeof a === 'function' && typeof b === 'function') continue;
    if (k === 'chat') {
      if (next.mine) return false;                       /* ticks read the chat's receipts */
      for (const f of _CHAT_FIELDS_FOR_OTHERS) {
        const x = a && a[f]; const y = b && b[f];
        if (x === y) continue;
        try { if (JSON.stringify(x) !== JSON.stringify(y)) return false; } catch { return false; }
      }
      continue;
    }
    return false;
  }
  return true;
}
const Bubble = React.memo(BubbleView, bubblePropsEqual);
function BubbleView({ m, mine, onOpen, onForward, onReply, onJump, nameOf, quoted, flash,
  groupPosition = 'single', chat, myUid, myName, onRetry, onRetrySend, onDiscard,
  starred, onStar, onDelete, vibeGate, receiptsOn = true, onAsk, onOpenReactions, onAlbumItem }) {
  const media = ['image', 'video', 'images', 'sticker', 'gif'].includes(m.type);
  const isText = m.type === 'text';
  // a shared Vibes post is a full card with its own background and radius — the
  // bubble behind it goes flat, exactly like the location card above
  const isVPost = m.type === 'shared_post';
  // a location card brings its own surface, radius and shadow — the bubble must get
  // out of its way rather than wrap it in a second one
  const isLocCard = m.type === 'location' && !m.live;
  const isFlight = m.type === 'flight';
  // the login card brings its own surface, border and radius — same deal as a flight card
  const isSec = m.type === 'security';
  const hasLink = m.type === 'text' && !!m.linkMetadata;
  // 🟢 TAIL RULE (identical to the app): only the NEWEST bubble of a same-sender run
  //    carries the beak — groupPosition 'single' or 'bottom'. Send another message
  //    within the group window and the tail moves down to that new last bubble.
  const showTail = isText && (groupPosition === 'single' || groupPosition === 'bottom');
  const outer = mine ? 'Right' : 'Left';
  // Same corner maths as the app: the tail-side bottom corner squares off so the beak
  // joins cleanly; grouped-but-not-newest bubbles use a small radius to stack.
  const radiusStyle = isText ? {
    [`borderTop${outer}Radius`]: (groupPosition === 'top' || groupPosition === 'single') ? 18 : 6,
    [`borderBottom${outer}Radius`]: showTail ? 4 : 6,
  } : {};
  // 🟢 GROUPS ON A BIG SCREEN. The phone has no room for a per-message avatar and prints
  //    the sender's name above every incoming bubble; the browser has the room, so the
  //    picture goes in a column beside the bubble and the name only opens a run. The
  //    avatar sits on the LAST bubble of a run (where the tail is, so they point at the
  //    same person) and the column keeps its width on the others, which is what stops a
  //    run from stair-stepping left and right as it goes.
  const groupIn = !mine && !!chat?.isGroup;
  const runFirst = groupPosition === 'single' || groupPosition === 'top';
  const runLast = groupPosition === 'single' || groupPosition === 'bottom';
  const senderName = groupIn ? (nameOf?.(m) || 'Allo user') : '';

  return (
    /* 🟢 `has-media` on the ROW, not just `media` on the bubble. The spacing rule
       has to compare a row with the row before it, and a class on the child
       cannot be reached from a sibling selector. */
    <div className={`row ${mine ? 'out' : 'in'} gp-${groupPosition} ${flash ? 'flash' : ''} ${groupIn ? 'grp' : ''} ${media ? 'has-media' : ''}`} data-mid={m.id}>
      {groupIn && (
        <span className="row-av" aria-hidden={!runLast}>
          {runLast && <MemberAvatar chat={chat} uid={m.senderId} name={senderName} img={m.senderImage} />}
        </span>
      )}
      <div className={`bubble ${media ? 'media' : ''} ${isLocCard ? 'loc' : ''} ${isVPost ? 'vpost' : ''} ${isFlight ? 'flight' : ''} ${isSec ? 'sec' : ''} ${hasLink ? 'haslink' : ''}`} style={{ ...radiusStyle, overflow: 'visible' }}>
        {showTail && <BubbleTail side={mine ? 'right' : 'left'} color={mine ? 'var(--bubble-out)' : 'var(--bubble-in)'} />}

        {/* ✦ WRITTEN BY ALLO AI.
            🟥 IT TAKES THE BUBBLE'S OWN CORNER, and on the web that is one
               declaration rather than any arithmetic: `border-radius: inherit`
               on an absolutely positioned child resolves to the parent's radius,
               so the badge follows `--bubble-rad` through all three appearance
               settings and through the inline override a grouped run applies.
               The phone had to be told the number; here it can simply ask.
            🟢 AND IT SITS IN THE TOP INNER CORNER — left on my bubble, right on
               theirs — because the outer one is where a run tucks together and,
               in a group, where the sender's name already is. */}
        {m.isAI && (
          <span className={`ai-badge ${mine ? 'out' : 'in'}`}>
            <Sparkles width={10} height={10} />
            Allo AI
          </span>
        )}

        {/* 🟢 THE ACTION PILL LIVES INSIDE THE BUBBLE, ABSOLUTELY POSITIONED. That's what
            makes it "smart" for text, whose width nobody can predict: anchored to the
            bubble's own edge it always lands exactly beside the message, at any length,
            while being out of flow means it can't squeeze the bubble or change where a
            line wraps. (The previous in-flow gutter reserved 64px on every row and did
            both.) `top:50%` centres it against the bubble rather than the row, so a tall
            photo or a flight card gets the pill at its middle, not down by the clock. */}
        {!m._pending && !m.isDeleted && !m.deleted && (
          <div className="row-acts">
            <button className="ract" data-tip="Reply" aria-label="Reply" onClick={() => onReply?.(m)}>
              <Reply width={15} height={15} />
            </button>
            {/* 👍 react from the browser too — the same sheet the pill opens, with its picker */}
            {onOpenReactions && (
              <button className="ract" data-tip="React" aria-label="React" onClick={() => onOpenReactions(m)}>
                <Smile width={15} height={15} />
              </button>
            )}
            {/* a failed send and a purged photo can't be forwarded — offering the
                button just to answer "no" in a modal is worse than not offering it */}
            {canForward(m) && (
              <>
                <span className="ract-sep" />
                <button className="ract" data-tip="Forward" aria-label="Forward" onClick={() => onForward?.(m)}>
                  <Forward width={15} height={15} />
                </button>
              </>
            )}
            <span className="ract-sep" />
            {/* 🟢 the same star the phone keeps — one Firestore subcollection, so a star
                made here is in the app's Starred screen on its next snapshot */}
            <button
              className={`ract ${starred ? 'starred' : ''}`}
              data-tip={starred ? 'Unstar' : 'Star'}
              aria-label={starred ? 'Unstar' : 'Star'}
              onClick={() => onStar?.(m)}
            >
              {starred ? <StarFill width={15} height={15} /> : <Star width={15} height={15} />}
            </button>
            <span className="ract-sep" />
            <button className="ract danger" data-tip="Delete" aria-label="Delete" onClick={() => onDelete?.(m)}>
              <Trash width={15} height={15} />
            </button>
          </div>
        )}
        {/* 🟢 "Forwarded from Nora" / "Forwarded from You" — the app has said this for
            ages and the web just said "Forwarded", which is the one thing it already
            knew. The name is stored on the doc as `originalSenderName`, but the reader
            overrides it with "You" whenever `originalSenderId` is us, so the same
            message reads correctly for everyone who receives it. */}
        {groupIn && runFirst && (
          <div className="sender-row">
            <span className="sender-name" style={{ color: nameColor(m.senderId) }}>{senderName}</span>
            {/* 👥 the same tick the official account wears in its own chat header */}
            {isOfficialSender(m) && <Verified width={13} height={13} />}
            {!isOfficialSender(m) && m.senderId ? <SupporterBadge uid={m.senderId} size={12} /> : null}
            {/* 🏷 the member's tag — same map, same place, as the phone draws it */}
            {chat?.participantTags?.[m.senderId] ? (
              <span className="member-tag">{chat.participantTags[m.senderId]}</span>
            ) : null}
          </div>
        )}
        {m.forwarded && (
          <div className="forwarded-tag">
            <Forward width={13} height={13} /> {forwardedLabel(m, myUid)}
          </div>
        )}
        {m.replyTo && (
          <ReplyQuote
            replyTo={m.replyTo}
            original={quoted}
            name={m.replyTo.senderId === myUid ? 'You' : nameOf?.({ senderId: m.replyTo.senderId })}
            onJump={onJump}
          />
        )}
        <MessageBody m={m} mine={mine} onOpen={onOpen} onRetry={onRetry} chat={chat} myUid={myUid} myName={myName} vibeGate={vibeGate} onAsk={onAsk} onAlbumItem={onAlbumItem} />
        {/* 👍 WHAT EVERYBODY ELSE THOUGHT OF IT. This client has never had
            message reactions — no picker, no long-press peek, nothing that could
            produce one — but it had no way to SEE them either, so an
            announcement forty people had reacted to looked untouched here, and
            a reaction somebody made on their phone vanished when they opened
            the browser. Reading is the half that was missing. */}
        <ReactionPill m={m} mine={mine} onOpenReactions={onOpenReactions} />
        <div className="meta">
          {/* 🟢 A failed send is RECOVERABLE: the prepared bytes are still in memory,
              so Retry re-uploads without re-picking the file. Without these the bubble
              was a dead end — "Not sent" with no way forward and no way to clear it. */}
          {m._failed && (
            <>
              <button className="mb-resend" onClick={() => onRetrySend(m)}>Retry</button>
              <button className="mb-resend ghost" onClick={() => onDiscard(m)}>Discard</button>
            </>
          )}
          <span className={`time ${m._failed ? 'failed' : ''}`}>{m._failed ? 'Not sent' : timeLabel(m.timestamp, m._lateSend)}</span>
          {/* 🖥📱 sent from my phone — the browser names the app, never itself (services/messageOrigin) */}
          {mine && !m._pending && originBadge(m, myUid, 'web') === 'phone' && (
            <span className="mb-origin" title="Sent from your phone" aria-label="Sent from your phone"><Smartphone width={12} height={12} /></span>
          )}
          {mine && !m._pending && <Ticks msg={m} chat={chat} myUid={myUid} receiptsOn={receiptsOn} />}
        </div>
      </div>
    </div>
  );
}

export default function ChatWindow({ chat, myUid, myProfile, onBack, onClose, onDropChat, onForward, onOpenMedia, onOpenChatId, allChats, syncedHistory, blocking }) {
  const [rawMsgs, setRawMsgs] = useState([]);
  const [decMsgs, setDecMsgs] = useState([]);
  // 👍 read at reaction time (the chat list's revert needs the newest message) without re-creating the handler
  const decMsgsRef = useRef(decMsgs);
  decMsgsRef.current = decMsgs;
  // …and the chat document as it is NOW (the revert reads its marker), without keying the handler on it
  const reactChatRef = useRef(chat);
  reactChatRef.current = chat;

  // ══ TRANSLATE MESSAGES ═══════════════════════════════════════════════════
  // The setting lives on the chat doc under MY uid, so this listener is also
  // what makes the phone's switch flip the web (and vice versa) with no extra
  // wiring. Eligibility is the app's: received text only, newer than the
  // `enabledAt` cutoff, cache first so a re-open costs no API calls.
  // ── 🔒 READ RECEIPTS: effective setting (per-chat override → global), synced.
  //    Fails CLOSED until both docs land — see readReceipts.js for why that race
  //    matters. `rrOn` gates every READ signal; delivery is never gated.
  // 🔒 PRIVATE MEDIA: mirror the app's own per-chat flag in, so a cover set on
  //    the phone is already up before the first bubble paints here.
  useEffect(() => watchPrivateMedia(chat.id, myUid), [chat.id, myUid]);

  const [rr, setRr] = useState(OFF_UNKNOWN);
  useEffect(() => watchReadReceipts(chat.id, myUid, setRr), [chat.id, myUid]);
  const rrOn = rr.ready && rr.enabled;

  const [txSettings, setTxSettings] = useState(null);
  useEffect(() => watchTranslation(chat.id, myUid, setTxSettings), [chat.id, myUid]);
  const txDoneRef = useRef(new Set());
  useEffect(() => { txDoneRef.current = new Set(); }, [chat.id, txSettings?.enabled, txSettings?.enabledAt]);
  useEffect(() => {
    if (!txSettings?.enabled || !decMsgs.length) return;
    let alive = true;
    const todo = decMsgs.filter((m) => !txDoneRef.current.has(m.id)
      && shouldTranslate(m, myUid, txSettings, tsOf));
    if (!todo.length) return;
    (async () => {
      const patch = new Map();
      for (const m of todo) {
        txDoneRef.current.add(m.id);
        const src = m._plain || m.text || '';
        let out = cachedTranslation(m.id);
        if (!out) {
          // eslint-disable-next-line no-await-in-loop
          out = await translateText(src, 'English');
          if (out && out !== src) setCachedTranslation(m.id, out);
        }
        if (out && out !== src) patch.set(m.id, out);
      }
      if (!alive || !patch.size) return;
      setDecMsgs((prev) => prev.map((m) => (patch.has(m.id) ? { ...m, _translated: patch.get(m.id) } : m)));
    })();
    return () => { alive = false; };
  }, [decMsgs, txSettings, myUid]);

  // translation OFF → drop what's on screen at once (the app reverts too)
  useEffect(() => {
    if (txSettings?.enabled) return;
    setDecMsgs((prev) => (prev.some((m) => m._translated)
      ? prev.map((m) => (m._translated ? { ...m, _translated: null } : m)) : prev));
  }, [txSettings?.enabled]);
  const [profile, setProfile] = useState(null);
  const [online, setOnline] = useState(false);
  const [lastSeen, setLastSeen] = useState(null); // ms — drives "last seen …"
  /* 📝 DRAFT: what was typed here and not sent comes back (services/drafts) — after switching
     chats, a reload, closing the tab. The chat list shows it as "Draft: …". Sending empties
     the box, which forgets it. */
  const [text, setText] = useState(() => { bindDrafts(myUid); return getDraft(chat?.id); });
  useEffect(() => { setDraft(chat?.id, text); }, [text, chat?.id]);
  /* ✦ ARMED FOR ALLO AI. Set when the draft ends in the mention, cleared when
     the card is dismissed or the message goes. The phone strips the tag out of
     the input and puts it back at send time; this does the same, so a message
     written here carries the mention for both clients to draw. */
  const [aiMode, setAiMode] = useState(false);
  const [aiError, setAiError] = useState(0);
  /* ══ 🤖 "@" IN A GROUP ═══════════════════════════════════════════
     🟥 THE CARET IS THE FEATURE. Which "@" run is being typed, where a picked name
        goes, and whether the list should still be open are all questions about the
        cursor — and a controlled <textarea> reports its value, not its selection, so
        it is read off the event on every interaction that can move it.
     🟥 AND ESCAPE HAS TO CLOSE IT. Without a dismissal flag the only way out of
        the list is to delete the "@", which is not what somebody who genuinely meant
        to type an address expects. The flag clears itself when the run ends, so the
        next "@" opens normally. */
  const taRef = useRef(null);
  const hiRef = useRef(null);
  const [caret, setCaret] = useState(0);
  const [mentionOff, setMentionOff] = useState(false);
  /* 🟥 NOT ME, AND NOT SOMEBODY WHO LEFT. Mentioning myself is a notification to
     myself; mentioning a member who left is a blue name in a room they cannot read. */
  const mentionUids = useMemo(() => {
    if (!chat?.isGroup) return [];
    const left = Array.isArray(chat?.leftParticipants) ? chat.leftParticipants : [];
    const seen = new Set();
    /* 🟥 the same defence the phone needs: a participant row is usually a uid
       string and is sometimes an object, and a string-only filter turns those groups
       into a picker that never opens, with nothing logged anywhere */
    return (Array.isArray(chat?.participants) ? chat.participants : [])
      .map((u) => (typeof u === 'string' ? u : (u?.uid || u?.id || '')))
      .filter((u) => {
        if (!u || u === myUid || left.includes(u) || seen.has(u)) return false;
        seen.add(u);
        return true;
      });
  }, [chat?.isGroup, chat?.participants, chat?.leftParticipants, myUid]);
  const mentionRunOpen = !!chat?.isGroup && !mentionOff && !!activeMentionQuery(text, caret);
  const mentionPeople = useLiveMentionMembers(
    mentionUids, chat?.participantNames, chat?.participantImages, mentionRunOpen,
  );
  /* 🟥 @everyone WAKES THE ROOM AND PIERCES EVERY MUTE IN IT, so who may send one is
     decided by the group's size and this person's admin rights — free in a
     family-sized room where a mistake costs nothing, admin-only where it is a
     broadcast. An unknown member count counts as big; see canMentionEveryone. */
  const mentionEveryoneAllowed = !!chat?.isGroup && canMentionEveryone({
    isAdmin: !!(myUid && Array.isArray(chat?.admins) && chat.admins.includes(myUid)),
    memberCount: mentionUids.length + 1,
  });
  const mentionMembers = useMemo(
    () => withEveryone(mentionPeople, mentionEveryoneAllowed),
    [mentionPeople, mentionEveryoneAllowed],
  );
  const mentionState = useMemo(
    () => ((chat?.isGroup && !mentionOff && mentionMembers.length)
      ? mentionPickerState(text, caret, mentionMembers)
      : null),
    [chat?.isGroup, mentionOff, mentionMembers, text, caret],
  );
  /* 🟢 the dismissal is for ONE run, not for the chat */
  useEffect(() => {
    if (mentionOff && !activeMentionQuery(text, caret)) setMentionOff(false);
  }, [mentionOff, text, caret]);
  const mentionSegs = useMemo(() => {
    if (!chat?.isGroup || !text || !mentionMembers.length) return null;
    const segs = segmentMentions(text, mentionMembers);
    return segs.some((s) => s.uid) ? segs : null;
  }, [chat?.isGroup, text, mentionMembers]);
  const pickMention = useCallback((m) => {
    const st = mentionPickerState(text, caret, mentionMembers);
    if (!st || !m) return;
    const next = applyMention(text, st.start, caret, m.name);
    setText(next.text);
    setCaret(next.caret);
    /* 🟥 THE FIELD HAS TO BE GIVEN THE CARET BACK. The list is dismissed by a
       mousedown that never let the textarea lose focus, but React has just replaced
       the value — without this the cursor lands at the end of the whole draft, which
       is the wrong place whenever somebody is fixing a name mid-sentence. */
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      try { ta.focus(); ta.setSelectionRange(next.caret, next.caret); } catch { /* detached */ }
    });
  }, [text, caret, mentionMembers]);
  const [showEmoji, setShowEmoji] = useState(false);
  /* 👍 the message whose reaction is being picked from the full emoji picker (the
     reaction sheet's "+"); null = the picker is the composer's */
  const [reactionEmojiTarget, setReactionEmojiTarget] = useState(null);
  useEffect(() => { if (!showEmoji) setReactionEmojiTarget(null); }, [showEmoji]);
  const [tray, setTray] = useState([]);        // staged attachments
  const [pending, setPending] = useState([]);  // optimistic outgoing media bubbles
  const [dragOver, setDragOver] = useState(false);
  const [chatOver, setChatOver] = useState(false);   // a chat row is hovering this pane
  const [showLocation, setShowLocation] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [typers, setTypers] = useState([]);
  // ── flight detection state ──
  const [replySrc, setReplySrc] = useState(null);    // the message being replied to

  /* ═══════════════════════════════════════════════════════════════════════
     💬 THEIR ABOUT STATUS — the speech bubble under the header picture.

     🟢 The visibility rule is services/about, a deliberate port of the phone's
        services/aboutStatus. There is no server-side enforcement of who may see
        a status, so that rule IS the protection, on both clients.
     🟢 A one-shot read, matching the phone: the status is a slow-moving thing
        and a listener per open chat is a listener per open chat.
     ═══════════════════════════════════════════════════════════════════════ */
  const [about, setAbout] = useState(null);          // { text, expiresAt } | null
  const [showAbout, setShowAbout] = useState(true);
  const [flashId, setFlashId] = useState(null);     // jump-to-original highlight
  const [showJump, setShowJump] = useState(false);  // the jump-to-latest button
  const [jumpCount, setJumpCount] = useState(0);    // messages that arrived while scrolled up
  const [unreadMark, setUnreadMark] = useState(null); // { anchorId, count, gone }
  const markedRef = useRef({ chatId: null, ms: 0 });
  const jumpSeen = useRef(new Set());   // ids already counted on the jump badge
  const onScreenIds = useRef([]);       // what was already loaded when you scrolled up
  const upRef = useRef(false);
  const unreadFrozen = useRef(null);    // the chat we've already decided the mark for
  const unreadTimer = useRef(null);
  const flashTimers = useRef([]);
  const [flight, setFlight] = useState(null);        // { detection, data, busy, failed }
  const [link, setLink] = useState(null);           // composer preview: { url, meta }
  const linkTimer = useRef(null);
  const linkDismissed = useRef(new Set());
  const [linkPatch, setLinkPatch] = useState({});   // clientTempId -> metadata, from the socket
  const flightTimer = useRef(null);
  const flightDismissed = useRef(new Set());
  const [toast, setToast] = useState('');
  const [retryTick, setRetryTick] = useState(0);
  const scrollRef = useRef(null);
  const rootRef = useRef(null);
  const distBottomRef = useRef(0);   // distance from the bottom, sampled on real scrolls
  const fileRef = useRef(null);
  const dragDepth = useRef(0);
  const sendLockRef = useRef(false);
  const otherUid = otherParticipant(chat, myUid);

  const myName = resolveName(myProfile) !== 'Allo user' ? resolveName(myProfile) : 'You';

  // header profile + presence
  useEffect(() => {
    let live = true;
    if (chat.isGroup) setProfile({ fullName: chat.name || chat.groupName, profileImage: chat.groupImage });
    else if (otherUid) getUserProfile(otherUid).then((p) => {
      if (!live) return;
      setProfile((cur) => cur || p);
      // seed last-seen from the user doc so the header isn't blank before RTDB fires
      const ls = p?.lastSeen ?? p?.last_changed;
      if (ls) setLastSeen(typeof ls === 'number' ? ls : (ls?.seconds ? ls.seconds * 1000 : Number(ls) || null));
    });
    // RTDB status/{uid} = { state, last_changed } (mobile also mirrors lastSeen)
    // 🟥 a blocked pair exchanges no presence — the app never attaches the status
    //    subscription when either side blocked (ChatHeaderStatus.tsx:72); mirroring
    //    that here also stops the "online" dot leaking through the header
    const _blockedHere = !!(blocking && otherUid && (blocking.byMe.has(otherUid) || blocking.me.has(otherUid)));
    const unsub = (!chat.isGroup && otherUid && !_blockedHere) ? watchPresence(otherUid, (s) => {
      if (!live) return;
      setOnline(!!s?.online);
      // while someone is ONLINE the app stores lastSeen: null — don't let that erase
      // the timestamp we already have, or the header flashes an empty status on the
      // way back to offline
      if (s?.lastSeen) setLastSeen(s.lastSeen);
    }) : null;
    // 🟢 WATCH the profile, don't read it once: their Last Seen / Online privacy can
    //    change while the chat is open, and the header must follow it.
    const unsubP = (!chat.isGroup && otherUid)
      ? watchUserProfile(otherUid, (p) => { if (live) setProfile(p); }) : null;
    return () => { live = false; unsub && unsub(); unsubP && unsubP(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.id, otherUid, chat.isGroup, blocking]);

  // ── 🔒 PRESENCE PRIVACY: their global settings + their per-chat switch toward me
  //    + my own reciprocity. One gate, shared with the chat-list dot.
  const [peerIsContact, setPeerIsContact] = useState(false);
  useEffect(() => {
    let live = true;
    if (!otherUid || profile?.privacySettings?.lastSeen !== 'my_contacts') { setPeerIsContact(false); return undefined; }
    isViewerInTargetContacts(otherUid, myUid).then((v) => live && setPeerIsContact(v));
    return () => { live = false; };
  }, [otherUid, myUid, profile?.privacySettings?.lastSeen]);

  const presenceVis = derivePresenceVisibility({
    viewerPrivacy: myProfile?.privacySettings,
    targetPrivacy: profile?.privacySettings,
    viewerUid: myUid,
    isContact: peerIsContact,
    perChatLastSeen: otherUid ? chat?.lastSeenBy?.[otherUid] : undefined,
    perChatOnline: otherUid ? chat?.onlineBy?.[otherUid] : undefined,
  });

  // ── typing: subscribe for THIS chat, and always stop broadcasting on the way out ──
  /* 🟥 KEYED ON THE CHAT'S ID, NOT THE CHAT OBJECT. Every update to the chat
     document (a new last message, a receipt) is a new `chat` object, so this ran
     its cleanup mid-sentence: it sent "stopped typing" to the other person while
     you were still typing (their indicator flickered off) and re-subscribed. The
     object is read through a ref for the farewell on the way out.
     🟢 AND A CHANGE OF NOTHING IS NOT A RENDER. watchTyping reports every chat's
        typers; a new array for this chat's unchanged list re-rendered the whole
        window — every bubble — on each heartbeat anywhere. */
  const typingChatRef = useRef(chat);
  typingChatRef.current = chat;
  useEffect(() => {
    const un = watchTyping((all) => {
      const next = (all[chat.id] || []).filter((t) => t.uid !== myUid);
      setTypers((prev) => {
        const sig = (l) => (l || []).map((t) => `${t.uid}|${t.name || ''}|${t.activity || ''}`).join(',');
        return sig(prev) === sig(next) ? prev : next;
      });
    });
    return () => {
      un();
      stopTyping(typingChatRef.current, myUid, myName);   // leaving mid-word must not strand the indicator
    };
  }, [chat.id, myUid, myName]); // eslint-disable-line react-hooks/exhaustive-deps

  // a message from someone is proof they stopped typing
  useEffect(() => {
    const last = rawMsgs[rawMsgs.length - 1];
    if (last?.senderId && last.senderId !== myUid) clearTypingFor(chat.id, last.senderId);
  }, [rawMsgs, chat.id, myUid]);

  // live messages — 👥 a group I was added to, only from my join on (services/joinFloor)
  const joinFloor = fetchFloorMs(joinedMsOf(chat, myUid), 0);
  useEffect(() => {
    setRawMsgs([]); setDecMsgs([]); setPending([]); setTray([]);
    const unsub = watchMessages(chat.id, setRawMsgs, { sinceMs: joinFloor });
    return () => unsub && unsub();
  }, [chat.id, joinFloor]);

  // 🟢 The phone's per-user watermark. "Clear chat" AND "Delete chat" both write
  //    chats/{id}.clearedAt[uid]; the app hides everything at or before it. The web has
  //    a SECOND source the phone doesn't — its own IndexedDB copy of the history export
  //    — so without this filter the browser happily re-displayed a conversation the
  //    user had already cleared on their phone.
  const clearedCutoff = useMemo(() => clearedAtMs(chat, myUid), [chat, myUid]);

  // decrypt pipeline — merge the phone's synced history (already plaintext) with the
  // live Firestore stream, then decrypt TEXT ONLY.
  //
  // 🟥 DELETE-FOR-ME OF A SWEPT MESSAGE. A 1:1 doc is often already gone from the
  //    cloud (store-and-forward), so there is nothing to mark deletedFor on — the only
  //    copy is the phone's export in front of us. Hiding it locally IS the delete for
  //    that copy; localStorage keeps the choice across reloads.
  // 🟥 DECLARED ABOVE THE DECRYPT EFFECT ON PURPOSE: that effect's dep array names
  //    hiddenIds, and a dep array is evaluated where it is written — naming a const
  //    declared further down is a TDZ ReferenceError that blanks the whole pane
  //    (the exact startForward bug, second verse).
  /* 🟢 THE SAME RECORD THE CHAT LIST READS. This was a private useState +
     localStorage pair living only in this component, which is why the row beside
     a message you had hidden went on showing it: nothing outside here could see
     the decision. hiddenMessages.js owns the store now and announces changes, so
     the thread and the sidebar cannot disagree about what is on screen. */
  const [hiddenIds, setHiddenIds] = useState(() => hiddenIdsFor(chat.id));
  useEffect(() => { setHiddenIds(hiddenIdsFor(chat.id)); }, [chat.id]);
  const hideLocally = (id) => {
    setHiddenIds(hideLocallyShared(chat.id, id));
    /* 🟥 AND OUT OF THE ARCHIVE TOO. The on-screen hide is per-tab state; the
       archive is what the chat list quotes from and what a reload reads. Hiding
       one without the other is how a deleted message keeps coming back. */
    try { forgetRetained(myUid, chat.id, id); } catch { /* never block the hide */ }
  };

  /* 🟥 UP HERE FOR THE SAME REASON hiddenIds IS — the decrypt effect below names
     this in its dependency array, and a dep array is evaluated where it is
     written. Declared further down it is a TDZ ReferenceError that blanks the
     whole pane. */
  /* the call rows this browser keeps for itself, because the Firestore copy is a
     relay the phones delete — see services/localCallRows */
  const [localCallRows, setLocalCallRows] = useState(() => getCallRows(chat.id));
  useEffect(() => {
    setLocalCallRows(getCallRows(chat.id));
    return subscribeCallRows(() => setLocalCallRows(getCallRows(chat.id)));
  }, [chat.id]);

  // 🟥 MEDIA IS NO LONGER DECRYPTED HERE. It used to walk the whole merged list and
  //    fetch every photo and video in the window the moment a chat opened — hundreds of
  //    megabytes for a chat you scrolled two messages into. Media is now pulled by the
  //    IntersectionObserver below, per bubble, as it approaches the viewport.
  useEffect(() => {
    let live = true;
    (async () => {
      // 🟢 mergeChatHistory needs to know which sealed copies are addressed to US, so a
      //    live doc this browser can't open never replaces the phone's readable export.
      const myDeviceId = await getIdentity().then((i) => i.deviceId).catch(() => null);
      /* 🟥 THE BROWSER'S OWN CALL ROWS, which exist nowhere else. A call's status
         message in `chats/{id}/messages` is a RELAY for the phones — ChatModal
         ingests it into SQLite and deletes it, including documents it did not
         write — so it is gone from Firestore within seconds and this list would
         never show it again. See services/localCallRows. Anything still live in
         Firestore wins by id, so a row is never drawn twice. */
      const have = new Set(rawMsgs.map((m) => m.id));
      const extraCallRows = localCallRows.filter((r) => !have.has(r.id));
      const liveWithLocal = extraCallRows.length
        ? orderMessages([...rawMsgs, ...extraCallRows])
        : rawMsgs;

      /* 🟥 KEEP WHAT ARRIVED. Live rows lived in memory only, and the documents
         they came from are swept by store-and-forward — so leaving the chat and
         coming back showed nothing until the phone exported again. Writing them
         into the archive makes a sync what it should always have been: how a NEW
         browser catches up, not what every message is waiting for. */
      try { retainLive(myUid, chat.id, liveWithLocal); } catch { /* never block a render */ }
      /* 🟥 AND LEAVE A COPY FOR MY OWN PHONE. The live relay only reaches a phone
         that is ON, and the Firestore document only survives until the recipient
         sweeps it — so a message sent from here while the handset was off could
         end up recoverable by nobody. The phone has always done this for its
         sends (deviceMailbox.mirrorMine); this is the half that was missing. */
      try {
        /* 🟥 AND ONLY WHAT IS RECENT. This used to hand over EVERY message of mine
           in the loaded window, on every live update — which is how one chat
           became hundreds of reads and writes and exhausted Firestore's write
           queue. A mailbox is a catch-up for a phone that was off for a while;
           anything older than a day is the backfill's job and its mailbox copy
           would have expired anyway. */
        const _cut = Date.now() - MIRROR_MAX_AGE_MS;
        const _mine = liveWithLocal
          .filter((m) => m && m.senderId === myUid && m.id && tsOf(m) >= _cut)
          .map((m) => m.id);
        if (_mine.length) mirrorOut(myUid, chat.id, _mine);
      } catch { /* last-resort path — never block a render */ }

      const merged = mergeChatHistory(liveWithLocal, syncedHistory?.[chat.id], myDeviceId)
        .filter((m) => !clearedCutoff || tsOf(m) > clearedCutoff)
        // 🟢 "Delete for me" on the phone writes deletedFor: arrayUnion(uid) on the
        //    MESSAGE doc (an array here — the CHAT doc uses a map for the same name).
        //    It stays in Firestore for the other participant, so the web has to honour
        //    it or a message you deleted keeps living in the browser.
        .filter((m) => !isDeletedFor(m, myUid))
        // deleted-for-me on a message whose cloud doc was already swept — see hideLocally
        .filter((m) => !hiddenIds.has(m.id))
        // 🟥 `vote_action` (and `reaction_action`) are written INTO the messages
        //    collection but are side-effect records, not messages. Rendering them
        //    would put an empty bubble in the chat every time anyone voted.
        .filter((m) => !isPollSideEffect(m));

      const painted = await Promise.all(merged.map(async (m) => {
        // 🟥 THE QUOTE HAS TO BE UNSEALED BEFORE THE TYPE DISPATCH. A reply to a photo is
        //    a MEDIA message, and media returns early below without ever touching
        //    decryptMessage — so hydrating inside it would leave every quote on a photo,
        //    video, voice note or document reading as a bare padlock.
        //    It also sits OUTSIDE the try, so the catch below can still return it: a
        //    later decrypt failure must not re-padlock a quote that already opened.
        let withQuote = m;
        try {
          if (m.replyTo?.encrypted) withQuote = { ...m, replyTo: (await hydrateReplyTo(m)) || m.replyTo };
        } catch { /* keep the sealed snapshot; ReplyQuote shows the type label */ }
        try {
          // a call / system entry from the phone's export is already plaintext
          if (isCallMessage(m) || (m.type === 'system' && m.text)) return withQuote;
          // a flight card is plaintext on both clients — there is nothing to unseal
          if (m.type === 'flight') return withQuote;
          if (INSTANT_TYPES.has(m.type)) {
            if ((m.type === 'text' || m.type === 'security') && m._plain !== undefined) return withQuote;
            if (m._payload !== undefined && m.type !== 'text') return withQuote;
            /* 🟥 EVERY SNAPSHOT RE-DECRYPTED EVERY TEXT MESSAGE. A receipt on one
               message re-ran the crypto for up to 200 — the raw docs never carry the
               plaintext, so nothing short-circuited. The unsealed fields are now
               remembered per message, keyed on the parts that can change what they
               decrypt to (receipts and reactions are not among them). */
            const _sig = decryptSig(m);
            const _hit = _plainCache.get(m.id);
            if (_hit && _hit.sig === _sig && _hit.uid === myUid) return { ...withQuote, ..._hit.extra };
            const _res = await decryptMessage(withQuote, myUid);
            if (_res && m.id && !_res._decryptError) {
              const extra = {};
              for (const k of Object.keys(_res)) if (k.startsWith('_') && _res[k] !== withQuote[k]) extra[k] = _res[k];
              _plainCache.set(m.id, { sig: _sig, uid: myUid, extra });
              if (_plainCache.size > 3000) { const k0 = _plainCache.keys().next().value; if (k0 !== undefined) _plainCache.delete(k0); }
            }
            return _res;
          }
          return withQuote; // media: the bubble shell (blur + size) needs no decryption
        } catch {
          // 🟥 `...m`, not `...withQuote`, threw away an already-unsealed quote and put the
          //    padlock fallback back on a message whose quote had just opened fine.
          return { ...withQuote, _plain: null, _decryptError: true };
        }
      }));
      if (!live) return;
      // keep whatever media we already resolved for a message across re-renders of the
      // list, so a new incoming message doesn't wipe every loaded photo back to a blur
      setDecMsgs((prev) => {
        const had = new Map(prev.map((x) => [x.id, x]));
        return painted.map((m) => {
          const old = had.get(m.id);
          if (!old) return m;
          const keep = {};
          if (old._media) keep._media = old._media;
          if (old._mediaBatch) keep._mediaBatch = old._mediaBatch;
          // 🟥 _docThumb WAS MISSING FROM THIS LIST, AND THAT IS THE DOCUMENT-PREVIEW BUG.
          //    A document's first page is decrypted once, lazily, by the media queue. This
          //    repaint runs on EVERY snapshot — and an incoming document triggers one
          //    immediately, because receiving it writes a delivered/seen receipt. The
          //    rebuilt entry comes straight from rawMsgs with no _docThumb, the keep-list
          //    didn't carry it, and `_mediaResolved` (which IS kept) then told the queue
          //    the message was already done — so it was never fetched again. The preview
          //    rendered for a frame and was gone.
          //    Why a document YOU send looks fine: it's the same race, but you win it.
          //    Receiving a message writes a delivered (and, with the chat open, a seen)
          //    receipt within milliseconds, so an INCOMING document is repainted long
          //    before its thumbnail — a fetch plus a decrypt — comes back. On a document
          //    you sent, the other side's receipts arrive seconds later, by which time
          //    the preview is already on screen. Same bug, different odds.
          //    _caption has exactly the same shape of bug: recomputed only during a
          //    decrypt, so a photo's caption vanished on the next repaint too.
          if (old._docThumb) keep._docThumb = old._docThumb;
          if (old._docThumbTried) keep._docThumbTried = true;
          if (old._caption != null && m._caption == null) keep._caption = old._caption;
          if (old._deviceOnly) keep._deviceOnly = true;
          if (old._mediaResolved) keep._mediaResolved = true;
          /* 🟢 CARRIED FOR THE SAME REASON `_mediaResolved` IS. This repaint runs
             on every snapshot and rebuilds each row from the raw document, which
             has no idea why a fetch failed — so without this the card forgets
             that the object was deleted and starts offering "Try again" again on
             the very next receipt. */
          if (old._mediaGone) keep._mediaGone = true;
          if (old._needsLoad) keep._needsLoad = true;
          if (old._loading) keep._loading = true;
          // 🟥 ANTI-FLICKER. A preview backfilled locally (or delivered over the socket)
          //    lands before the Firestore updateDoc echoes back — and in a 1:1 chat that
          //    echo may never come at all, because store-and-forward has already deleted
          //    the doc. Letting a snapshot without the field overwrite one with it made
          //    the card appear and then vanish a moment later.
          if (old.linkMetadata && !m.linkMetadata) keep.linkMetadata = old.linkMetadata;
          const next = { ...m, ...keep };
          /* 🟢 UNCHANGED ROW → THE SAME OBJECT. Every snapshot (a receipt on any message)
             rebuilt every row, so nothing downstream could ever tell an untouched
             message from a changed one and the whole history re-rendered. */
          return sameRowShape(old, next) ? old : next;
        });
      });
    })();
    return () => { live = false; };
  }, [rawMsgs, localCallRows, myUid, syncedHistory, chat.id, clearedCutoff, hiddenIds]);

  // ══════════════════════════════════════════════════════════════════════════
  //  LAZY MEDIA — fetch + decrypt only what is (about to be) on screen
  // ══════════════════════════════════════════════════════════════════════════
  const msgByIdRef = useRef(new Map());
  const requestedRef = useRef(new Set());
  const sigRef = useRef(new Map());          // id → the source we last tried against
  /* 🟥 A REF, AND IT CANNOT BE A DEPENDENCY. `requestMedia` is declared below
     this effect, and a dependency array is evaluated DURING RENDER — naming it
     there is a temporal-dead-zone ReferenceError, swallowed by whatever catch is
     nearest and impossible to connect to the symptom. */
  const requestMediaRef = useRef(null);
  const queueRef = useRef([]);
  const lanesRef = useRef(0);
  const ioRef = useRef(null);
  const ioMapRef = useRef(new Map()); // element → message id

  /* the id → message map is built once (msgById, below) and mirrored here */

  /* 🟥 A BETTER COPY OF A MESSAGE DESERVES ANOTHER ATTEMPT. See mediaSig: the
     export paints before the Firestore documents land, so the queue can answer
     "unavailable" about a copy that is then replaced. Only messages we have
     ALREADY tried are re-armed, and only when the thing we tried against has
     actually changed — an untouched list re-arms nothing. */
  useEffect(() => {
    const again = [];
    decMsgs.forEach((m) => {
      if (!m?.id) return;
      const sig = mediaSig(m);
      const was = sigRef.current.get(m.id);
      sigRef.current.set(m.id, sig);
      if (was === undefined || was === sig) return;
      /* 🟥 A SUCCESS IS NEVER RE-OPENED. The two copies of a media message land
         within a second of each other and the SECOND one can be the unreadable
         one — after a re-link the sealed Firestore document is addressed to a
         device key this browser no longer has, while the export beside it is
         plaintext. Re-arming here put a spinner over a photo that was already on
         screen and then wrote the padlock's answer on top of it. The re-arm
         exists to give a FAILURE another chance; a picture that is rendering is
         not a failure. (mergeResolved below is the same rule at the other end,
         for any route that reaches resolveOne without passing through here.) */
      if (fullyResolvedMedia(m)) return;
      const tried = requestedRef.current.has(m.id) || requestedRef.current.has(`${m.id}:full`);
      if (!tried) return;
      const forced = requestedRef.current.has(`${m.id}:full`);
      requestedRef.current.delete(m.id);
      requestedRef.current.delete(`${m.id}:full`);
      again.push({ id: m.id, force: forced });
    });
    if (!again.length) return;
    const ids = new Set(again.map((a) => a.id));
    /* the spinner, not the error, while the second attempt runs */
    setDecMsgs((prev) => prev.map((x) => (ids.has(x.id)
      ? { ...x, _mediaResolved: false, _deviceOnly: false } : x)));
    again.forEach((a) => requestMediaRef.current?.(a.id, a.force));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decMsgs]);

  // a fresh chat starts with a clean slate (and `retryTick` re-arms a failed one)
  useEffect(() => {
    requestedRef.current = new Set();
    queueRef.current = [];
  }, [chat.id, retryTick]);

  const resolveOne = useCallback(async (m, force) => {
    const opts = { skipVideo: !force };
    try {
      /* 🟥 THE SHAPE, NOT THE KEY. This asked `m.mediaKey || m.mediaKeys` — a
         test of whether the message is ENCRYPTED, when the question is which
         VOCABULARY of field names it uses. `exportMessage` writes
         `mediaKey: null` for a plaintext file, so every unencrypted photo from
         the phone's export was handed to the Firestore decoder, which looks for
         `imageUrl` and found only `url`. The one chat that is never encrypted is
         the official Allo account. See isExportedEntry in services/historySync. */
      const done = isExportedEntry(m)
        ? await normalizeSynced(m, opts)
        : await decryptMessage(m, myUid, opts);
      // 🟢 _mediaResolved separates "still fetching" (spinner) from "we tried and there
      //    is nothing" (an honest error + Retry) — without it a failed fetch spins for ever.
      // 🟥 _docThumbTried IS SEPARATE FROM _mediaResolved ON PURPOSE. DocBubble used
      //    _mediaResolved to decide whether to give up on the preview strip — but that
      //    flag means "the media queue finished with this message", and it gets set by
      //    paths that never even looked at the thumbnail (the hasCloudSource bail-out
      //    above being the worst). One flag answering two questions is what made the
      //    strip render for a frame and then collapse. This one answers only the
      //    question the strip is actually asking.
      const tried = m.type === 'document' ? { _docThumbTried: true } : null;
      /* 🟢 WHY IT FAILED, CARRIED TO THE BUBBLE. Without this the card cannot
         tell a reader whose photo the bucket deleted apart from one whose
         network blipped — and it was offering the button to the first and not
         to the second. */
      const src = m.url || m.imageUrl || m.videoUrl || m.audioUrl || m.documentUrl || null;
      const gone = src ? mediaFailure(src, m.mediaKey?.k) === GONE : false;
      setDecMsgs((prev) => prev.map((x) => {
        if (x.id !== m.id) return x;
        const next = mergeResolved(x, done);
        /* 🟢 AND "GONE" IS ABOUT WHAT WE ENDED UP WITH, not about the attempt. A
           card that is showing the picture must never also be offering to
           explain that the picture was deleted. */
        return { ...next, ...tried, _mediaGone: hasResolvedMedia(next) ? false : gone, _mediaResolved: true, _loading: false };
      }));
    } catch {
      const tried = m.type === 'document' ? { _docThumbTried: true } : null;
      setDecMsgs((prev) => prev.map((x) => (x.id === m.id
        ? { ...x, ...tried, _mediaResolved: true, _loading: false } : x)));
    }
  }, [myUid]);

  const pump = useCallback(() => {
    while (lanesRef.current < MEDIA_LANES && queueRef.current.length) {
      const job = queueRef.current.shift();
      lanesRef.current += 1;
      resolveOne(job.m, job.force).finally(() => { lanesRef.current -= 1; pump(); });
    }
  }, [resolveOne]);

  const requestMedia = useCallback((id, force = false) => {
    const key = force ? `${id}:full` : id;
    if (requestedRef.current.has(key)) return;
    const m = msgByIdRef.current.get(id);
    if (!m || m.type === 'system' || INSTANT_TYPES.has(m.type)) return;
    if (m.mediaDeletedFromStorage) return;   // the bytes are gone on purpose
    if (!hasCloudSource(m)) {                 // nothing to fetch — say so, don't spin
      requestedRef.current.add(key);
      setDecMsgs((prev) => prev.map((x) => (x.id === id
        ? { ...x, _deviceOnly: true, _mediaResolved: true, _loading: false } : x)));
      return;
    }
    /* 🟥 A VIDEO NEVER REACHED THE DECODER, SO IT COULD NOT SAY "I ALREADY HAVE
       THIS". The rule was right when the only answer to a video was a download:
       skip it, draw the button, wait for a tap. It is wrong now that the bytes
       may be on disk — the decoder's `skipVideo` branch checks the store first
       and returns instantly with no network, and it never gets the chance if
       the request is dropped here.
       🟢 THE DOWNLOAD IS STILL NOT DONE WITHOUT A TAP. What runs is one cheap
          IndexedDB probe; a clip that is not stored still ends at the button. */
    if (!force && m.type === 'video' && m._needsLoad) return;
    requestedRef.current.add(key);
    if (force) setDecMsgs((prev) => prev.map((x) => (x.id === id ? { ...x, _loading: true } : x)));
    queueRef.current.push({ m, force });
    pump();
  }, [pump]);
  requestMediaRef.current = requestMedia;

  /** 🟥 Retry has to re-request DIRECTLY. Clearing the "already requested" set is not
   *  enough: an IntersectionObserver only fires when intersection CHANGES, and the
   *  bubble you are retrying is by definition already on screen — so nothing would
   *  ever re-trigger it and the button did nothing. */
  const retryMedia = useCallback((id) => {
    requestedRef.current.delete(id);
    requestedRef.current.delete(`${id}:full`);
    setDecMsgs((prev) => prev.map((x) => (x.id === id
      ? { ...x, _mediaResolved: false, _docThumbTried: false, _needsLoad: false, _loading: true } : x)));
    requestMedia(id, true);
  }, [requestMedia]);

  /**
   * 🟥 A HANDLE CAN DIE WHILE THE THING HOLDING IT IS ON SCREEN. An object URL
   *    is revoked when too many are alive, and the string in `_media` does not
   *    stop being a string — so the element paints a broken glyph and the app,
   *    having no onError anywhere, never learns.
   *
   * 🟢 THE BYTES ARE STILL ON DISK, so the first answer to a broken element is
   *    simply another handle, minted from the store with no network and no
   *    decrypt. Only when the store has nothing either is this a real failure,
   *    and then it goes down the ordinary retry path — which will say "expired"
   *    rather than offering a button, if that is what it turns out to be.
   */
  const healMedia = useCallback(async (id) => {
    const m = msgByIdRef.current.get(id);
    if (!m) return;
    const src = m.url || m.imageUrl || m.videoUrl || m.audioUrl || m.documentUrl || null;
    const key = m.mediaKey?.k
      || (m.imagePerDeviceKeys || m.videoPerDeviceKeys || m.audioPerDeviceKeys ? undefined : null);
    if (src && key !== undefined) {
      const again = await remintMedia(src, m.mediaKey?.k ?? key);
      if (again && again !== m._media) {
        setDecMsgs((prev) => prev.map((x) => (x.id === id ? { ...x, _media: again } : x)));
        return;
      }
    }
    /* 🟢 CLEARED BEFORE RE-REQUESTING, so the element stops rendering a handle
       we already know is dead — otherwise a failing image would sit there
       looking broken for the whole of the second attempt. */
    setDecMsgs((prev) => prev.map((x) => (x.id === id
      ? { ...x, _media: null, _mediaResolved: false, _loading: true } : x)));
    requestedRef.current.delete(id);
    requestedRef.current.delete(`${id}:full`);
    requestMedia(id, true);
  }, [requestMedia]);

  /** One album tile (a video inside a batch) — decrypt just that item. */
  const loadTile = useCallback(async (m, index) => {
    const mark = (patch) => setDecMsgs((prev) => prev.map((x) => (x.id === m.id
      ? { ...x, _mediaBatch: (x._mediaBatch || []).map((it, i) => (i === index ? { ...it, ...patch } : it)) }
      : x)));
    mark({ loading: true });
    const url = await loadBatchItem(m, index);
    mark({ url, loading: false, needsLoad: !url });
  }, []);

  // ONE observer for the whole conversation (not one per bubble — a 200-message chat
  // would otherwise allocate 200 of them), rooted on the scroller so "visible" means
  // visible in the chat, not in the window.
  //
  // 🟥 CREATED ON DEMAND, NOT IN AN EFFECT. React runs CHILD effects before PARENT
  //    effects, so every media bubble registers before this component's own effect
  //    would have run — the observer would still be null, every bubble would take the
  //    "no IntersectionObserver" fallback, and the whole chat would eager-load exactly
  //    like before. Building it on the first observe() call sidesteps the ordering
  //    entirely (refs are attached before any effect, so scrollRef is already live).
  const ensureIO = useCallback(() => {
    if (ioRef.current) return ioRef.current;
    const root = scrollRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return null;
    ioRef.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const id = ioMapRef.current.get(e.target);
        if (id) requestMedia(id);
      });
    }, { root, rootMargin: `${MEDIA_PRELOAD_PX}px 0px` });
    return ioRef.current;
  }, [requestMedia]);

  // tear down when the conversation changes (the next observe() builds a fresh one
  // rooted on the new scroller)
  useEffect(() => () => {
    ioRef.current?.disconnect();
    ioRef.current = null;
    ioMapRef.current = new Map();
  }, [chat.id]);

  const mediaCtx = useMemo(() => ({
    observe: (el, id) => {
      ioMapRef.current.set(el, id);
      const io = ensureIO();
      // genuinely no IntersectionObserver (very old browser) → load it now, which is
      // simply the old behaviour rather than a bubble that never fills in
      if (io) io.observe(el); else requestMedia(id);
    },
    unobserve: (el) => {
      ioRef.current?.unobserve(el);
      ioMapRef.current.delete(el);
    },
    requestMedia,
    retryMedia,
    healMedia,
    loadTile,
  }), [ensureIO, requestMedia, retryMedia, healMedia, loadTile]);

  // an optimistic bubble retires the moment its real doc lands (matched on clientTempId)
  useEffect(() => {
    if (!pending.length) return;
    const live = new Set(rawMsgs.map((m) => m.clientTempId).filter(Boolean));
    setPending((p) => p.filter((x) => x._failed || !live.has(x.clientTempId)));
  }, [rawMsgs]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🟢 OPEN AT THE NEWEST MESSAGE. useLayoutEffect runs after DOM mutation but BEFORE
  //    the browser paints, so the jump to the bottom is never visible — the chat simply
  //    appears already scrolled down (no "starts at top then scrolls" animation).
  const scrollToEnd = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    distBottomRef.current = 0;
    requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; });
    setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 120);
  }, []);

  // 🟥 THE SCROLL-YANK. This used to be a bare `scrollToEnd()` on every `decMsgs`
  //    change — which was survivable when media was decrypted in one burst at chat
  //    open, and became a bug the moment loading went lazy: each photo that resolves
  //    patches decMsgs, so scrolling UP fetched images, and every image that landed
  //    threw the viewport back down to the newest message. It has to distinguish "a
  //    message was ADDED" from "an existing message changed".
  const scrollStateRef = useRef({ chatId: null, count: 0, primed: false });
  // true from the moment a chat opens until the reader scrolls up in it
  const anchorBottomRef = useRef(true);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const total = decMsgs.length + pending.length;
    const st = scrollStateRef.current;

    if (st.chatId !== chat.id) {          // opening a conversation → start at the newest
      scrollStateRef.current = { chatId: chat.id, count: total, primed: total > 0 };
      anchorBottomRef.current = true;     // stay pinned until the reader takes control
      scrollToEnd();
      return;
    }

    // 🟥 "SOMETIMES IT OPENS AT THE TOP." The branch above fires on the FIRST pass for a
    //    chat — and on that pass the list is usually still EMPTY, because the decrypt
    //    effect clears decMsgs on a chat change and refills it a tick later. So the
    //    "start at the newest" scroll landed on nothing, and when the messages did
    //    arrive they came through the growth path below, where scrollTop is 0, so
    //    `nearBottom` was false and we deliberately did NOT move. Result: the top of the
    //    conversation. It only happened SOMETIMES because a chat whose history is
    //    already cached paints on that very first pass, with a full list, and scrolls
    //    correctly. Opening isn't finished until the conversation actually has content.
    if (!st.primed) {
      scrollStateRef.current = { chatId: chat.id, count: total, primed: total > 0 };
      scrollToEnd();
      return;
    }

    const grew = total > st.count;
    scrollStateRef.current = { ...st, count: total };
    if (!grew) return;                    // media resolved / receipt changed → don't move

    // 🟥 "PRIMED" WASN'T ENOUGH, AND THIS IS THE CHAT THAT PROVED IT. A conversation can
    //    arrive in WAVES: a handful of live Firestore docs first, then hundreds more when
    //    the phone's synced history merges in. The first wave primed the flag and scrolled
    //    to the bottom of a five-message list; the second wave came through here as
    //    "growth", by which point scrollHeight had exploded while scrollTop was still ~0,
    //    so `nearBottom` was false and we deliberately stayed put — at the top of the
    //    conversation. It only ever showed on a chat with a big second wave, which is why
    //    every OTHER chat looked fine.
    //
    //    So the question isn't "were they near the bottom", it's "have they taken control
    //    yet". A chat opens pinned to the newest message and STAYS pinned through however
    //    many waves it takes, until the reader scrolls up — which is the moment they, not
    //    the loader, own the viewport. (trackScroll flips it; our own scrollToEnd lands at
    //    the bottom and so can never flip it by accident.)
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 260;
    if (anchorBottomRef.current || nearBottom) scrollToEnd();
  }, [decMsgs, pending.length, chat.id, scrollToEnd]);

  // 🟥 THE TYPING BUBBLE DIDN'T PUSH THE VIEW UP. It renders inside the scroller like
  //    any other row, so when it appears the content grows by ~54px and the newest
  //    message — plus half the dots themselves — slides under the composer. Nothing
  //    scrolled, because the effect above only reacts to the MESSAGE count and the
  //    ResizeObserver watches the scroller's own box, which never changes size.
  //
  //    "Was the user at the bottom?" has to be answered from BEFORE the growth, and
  //    content growth does NOT fire a scroll event (scrollTop is untouched) — so a
  //    distance sampled on real scrolls is still the pre-growth distance at the moment
  //    the indicator lands. That's the difference between following the conversation
  //    and yanking someone out of the history they were reading.
  const trackScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const d = el.scrollHeight - el.scrollTop - el.clientHeight;
    distBottomRef.current = d;
    // 🟢 300px is the phone's own threshold. Below it you're effectively AT the bottom —
    //    which is also the moment anything that arrived behind you stops being news, so
    //    the badge clears here rather than on the button tap: reaching the bottom by
    //    dragging counts exactly as much as pressing the button to get there.
    // 🟢 The reader has taken control — stop re-pinning to the bottom on every wave of
    //    late-arriving history. Only a real scroll away from the bottom does this;
    //    scrollToEnd lands at d ≈ 0, so it cannot release its own anchor.
    if (d > 300) anchorBottomRef.current = false;
    const up = d > 300;
    if (up !== upRef.current) {
      upRef.current = up;
      setShowJump(up);
      // 🟥 PRIME THE SET ON THE WAY UP, not on the way in. Starting it empty meant the
      //    first repaint after you scrolled away counted the ENTIRE loaded backlog as
      //    new — and since reportSeen writes `seen:true` back onto those same docs, the
      //    echo alone was enough: scroll up, and the button lit with "40 new messages"
      //    without anyone having sent one. Everything already on screen is, by
      //    definition, not news.
      if (up) jumpSeen.current = new Set(onScreenIds.current);
      else { setJumpCount(0); jumpSeen.current = new Set(); }
    }
  }, []);

  // a signature so a typer joining, leaving, or switching to "recording" all re-run this
  const typingSig = typers.map((t) => `${t.uid}:${t.activity || 'typing'}`).join(',');
  useLayoutEffect(() => {
    if (!typingSig) return;              // it going AWAY must never move the viewport
    if (!scrollRef.current) return;
    if (distBottomRef.current < 140) scrollToEnd();
  }, [typingSig, scrollToEnd]);

  useEffect(() => { onScreenIds.current = decMsgs.map((m) => m.id); }, [decMsgs]);

  // 🟥 THE UNREAD MARK IS FROZEN AT OPEN, ON PURPOSE. It's computed from
  //    `readUpTo[me]` — the same watermark the phone honours — the first time this chat
  //    has messages on screen, and then never recomputed. Recomputing would be wrong in
  //    a way that's easy to miss: the very act of opening the chat advances the
  //    watermark past everything, so a live calculation would erase the line you were
  //    meant to be shown a fraction of a second after drawing it.
  useEffect(() => {
    setUnreadMark(null);
    unreadFrozen.current = null;
    jumpSeen.current = new Set();
    upRef.current = false;
    setJumpCount(0);
    setShowJump(false);
  }, [chat.id]);

  useEffect(() => {
    if (unreadFrozen.current === chat.id || !decMsgs.length) return;
    const cursor = readUpToMs(chat, myUid);
    const badge = Number(chat.unreadCounts?.[myUid]) || 0;

    // 🟥 LATCH ON A RESULT, NEVER ON AN EMPTY ONE. `decMsgs` is a union of the live
    //    Firestore window and the phone's synced history, and those two land in either
    //    order. Latching the first time the list was merely non-empty meant that when
    //    history won the race — a warm IndexedDB and a cold network — we decided "no
    //    unread" from a list of nothing but old messages, and the five that arrived a
    //    tick later could never re-open the question. Finding nothing simply isn't an
    //    answer yet; it becomes one when the read watermark moves past everything,
    //    which is what stops this from evaluating forever.
    let fresh = [];
    if (cursor) {
      fresh = decMsgs.filter((m) => tsOf(m) > cursor && countsAsUnread(m, myUid));
    } else if (badge > 0) {
      // no watermark on any device yet — fall back to the badge, so the line and the
      // number the user just clicked agree, instead of marking the whole history unread
      const mine = decMsgs.filter((m) => countsAsUnread(m, myUid));
      fresh = mine.slice(Math.max(0, mine.length - badge));
    }
    if (!fresh.length) return;

    unreadFrozen.current = chat.id;
    setUnreadMark({
      anchorId: fresh[0].id,
      count: fresh.length,
      // 🟥 a TIMESTAMP, not a length. The dismiss check used to compare list lengths,
      //    which move for reasons that have nothing to do with new messages: history
      //    sync merging 300 older rows dismissed the line before it could be read, and
      //    a delete-for-everyone shrank the list so the NEXT real message only restored
      //    the old count and never dismissed it at all.
      afterMs: tsOf(fresh[fresh.length - 1]),
    });
  }, [decMsgs, chat, myUid]);

  // 🟢 "once I send or receive another message the line goes away, nicely". The row
  //    collapses its own height as it fades, so the transcript closes the gap instead
  //    of snapping shut, and it's unmounted once the animation has actually finished.
  useEffect(() => {
    const mark = unreadMark;
    if (!mark?.anchorId || mark.gone) return;
    const newer = decMsgs.some((m) => tsOf(m) > mark.afterMs) || pending.length > 0;
    if (!newer) return;
    setUnreadMark((u) => (u ? { ...u, gone: true } : u));
    // 🟥 the timer lives in a ref. Returning it as this effect's cleanup meant React
    //    tore it down on the very next run — which this effect's own setState triggers —
    //    so the unmount never fired and the collapsed row stayed in the tree for good.
    clearTimeout(unreadTimer.current);
    unreadTimer.current = setTimeout(
      () => setUnreadMark((u) => (u?.gone ? null : u)), 420,
    );
  }, [decMsgs, pending.length, unreadMark]);

  useEffect(() => () => clearTimeout(unreadTimer.current), []);

  // 🟥 THE BUTTON HAS TO SIT ON TOP OF WHATEVER THE COMPOSER CURRENTLY IS. A fixed
  //    `bottom: 96px` is measured from the chat pane, not from the composer — and the
  //    composer is not a fixed height: the textarea grows to 140px, and the reply strip,
  //    the attachment tray and the voice recorder all stack above it. Past 96px the
  //    button (z-index 30) started covering the textarea and eating clicks on Send.
  //    Measuring the gap below the scroller is the only offset that's always right.
  useEffect(() => {
    const root = rootRef.current;
    const el = scrollRef.current;
    if (!root || !el || typeof ResizeObserver === 'undefined') return undefined;
    const sync = () => {
      const gap = root.getBoundingClientRect().bottom - el.getBoundingClientRect().bottom;
      root.style.setProperty('--jump-bottom', `${Math.max(12, Math.round(gap) + 14)}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(root); ro.observe(el);
    return () => ro.disconnect();
  }, [chat.id]);

  // 🟥 CLEARING THE BADGE ON BOTH CLIENTS. `unreadCounts[me] = 0` alone clears it here
  //    and does nothing on the phone, whose badge comes from a local cursor it seeds
  //    from `readUpTo`. markChatRead writes three things, keyed on the newest message's
  //    OWN timestamp — never Date.now(), which would corrupt the server's
  //    all-participants-have-read comparison:
  //
  //      unreadCounts[me]=0 + manuallyUnread[me]=false   always — my own counter
  //      users/{me}/chatReads/{chat}.at                  always — my private read mark
  //      chats/{id}.readUpTo[me]                         only when receipts are on
  //
  //    The middle one is the fix for "I read it on the web and the phone kept its
  //    badge". readUpTo is the sender's RECEIPT as well as my watermark, so with
  //    receipts off nothing wrote it and no other device of mine could ever learn
  //    that I had read the chat. See services/receipts.js.
  //    It runs over every decrypted row regardless of type, so a chat whose newest
  //    message is a document, a photo or a location clears exactly like a text one.
  useEffect(() => {
    const commit = () => {
      const newest = decMsgs.reduce((mx, m) => (m.senderId !== myUid ? Math.max(mx, tsOf(m)) : mx), 0);
      if (!newest) return;                       // nothing decrypted yet — nothing to claim
      const seen = markedRef.current;
      const already = seen.chatId === chat.id ? seen.ms : 0;
      if (newest <= already && !(Number(chat.unreadCounts?.[myUid]) > 0)) return;
      markedRef.current = { chatId: chat.id, ms: Math.max(newest, already) };
      markChatRead(chat.id, myUid, newest, { publishRead: rrOn });
    };
    if (document.visibilityState === 'visible') { commit(); return undefined; }
    // 🟢 a chat opened in a background tab still has to commit when you come back to it,
    //    or the badge outlives being read until the next message happens to arrive
    const onVis = () => { if (document.visibilityState === 'visible') commit(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [decMsgs, chat.id, chat.unreadCounts, myUid, rrOn]);

  // 🟢 A preview that resolved on the SENDER's device after they'd already hit send.
  //    It's keyed on clientTempId rather than the message id, because in a 1:1 chat the
  //    doc it would otherwise name has usually been swept by then.
  useEffect(() => watchLinkUpdates((cid, tempId, meta) => {
    if (cid !== chat.id) return;
    setLinkPatch((prev) => (prev[tempId] ? prev : { ...prev, [tempId]: meta }));
  }), [chat.id]);
  useEffect(() => { setLinkPatch({}); }, [chat.id]);

  // 🟢 THE BADGE ON THE BUTTON. Only messages from someone else count — your own send
  //    scrolls you to the bottom anyway — and each id is counted once, so a message
  //    being patched (media resolving, a receipt landing) can't inflate the number.
  useEffect(() => {
    if (distBottomRef.current <= 300) return;
    let added = 0;
    decMsgs.forEach((m) => {
      if (!m.id || m.senderId === myUid) return;
      if (jumpSeen.current.has(m.id)) return;
      jumpSeen.current.add(m.id);
      if (countsAsUnread(m, myUid)) added += 1;
    });
    if (added) setJumpCount((n) => n + added);
  }, [decMsgs, myUid]);

  // 🟢 RECEIPTS: anything that reached this device is DELIVERED; because the chat is
  //    open on screen, it's also SEEN — so the sender goes ✓✓ grey → ✓✓ green, exactly
  //    like reading it on the phone. (Only when the tab is actually visible.)
  useEffect(() => {
    const incoming = decMsgs.filter((m) => m.id && m.senderId !== myUid);
    if (!incoming.length) return;
    // delivery is NOT a read receipt — grey ✓✓ keeps working with receipts off,
    // exactly as on the phone (report-delivered is never gated there either)
    incoming.forEach((m) => reportDelivered(chat.id, m, myUid));
    if (!rrOn) return;                       // 🔒 off, or not known yet → say nothing
    if (document.visibilityState === 'visible') {
      incoming.forEach((m) => reportSeen(chat.id, m, myUid));
    } else {
      const onVis = () => {
        if (document.visibilityState === 'visible') {
          incoming.forEach((m) => reportSeen(chat.id, m, myUid));
          document.removeEventListener('visibilitychange', onVis);
        }
      };
      document.addEventListener('visibilitychange', onVis);
      return () => document.removeEventListener('visibilitychange', onVis);
    }
  }, [decMsgs, chat.id, myUid, rrOn]);

  // keep pinned to the bottom as media finishes loading and grows the list
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 260;
      if (nearBottom) el.scrollTop = el.scrollHeight;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [chat.id]);

  // ── who sent it / when — the media viewer's centre block ──
  const noteSelf = isNoteToSelf(chat, myUid);
  // 🟥 ABSENT MEANS ALLOWED — see canSendMessages(). An admin is never restricted, and a
  //    group created before the flag existed must not lock itself on the web alone.
  const mayPost = canSendMessages(chat, myUid);

  /* 🟢 MY OWN VIBES GATE, for the shared-post cards below. One listener per open
     chat on my user doc — the same doc the app's MessageBubble reads dateOfBirth
     from — so finishing the Vibes questions in the other tab unlocks the cards
     here without a reload. */
  const [vibeGate, setVibeGate] = useState(VSB_GATE_UNKNOWN);
  useEffect(() => watchVibeGate(myUid, setVibeGate), [myUid]);

  /* 🟥 HAVE I LEFT THIS GROUP? Read from `leftParticipants`, exactly as
     ChatModal derives `hasLeftGroup`. Leaving keeps me in `participants` (every
     chat query is array-contains on it), so the chat and its history stay — but
     the composer must go, or the web would happily let me type into a group I
     walked out of and the write would land. The phone REPLACES the input with a
     line of text; same here. */
  const iLeftGroup = !!chat.isGroup && (chat.leftParticipants || []).includes(myUid);
  /* 🔗 A TEMPORARY CHAT THAT HAS RUN OUT. The server deletes the conversation on
     expiry, so this is not a soft state to argue with — there is nothing left to
     send to. Locking the composer says so before a message is typed rather than
     after it fails. */
  const guestChat = isGuestChat(chat);
  const guestOver = guestChat && guestChatExpired(chat);
  /* 💬 THE OFFICIAL ALLO CHAT. One flag turns off four things — the call
     buttons, translate, the group actions, and the ordinary composer — because
     four separate conditions spread across three files would drift apart, and
     the one that drifted would be the one nobody was testing. */
  const officialChat = isOfficialChat(chat);

  /* 💬 ONLY FOR AN ORDINARY PERSON — a group has no status, Note to Self is you,
     and the official account and Allo AI are not people. Same exclusions the
     phone's chat info screen applies. */
  const aboutEligible = !!otherUid && !chat.isGroup && !noteSelf && !officialChat
    && otherUid !== 'allo-ai-bot' && otherUid !== myUid;

  useEffect(() => {
    if (!aboutEligible || !myUid) { setAbout(null); return undefined; }
    let live = true;
    setShowAbout(true);
    loadAboutFor(myUid, otherUid)
      .then((a) => { if (live) setAbout(a); })
      .catch(() => { if (live) setAbout(null); });
    return () => { live = false; };
  }, [aboutEligible, myUid, otherUid]);

  /* a status that runs out while the chat is open should go, not linger */
  useEffect(() => {
    if (!about?.expiresAt) return undefined;
    const msLeft = about.expiresAt - Date.now();
    if (msLeft <= 0) { setAbout(null); return undefined; }
    const tm = setTimeout(() => setAbout(null), msLeft);
    return () => clearTimeout(tm);
  }, [about]);
  const [supportMenu, setSupportMenu] = useState(null);
  useEffect(() => {
    if (!officialChat) { setSupportMenu(null); return; }
    /* re-opening is idempotent on the server and returns the current menu, so
       this is also how a reload gets the catalogue without a second endpoint */
    openSupport().then((o) => setSupportMenu(o.menu)).catch(() => {});
  }, [officialChat, chat.id]);

  /* 🟢 THE PROMPTS COME FROM THE THREAD, NOT FROM STATE. The server puts the
     category's follow-up questions and whether a screenshot helps onto the
     message it sent; reading them back off the newest such message means a
     reload, a second tab and a device that was offline all show the same
     prompts, with nothing to keep in sync. */
  const lastSupportAnswer = useMemo(() => {
    if (!officialChat) return null;
    for (let i = decMsgs.length - 1; i >= 0; i -= 1) {
      const m = decMsgs[i];
      if (m?.supportPayload && (m.supportKind === 'answer' || m.supportKind === 'ask')) {
        return m.supportPayload;
      }
    }
    return null;
  }, [officialChat, decMsgs]);
  /* the link row behind this chat — when the link was made, what it was for, and
     the passcode while it is still unclaimed. Owner-readable only, by rule. */
  const [guestLink, setGuestLink] = useState(null);
  useEffect(() => {
    if (!guestChat) { setGuestLink(null); return undefined; }
    return watchGuestLinkForChat(chat.id, myUid, setGuestLink);
  }, [guestChat, chat.id, myUid]);

  // ── blocking: same two directions the app derives, same precedence (mine first) ──
  const blockedByMe = !!(blocking && otherUid && blocking.byMe.has(otherUid)) && !chat.isGroup;
  const blockedMe = !!(blocking && otherUid && blocking.me.has(otherUid)) && !chat.isGroup;
  const anyBlock = blockedByMe || blockedMe;
  const [blockAsk, setBlockAsk] = useState(null);   // 'block' | 'unblock' | null

  // ── stars, delete, and the burger-menu tools ──────────────────────────────
  const [starredByChat, setStarredByChat] = useState(new Map());
  useEffect(() => watchStarred(myUid, setStarredByChat), [myUid]);
  // memoized: `|| new Set()` bare would be a fresh object every render, and it sits in
  //  onStarMsg's dep array — a callback that changes every render defeats its purpose
  const starredSet = useMemo(() => starredByChat.get(chat.id) || new Set(), [starredByChat, chat.id]);

  const [toolsMode, setToolsMode] = useState(null);   // null | 'starred' | 'search' | 'appearance'

  // ── chat info: opens from the header, same squeeze slot as the tools panel.
  //    Width is user-draggable — wider is allowed, narrower than the default never.
  const INFO_W = 420;
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoW, setInfoW] = useState(INFO_W);
  useEffect(() => { setInfoOpen(false); setInfoW(INFO_W); }, [chat.id]);
  // the two panels share one slot — opening either closes the other
  const openTools = (mode) => { setInfoOpen(false); setToolsMode(mode); };
  const openInfo = () => { setToolsMode(null); setInfoOpen(true); };

  // ── calls: engine state feeds the header buttons (green / dim / start) ─────
  const [callState, setCallState] = useState(getCallState());
  useEffect(() => subscribeCalls(setCallState), []);

  // ── per-chat wallpaper & appearance (web-only, localStorage — never synced) ──
  const [appearance, setAppearance] = useState(() => loadAppearance(chat.id));
  useEffect(() => { setAppearance(loadAppearance(chat.id)); }, [chat.id]);
  const changeAppearance = (next) => { setAppearance(next); saveAppearance(chat.id, next); };
  const [hdrMenu, setHdrMenu] = useState(false);
  /* 💬 which finished call's in-call chat is open, by session id */
  const [callChatId, setCallChatId] = useState(null);
  const [delTarget, setDelTarget] = useState(null);   // the message the sheet is about


  const onStarMsg = useCallback((m) => {
    toggleStar(myUid, chat.id, m.id, starredSet.has(m.id)).catch(() => {});
  }, [myUid, chat.id, starredSet]);

  const doDeleteForMe = async (m) => {
    setDelTarget(null);
    if (typeof m?._albumIndex === 'number') { await removeAlbumItem(chat, m, m._albumIndex, myUid, 'me').catch(() => {}); return; } // 🧩
    if (noteSelf) { await eraseCompletely(chat.id, m.id); hideLocally(m.id); return; }
    const marked = await deleteForMe(chat.id, m.id, myUid);
    if (!marked) hideLocally(m.id);
  };
  const doDeleteForEveryone = async (m) => {
    setDelTarget(null);
    if (typeof m?._albumIndex === 'number') { await removeAlbumItem(chat, m, m._albumIndex, myUid, 'everyone').catch(() => {}); return; } // 🧩
    await deleteForEveryone(chat, m, myUid);
    // was it the newest visible message? then the chat row must say so, like the app
    const newest = decMsgs[decMsgs.length - 1];
    if (newest && newest.id === m.id) tombstonePreview(chat.id).catch(() => {});
  };
  const chatTitle = chat.isGroup
    ? groupTitle(chat)
    : noteSelf
      ? 'Note to Self'
      : (resolveName(profile) !== 'Allo user' ? resolveName(profile) : (chat.participantNames?.[otherUid] || 'Allo user'));

  // 🟢 JUMP TO THE ORIGINAL. The app centres the target instantly and flashes it grey
  //    (200ms in, 800ms hold, 1000ms out). Here the row carries a data-mid, so finding
  //    it is a query rather than an index lookup — which also means it degrades
  //    honestly: a quote whose original has been cleared or deleted simply doesn't move
  //    the view, instead of scrolling somewhere arbitrary.
  const jumpTo = useCallback((messageId) => {
    if (!messageId) return;
    const el = scrollRef.current?.querySelector(`[data-mid="${CSS.escape(String(messageId))}"]`);
    if (!el) {
      const msg = 'That message isn\u2019t loaded in this chat';
      setToast(msg);
      setTimeout(() => setToast((t) => (t === msg ? '' : t)), 4200);
      return;
    }
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    clearTimeout(flashTimers.current[0]);
    clearTimeout(flashTimers.current[1]);
    setFlashId(null);
    // the null → id round trip is what restarts the CSS animation on a repeat jump
    flashTimers.current[0] = setTimeout(() => setFlashId(messageId), 60);
    flashTimers.current[1] = setTimeout(() => setFlashId(null), 2200);
  }, []);

  /* 👍 "SOMEONE REACTED TO YOUR MESSAGE" — the phone's container, in the browser.
     On the way in, the newest reaction by someone else on one of MY messages made
     since I last left this chat; while I am here, each new one. Never for a message
     in sight (you can watch it land), never twice (a remembered set per chat). A
     click glides to the message and flashes it — inside a run of photos too. */
  const [rxToast, setRxToast] = useState(null);   // { id, emoji, who, what } | null
  const rxSinceRef = useRef(0);
  const rxSeenRef = useRef(new Set());
  const rxTimerRef = useRef(null);
  useEffect(() => {
    const vKey = `rt_lastvisit_${chat.id}`;
    const sKey = `rt_seen_${chat.id}`;
    try { rxSinceRef.current = Number(window.localStorage.getItem(vKey)) || (Date.now() - 12 * 60 * 60 * 1000); } catch { rxSinceRef.current = Date.now(); }
    try { rxSeenRef.current = new Set(JSON.parse(window.localStorage.getItem(sKey) || '[]')); } catch { rxSeenRef.current = new Set(); }
    setRxToast(null);
    const mark = () => { try { window.localStorage.setItem(vKey, String(Date.now())); } catch { /* blocked storage */ } };
    const onVis = () => { if (document.visibilityState === 'hidden') mark(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', mark);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('pagehide', mark); mark(); };
  }, [chat.id]);
  useEffect(() => {
    const t = setTimeout(() => {
      let best = null;
      for (const m of decMsgs) {
        if (!m || m.senderId !== myUid || m.isDeleted || m.deleted) continue;
        const r = m.reactions || {};
        for (const emoji of Object.keys(r)) {
          for (const uid of (Array.isArray(r[emoji]) ? r[emoji] : [])) {
            if (!uid || String(uid) === String(myUid)) continue;
            const ts = Number(m.reactionsAt?.[emoji]?.[uid]) || 0;
            const key = `${m.id}|${emoji}|${uid}`;
            if (ts > rxSinceRef.current && !rxSeenRef.current.has(key) && (!best || ts > best.ts)) best = { m, emoji, uid: String(uid), ts, key };
          }
        }
      }
      if (!best) return;
      rxSeenRef.current.add(best.key);
      try { window.localStorage.setItem(`rt_seen_${chat.id}`, JSON.stringify(Array.from(rxSeenRef.current).slice(-200))); } catch { /* blocked storage */ }
      // in sight → silent
      const host = scrollRef.current;
      const el = host?.querySelector(`[data-mid="${CSS.escape(String(best.m.id))}"]`);
      if (host && el) {
        const a = host.getBoundingClientRect(); const b = el.getBoundingClientRect();
        if (b.bottom > a.top + 8 && b.top < a.bottom - 8) return;
      }
      const m = best.m;
      const raw = m.type === 'text' ? String(m._plain || m.text || '').trim() : '';
      const what = raw ? (raw.length > 30 ? `${raw.slice(0, 30)}…` : raw)
        : (m.type === 'image' ? 'a photo' : m.type === 'video' ? 'a video' : m.type === 'images' ? 'an album'
          : m.type === 'audio' ? 'a voice message' : m.type === 'document' ? 'a document' : m.type === 'location' ? 'a location'
          : m.type === 'poll' ? 'a poll' : 'a message');
      setRxToast({ id: m.id, emoji: best.emoji, who: senderNameOf({ senderId: best.uid }), what });
      clearTimeout(rxTimerRef.current);
      rxTimerRef.current = setTimeout(() => setRxToast(null), 4500);
    }, 700);   // after the list has landed where it opens, so "in sight" is real
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decMsgs, chat.id, myUid]);

  // 🔎 the sidebar's search asks us to scroll to a hit. jumpTo already handles
  //    the "not in this window" case with its own toast, so this is just routing.
  useEffect(() => {
    const fn = (e) => {
      const d = e?.detail;
      if (!d || d.chatId !== chat.id || !d.messageId) return;
      jumpTo(d.messageId);
    };
    window.addEventListener('allo:jump-message', fn);
    return () => window.removeEventListener('allo:jump-message', fn);
  }, [chat.id, jumpTo]);

  const startReply = useCallback((m) => {
    setReplySrc(m);
    setShowEmoji(false);
    setTimeout(() => {
      const ta = document.querySelector('.composer textarea');
      if (ta) ta.focus();
    }, 0);
  }, []);

  const senderNameOf = useCallback((m) => {
    if (m.senderId === myUid) return myName;
    if (chat.isGroup) return m.senderName || chat.participantNames?.[m.senderId] || 'Allo user';
    return chatTitle;
  }, [myUid, myName, chat.isGroup, chat.participantNames, chatTitle]);

  /* 👍 WHO REACTED — the sheet holds an ID, not a message, for the same reason the
     phone's does: the roster is derived from the message's own `reactions` map, so
     a captured object would freeze the list the moment you change your own
     reaction from inside the sheet. */
  const [rxSheet, setRxSheet] = useState(null);   // { id, agg } | null
  const openReactions = useCallback((m, agg) => {
    if (m?.id) setRxSheet({ id: String(m.id), agg: agg || null });
  }, []);
  const closeReactions = useCallback(() => setRxSheet(null), []);
  /* 🟢 THE SAME WRITER THE GUEST PAGE USES, and the only change it needed was the
     recipient list: the server fans a reaction out per socket, so a group needs one
     event per member and a guest page has exactly one counterpart. */
  const pickReaction = useCallback((m, emoji) => {
    const members = chat.isGroup
      ? (chat.participants || []).filter((u) => !(chat.leftParticipants || []).includes(u))
      : [];
    const cur = m?.reactions || {};
    const mine = Object.keys(cur).find((e) => (cur[e] || []).map(String).includes(String(myUid))) || null;
    const removing = mine === emoji;
    toggleReaction({
      chatId: chat.id,
      message: m,
      myUid,
      myName,
      emoji,
      isGroup: !!chat.isGroup,
      recipients: chat.isGroup ? members : undefined,
      toUserId: chat.isGroup ? undefined : (chat.participants || []).find((u) => u !== myUid),
    }).catch(() => {});
    /* 👍 the chat list hears it too — the row moves up and reads "You reacted …"
       (and goes back on a removal), the same chat-document write the phone makes */
    const rows = decMsgsRef.current || [];
    let newest = null;
    for (const x of rows) if (x && !x.isDeleted && !x.deleted && (!newest || tsOf(x) > tsOf(newest))) newest = x;
    writeReactionPreview({ chat: reactChatRef.current || chat, message: m, emoji, removing, myUid, myName, newest })
      .catch((e) => console.warn('[reaction] chat-list preview write failed:', e?.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.id, chat.isGroup, chat.participants, chat.leftParticipants, myUid, myName]);

  // 🟥 THE ATTRIBUTION HAS TO BE RESOLVED HERE. Once the picker is open we've left the
  //    chat that knows the names — App only holds the message. `originalSenderName` is a
  //    STORED field on the forwarded doc (the recipient has no way to look up a person
  //    from a chat they aren't in), so it must be captured at the moment of the tap.
  //    🟥 And it has to be declared AFTER senderNameOf: a dep array is evaluated where
  //    it's written, so naming a `const` defined further down is a TDZ ReferenceError on
  //    the very first render — which ErrorBoundary quietly turns into a blank chat pane.
  const startForward = useCallback((m) => {
    onForward({
      message: m,
      originalSenderName: m.senderId === myUid ? (myName || 'You') : senderNameOf(m),
      sourceChatId: chat.id,
    });
  }, [onForward, myUid, myName, senderNameOf, chat.id]);

  /** Build the viewer payload for a message + a starting index. */
  const openMedia = useCallback((m, index = 0, itemsOverride = null) => {
    // 🔒 a covered item never reaches the fullscreen viewer, whatever route asked
    if (pmCovered(chat.id, m.id)) return;
    /* 🟢 AN OVERRIDE, FOR THE ONE SHAPE THIS CANNOT DERIVE. A legacy official
       message carries its files in `media[]` and has no `_media` for the queue
       to have resolved — see OfficialMedia. Everything else about the payload
       is the same, so only the items are handed in. */
    let items = itemsOverride;
    if (!items && m.type === 'images') {
      /* 🧩 only the items still part of the album; `index` is the item's own index */
      const _all = m._mediaBatch || [];
      const _vis = albumVisibleIndices({ ...m, images: Array.from({ length: _all.length || (m.images || []).length }) }, myUid);
      items = _vis.map((o) => ({ kind: _all[o]?.kind, url: _all[o]?.url }));
      index = Math.max(0, _vis.indexOf(index));
    }
    else if (!items && m.type === 'video') items = [{ kind: 'video', url: m._media }];
    else if (!items) items = [{ kind: 'image', url: m._media }];
    if (!items.some((i) => i.url)) return;
    onOpenMedia({
      items, index,
      senderName: senderNameOf(m),
      timestamp: tsOf(m),
      mine: m.senderId === myUid,
      caption: m._caption || m.caption || '',
    });
  }, [onOpenMedia, senderNameOf, myUid, chat.id]);

  /* 🧩 a tile of a run: the viewer pages through the run's items that have loaded */
  const openRun = useCallback((run, index = 0) => {
    const its = (run?.items || []);
    const ready = its.filter((it) => it._media);
    if (!ready.length || pmCovered(chat.id, run.id)) return;
    const start = Math.max(0, ready.indexOf(its[index]));
    onOpenMedia({
      items: ready.map((it) => ({ kind: it.type === 'video' ? 'video' : 'image', url: it._media })),
      index: start,
      senderName: senderNameOf(run),
      timestamp: tsOf(its[index] || run),
      mine: run.senderId === myUid,
      caption: '',
    });
  }, [onOpenMedia, senderNameOf, myUid, chat.id]);
  /* 🧩 one item of an album: forward just it, or delete just it */
  const onAlbumItem = useCallback((what, m, index) => {
    if (what === 'reply') {
      /* 🧩 a reply to ONE item: the quote reads as that photo / video and points at the album */
      const kind = m._mediaBatch?.[index]?.kind;
      const src = albumItemReplySource(m, index, kind ? kind === 'video' : undefined);
      if (src) startReply({ ...src, ...(kind !== 'video' && m._mediaBatch?.[index]?.url ? { _media: m._mediaBatch[index].url } : {}) });
      return;
    }
    if (what === 'forward') startForward({ ...m, _forwardIndices: [index] });
    else setDelTarget({ ...m, _albumIndex: index });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startForward, startReply]);

  // ══════════════════════════════════════════════════════════════════════════
  //  ATTACHMENTS — picker, drag-and-drop, paste
  // ══════════════════════════════════════════════════════════════════════════
  const flash = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast((t) => (t === msg ? '' : t)), 4200);
  }, []);

  const addFiles = useCallback(async (fileList) => {
    const all = Array.from(fileList || []);
    if (!all.length) return;

    // 🟢 anything that isn't a photo or a clip travels as a DOCUMENT. Several documents
    //    become several standalone messages — the phone has no album equivalent for
    //    them (ChatModal ~8874 loops one at a time), so neither do we.
    const docs = all.filter((f) => isDocFile(f));
    if (docs.length) {
      setTray((t) => [
        ...t,
        ...docs.slice(0, MAX_ATTACHMENTS - t.length).map((file) => ({
          id: newTempId(), file, kind: 'doc', url: null, prepared: true, error: null,
        })),
      ]);
    }

    const files = all.filter((f) => !isDocFile(f) && (isImageFile(f) || isVideoFile(f)));
    if (!files.length) return;

    // stage instantly with a local preview; the heavy prep (decode, thumbnail,
    // dimensions) runs behind it so the tray never feels blocked
    const staged = files.slice(0, MAX_ATTACHMENTS).map((file) => ({
      id: newTempId(),
      file,
      kind: isVideoFile(file) ? 'video' : 'image',
      url: localPreviewUrl(file),
      prepared: null,
      error: null,
    }));
    setTray((t) => {
      const room = MAX_ATTACHMENTS - t.length;
      if (room <= 0) { flash(`You can attach up to ${MAX_ATTACHMENTS} items at once.`); return t; }
      return [...t, ...staged.slice(0, room)];
    });

    for (const s of staged) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const prepared = await prepareFile(s.file);
        setTray((t) => t.map((x) => (x.id === s.id ? { ...x, prepared } : x)));
      } catch (e) {
        setTray((t) => t.filter((x) => x.id !== s.id));
        flash(e?.message || 'That file could not be read.');
      }
    }
  }, [flash]);

  const removeTrayItem = (id) => setTray((t) => {
    const hit = t.find((x) => x.id === id);
    if (hit?.url) { try { URL.revokeObjectURL(hit.url); } catch {} }
    return t.filter((x) => x.id !== id);
  });

  // full-pane drop target. dragDepth counts enter/leave so moving over a child
  // element doesn't flicker the overlay off.
  // 🟥 TWO DIFFERENT THINGS CAN BE DROPPED HERE and they must never be confused: a file
  //    from the desktop (send it) and a chat row from the list (open it beside this one).
  //    dataTransfer.types is the only thing readable during a dragover — the payload
  //    itself is deliberately unreadable until drop — so the custom MIME type is what
  //    distinguishes them, and each shows its own overlay.
  const CHAT_MIME = 'text/allo-chat';
  const isChatDrag = (e) => !!e.dataTransfer?.types?.includes(CHAT_MIME);
  const isFileDrag = (e) => !!e.dataTransfer?.types?.includes('Files');

  const onDragEnter = (e) => {
    if (isChatDrag(e)) { if (onDropChat) { dragDepth.current += 1; setChatOver(true); } return; }
    if (!isFileDrag(e)) return;
    dragDepth.current += 1; setDragOver(true);
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) { setDragOver(false); setChatOver(false); }
  };
  const onDrop = (e) => {
    e.preventDefault(); dragDepth.current = 0; setDragOver(false); setChatOver(false);
    if (isChatDrag(e)) {
      const id = e.dataTransfer.getData(CHAT_MIME);
      if (id && id !== chat.id) onDropChat?.(id);
      return;
    }
    if (anyBlock) return;                 // no attachments into a blocked chat, either
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  // paste a screenshot straight into the chat
  useEffect(() => {
    const onPaste = (e) => {
      const files = Array.from(e.clipboardData?.files || []);
      if (files.length) { e.preventDefault(); addFiles(files); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [addFiles]);

  // ══════════════════════════════════════════════════════════════════════════
  //  SEND
  // ══════════════════════════════════════════════════════════════════════════
  /**
   * 🟢 CONSUMING THE DRAFT REPLY. Sealing needs the participants' device directory, so
   *    it's async — which means it has to happen ONCE, up front, and the resulting
   *    bundle gets handed to whichever send path runs. Clearing `replySrc` immediately
   *    (rather than after the await) is deliberate: the strip disappears the instant you
   *    press send, and a second Enter can't attach the same quote twice.
   */
  const takeReply = useCallback(async () => {
    const src = replySrc;
    if (!src) return { fields: {}, quote: null };
    setReplySrc(null);
    // 🟢 `quote` is the PLAINTEXT copy, for the optimistic bubble only. `fields` is the
    //    redacted, sealed one that goes to Firestore — the two must never be swapped,
    //    which is why they're separate return values rather than one object the caller
    //    might spread by accident.
    const quote = quoteFrom(src);
    /* 🟥 AN ABOUT REPLY IS NOT SEALED, because the phone does not seal it. It
       writes the quote as plaintext and renders `replyTo.type === 'about'` as a
       floating bubble; a sealed one would arrive there as a padlock inside that
       bubble. The status is already visible to whoever can see it — the sealing
       here protects message content, and this is not message content. */
    if (src.type === 'about') return { fields: { replyTo: quote }, quote };
    try {
      return { fields: await buildReplyFields(src, sealMembers(chat)), quote };
    } catch {
      return { fields: {}, quote: null };   // a quote is never worth losing the message over
    }
  }, [replySrc, chat.participants]);

  const sendTray = useCallback(async (entries, caption, reply, quote) => {
    const clientTempId = newTempId();
    const kinds = entries.map((e) => e.kind);
    const localItems = entries.map((e) => ({ kind: e.kind, url: e.url }));
    const optimistic = {
      id: clientTempId,
      clientTempId,
      _pending: true,
      _entries: entries, // kept so Retry can re-send without re-picking the files
      // 🟥 the SEALED bundle and the PLAINTEXT quote both ride the optimistic bubble:
      //    the first so Retry re-attaches the quote instead of silently dropping it,
      //    the second so your own reply shows its quote immediately rather than popping
      //    it in a second later when the Firestore doc round-trips.
      _reply: reply, replyTo: quote || undefined,
      senderId: myUid,
      timestamp: Date.now(),
      type: entries.length > 1 ? 'images' : (kinds[0] === 'video' ? 'video' : 'image'),
      _caption: caption,
      _uploadPct: 0,
      _mediaResolved: true,
      ...(entries.length > 1
        ? { _mediaBatch: localItems, images: entries.map(() => ''), batchThumbnails: [] }
        : { _media: localItems[0].url, width: entries[0].prepared?.width, height: entries[0].prepared?.height }),
    };
    setPending((p) => [...p, optimistic]);
    // 🟢 tell the other side WHAT is coming, like the phone does during an upload
    publishTyping(chat, myUid, myName, entries.length > 1 ? 'album' : (kinds[0] === 'video' ? 'video' : 'photo'));

    const patch = (fn) => setPending((p) => p.map((x) => (x.clientTempId === clientTempId ? { ...x, ...fn(x) } : x)));

    try {
      // wait for any item still being prepared in the background
      const items = [];
      for (const e of entries) {
        let prep = e.prepared;
        if (!prep || prep === true) {
          // eslint-disable-next-line no-await-in-loop
          prep = await prepareFile(e.file);
        }
        items.push(prep);
      }

      await sendMediaMessage({
        chat, myUid, myName, items, caption, tempId: clientTempId, reply,
        /* 🟥 THE CAPTION FIELD HERE IS THE COMPOSER — same textarea, different
           placeholder — so an "@" in it already opened the picker and already turned
           blue. It just was not being carried, which is the difference between a
           feature and a screenshot of one. Derived from the caption being SENT, not
           from what the picker recorded. */
        mentions: chat.isGroup ? collectMentions(caption || '', mentionMembers) : [],
        mentionsEveryone: !!chat.isGroup && textMentionsEveryone(caption || '', mentionMembers),
        onProgress: (f) => patch(() => ({ _uploadPct: Math.round(f * 100) })),
      });
      patch(() => ({ _uploadPct: 100 }));
      stopTyping(chat, myUid, myName);
      // the Firestore doc lands right after and retires this bubble (see the effect above)
    } catch (err) {
      stopTyping(chat, myUid, myName);
      console.warn('[media send] failed:', err);
      patch(() => ({ _failed: true, _uploadPct: null }));
      flash(err?.message || 'Could not send that media.');
    }
  }, [chat, myUid, myName, flash]);

  const retrySend = useCallback((msg) => {
    setPending((p) => p.filter((x) => x.clientTempId !== msg.clientTempId));
    if (msg._entries?.length) sendTray(msg._entries, msg._caption || '', msg._reply, msg.replyTo);
  }, [sendTray]);

  const discardSend = useCallback((msg) => {
    setPending((p) => p.filter((x) => x.clientTempId !== msg.clientTempId));
  }, []);

  /** Documents go one message each, with their own optimistic bubble + progress. */
  const sendDocs = useCallback(async (docs, reply, quote) => {
    // 🟢 several documents are several standalone messages (the phone does the same),
    //    so the quote attaches to the FIRST one only — otherwise dropping five files
    //    while replying puts the same quote block on five bubbles in a row.
    // 🟥 keyed on the INDEX, not on a flag cleared after a successful send: clearing it
    //    on success meant a first upload that failed handed the quote straight to the
    //    second document, and then the third, producing exactly the repetition this is
    //    supposed to prevent.
    for (let i = 0; i < docs.length; i += 1) {
      const d = docs[i];
      const carry = i === 0 ? reply : undefined;
      const clientTempId = newTempId();
      setPending((p) => [...p, {
        id: clientTempId, clientTempId, _pending: true, _entries: [d],
        ...(i === 0 && quote ? { replyTo: quote } : {}),
        senderId: myUid, timestamp: Date.now(), type: 'document',
        documentName: d.file.name, documentSize: d.file.size, documentType: d.file.type || '',
        _uploadPct: 0, _mediaResolved: true,
      }]);
      const patch = (fn) => setPending((p) => p.map((x) => (x.clientTempId === clientTempId ? { ...x, ...fn(x) } : x)));
      publishTyping(chat, myUid, myName, 'document');
      try {
        // eslint-disable-next-line no-await-in-loop
        await sendDocumentMessage({
          chat, myUid, myName, file: d.file, reply: carry,
          onProgress: (f) => patch(() => ({ _uploadPct: Math.round(f * 100) })),
        });
        patch(() => ({ _uploadPct: 100 }));
        stopTyping(chat, myUid, myName);
      } catch (err) {
        stopTyping(chat, myUid, myName);
        patch(() => ({ _failed: true, _uploadPct: null }));
        flash(err?.message || `Could not send ${d.file.name}.`);
      }
    }
  }, [chat, myUid, myName, flash]);

  // Leaving a chat forgets what was dismissed, exactly like the phone. This has to sit
  // ABOVE the detector: effects run in source order, so the reset lands first and the
  // detector immediately re-evaluates the composer against the new chat.
  useEffect(() => { flightDismissed.current = new Set(); setFlight(null); setReplySrc(null); }, [chat.id]);

  // 🟢 THE LINK PREVIEW STRIP. Detection is a regex; only the lookup is debounced, and
  //    400ms is deliberately longer than it needs to be — the point is that a URL pasted
  //    and sent in one motion never costs a request at all. If you pause, the card
  //    appears; if you don't, doSend uses whatever is already cached and moves on.
  useEffect(() => {
    clearTimeout(linkTimer.current);
    const url = firstUrl(text);
    if (!url || linkDismissed.current.has(url)) { setLink(null); return undefined; }

    const hit = cachedLink(url);
    if (hit) { setLink({ url, meta: hit }); return undefined; }
    if (hit === null) { setLink(null); return undefined; }   // known to have no preview

    let live = true;
    linkTimer.current = setTimeout(async () => {
      const meta = await fetchLink(url);
      // the composer may have moved on while we were waiting — never show a stale card
      if (live && meta && firstUrl(text) === url) setLink({ url, meta });
    }, 400);
    return () => { live = false; clearTimeout(linkTimer.current); };
  }, [text]);

  useEffect(() => { linkDismissed.current = new Set(); setLink(null); }, [chat.id]);

  // 🟢 THE COMPOSER PREVIEW. Detection itself is synchronous and free (a regex plus a
  //    table lookup), so it runs on every keystroke; only the NETWORK call is debounced.
  //    700ms is chosen against the thing the user actually complained about — typing
  //    "AA123" and hitting Enter immediately. That send happens long before this fires,
  //    which is correct: the preview never appears, no request is made, and doSend below
  //    still produces a flight card. The preview is an affordance for people who pause,
  //    not a step in the send path.
  useEffect(() => {
    clearTimeout(flightTimer.current);
    const det = detectFlight(text);
    if (!det || flightDismissed.current.has(det.canonical)) { setFlight(null); return undefined; }

    const hit = cachedFlight(det.canonical);
    if (hit) { setFlight({ detection: det, data: hit, busy: false, failed: false }); return undefined; }
    if (hit === null) { setFlight({ detection: det, data: null, busy: false, failed: true }); return undefined; }

    setFlight({ detection: det, data: null, busy: true, failed: false });
    let live = true;
    flightTimer.current = setTimeout(async () => {
      const r = await lookupFlight(det.canonical);
      if (!live) return;
      // the message may have moved on while we were waiting — never show a stale card
      setFlight((cur) => (cur?.detection?.canonical !== det.canonical ? cur
        : { detection: det, data: r.data, busy: false, failed: !r.data }));
    }, 700);
    return () => { live = false; clearTimeout(flightTimer.current); };
  }, [text, chat.id]);

  const dismissFlight = () => {
    if (flight?.detection) flightDismissed.current.add(flight.detection.canonical);
    setFlight(null);
  };

  /**
   * 🟢 "I type AA123 and send it very fast" — the card must still appear.
   *
   * The phone gives up here: ChatModal only builds a card when the typing-time preview
   * ALREADY resolved, so a fast send silently degrades to plain text. That's the bug.
   *
   * Instead we give the lookup a 1.8s budget — long enough that a warm API almost always
   * wins, short enough that nobody notices — and then send regardless. If it lost the
   * race the message goes out with NO flightData and gets patched the moment the answer
   * lands, which is precisely the shape the app's own healing rule repairs
   * (`newMsg.flightData && !oldMsg.flightData`).
   */
  const sendFlight = async (detection, body, reply, quote) => {
    const clientTempId = `temp-flight-${Date.now()}`;
    let data = cachedFlight(detection.canonical) || null;

    // 🟢 the bubble appears NOW, before the lookup — the card inside it resolves itself
    //    from the same cache a moment later, so a fast send looks instant and simply
    //    fills in, rather than sitting on a cleared composer with nothing on screen.
    setPending((p) => [...p, {
      id: clientTempId, clientTempId, _pending: true, type: 'flight',
      senderId: myUid, timestamp: Date.now(),
      text: body, flightNumber: detection.canonical,
      ...(data ? { flightData: data } : {}),
      ...(quote ? { replyTo: quote } : {}),
    }]);

    // 🟥 WAITING HERE IS DELIBERATE, and it is about the RECIPIENT, not us.
    //    The socket relay is what actually reaches their phone, and it carries whatever
    //    flightData we have AT THAT MOMENT. A 1:1 message doc is deleted by the server's
    //    store-and-forward sweep once delivered, so a later patchFlightData() may find
    //    nothing to patch — leaving them with a permanent "Flight data unavailable".
    //    Spending up to 2.5s to send ONE complete message beats sending an empty one
    //    fast and hoping the repair lands.
    if (!data) {
      const r = await lookupWithDeadline(detection.canonical, 2500);
      data = r.data || null;
    }

    try {
      const id = await sendFlightMessage({ chat, myUid, myName, text: body, detection, data, clientTempId, reply });
      if (!data) {
        // still worth trying: it repairs our own copy, every group member, and any 1:1
        // recipient who hadn't come online yet
        lookupFlight(detection.canonical).then((r) => {
          if (r.data) patchFlightData(chat.id, id, r.data);
        });
      }
    } catch (err) {
      console.warn('[flight send] failed:', err);
      setPending((p) => p.map((x) => (x.clientTempId === clientTempId ? { ...x, _failed: true } : x)));
      flash('Could not send that flight card.');
    }
  };

  const doSend = async () => {
    // 🟥 belt to the composer's braces: the composer is replaced while blocked, but
    //    drag-and-drop and programmatic sends don't pass through the composer
    if (anyBlock) return;
    // 🟢 TAP LOCK (same reason the phone has docSendTapLockRef): `tray` is read from
    //    this render's closure, so two sends fired in the SAME tick — Enter plus a
    //    click, or a double-click — both see the un-cleared tray and send it twice.
    //    setTray([]) can't prevent that; only a synchronous ref can.
    if (sendLockRef.current) return;
    sendLockRef.current = true;
    setTimeout(() => { sendLockRef.current = false; }, 500);

    stopTyping(chat, myUid, myName);
    const clean = text.trim();
    const entries = tray;

    // 🟥 BAIL BEFORE CONSUMING THE DRAFT REPLY. Enter on an empty composer is a reflex —
    //    and the composer is auto-focused the moment you tap Reply. Sealing first meant
    //    that stray Enter silently swallowed the quote: the strip vanished, nothing was
    //    sent, and the user had to hunt for the message and tap Reply again.
    if (!entries.length && !clean) { sendLockRef.current = false; return; }

    // 🟥 CLEAR THE COMPOSER BEFORE THE AWAIT. Sealing a quote is a network round trip —
    //    one device-directory read per participant — which on a group easily outruns the
    //    500ms tap lock. With the clear *after* the await, the text and the tray were
    //    still sitting there when the lock released: a second Enter sent the whole thing
    //    again, files and all. Clearing synchronously closes that window for good.
    setTray([]); setText(''); setShowEmoji(false);
    // 🟢 pressing send while reading history should bring you back to the conversation —
    //    otherwise your own message lands somewhere below the fold and the jump button
    //    lights up for a message you just wrote yourself
    scrollToEnd();
    const { fields: reply, quote } = await takeReply();

    if (entries.length) {
      const docs = entries.filter((e) => e.kind === 'doc');
      const media = entries.filter((e) => e.kind !== 'doc');
      // 🟢 the quote rides the FIRST thing sent and nothing else. Attaching it to every
      //    file in a five-document drop would quote the same message five times.
      if (docs.length) await sendDocs(docs, reply, quote);
      if (media.length) await sendTray(media, clean, docs.length ? {} : reply, docs.length ? null : quote);
      return;
    }

    // 🟥 "if I send fast before the preview appears, don't show one." So the send reads
    //    the CACHE and never awaits: a URL you pasted and fired in one motion goes out
    //    as plain text, and the card fills itself in a moment later through the backfill
    //    below — on the recipient's screen too. Waiting here would trade a guaranteed
    //    instant send for a card nobody asked to wait for.
    const linkUrl = firstUrl(clean);
    const linkMeta = linkUrl && !linkDismissed.current.has(linkUrl) ? cachedLink(linkUrl) || null : null;
    setLink(null);

    // 🟥 detect against the text being SENT, not against `flight` — the preview may
    //    still be mid-debounce (fast send) or describe an older draft.
    const det = detectFlight(clean);
    if (det && !flightDismissed.current.has(det.canonical)) {
      setFlight(null);
      await sendFlight(det, clean, reply, quote);
      return;
    }

    /* ✦ ASKING ALLO AI. The question is sent like any other message — sealed,
       relayed, previewed in the chat list — and then answered. `askInChat` owns
       the order of those steps and the marker that tells every other device,
       yours and theirs, that something is being written. */
    if (aiMode || isAiAsk(clean)) {
      setAiMode(false);
      askInChat({
        chatId: chat.id, participants: sealMembers(chat), senderId: myUid,
        senderName: myName, isGroup: !!chat.isGroup, text: clean, reply,
        groupName: chat.isGroup ? (chat.groupName || chat.name || null) : null,
        /* ✦ WHO ELSE WILL READ THE ANSWER, BY NAME. `participantNames` is the
           map the group surfaces already trust — a member's own profile can be
           missing or stale, this cannot. */
        otherNames: (chat.isGroup
          ? (chat.participants || []).filter((u) => u !== myUid).map((u) => chat.participantNames?.[u])
          : [profile?.fullName || chat.otherUser?.fullName])
          .map((x) => String(x || '').trim()).filter(Boolean),
      }).then((r) => { if (!r.ok && r.reason === 'ai_failed') setAiError(Date.now()); });
      return;
    }

    const sentId = await sendTextMessage({
      chatId: chat.id, participants: sealMembers(chat), senderId: myUid,
      text: clean, isGroup: !!chat.isGroup, reply, linkMetadata: linkMeta,
      /* 🟥 DERIVED FROM THE TEXT BEING SENT, not from what the picker recorded.
         Pick a name, backspace over it, send — a list kept by the picker still holds
         that uid, and somebody is told they were mentioned in a message that does
         not contain their name. */
      mentions: chat.isGroup ? collectMentions(clean, mentionMembers) : [],
      mentionsEveryone: !!chat.isGroup && textMentionsEveryone(clean, mentionMembers),
    });

    // it went without a card — resolve one and patch the message in place
    if (linkUrl && !linkMeta && !linkDismissed.current.has(linkUrl) && sentId) {
      fetchLink(linkUrl).then((meta) => {
        if (!meta) return;
        setLinkPatch((prev) => ({ ...prev, [sentId.clientTempId]: meta }));
        backfillLink({
          chatId: chat.id, messageId: sentId.id, clientTempId: sentId.clientTempId,
          participants: sealMembers(chat), myUid, metadata: meta,
        });
      });
    }
  };

  // 🟢 BUBBLE GROUPING — mirrors the app exactly: two messages are "connected" when
  //    they're from the SAME sender and within 60s of each other. The list here is
  //    oldest→newest, so prev = older, next = newer.
  //      middle = connected both sides · top = only a newer one · bottom = only an
  //      older one · single = neither. The tail lives on 'single' | 'bottom'.
  // the quoted message itself, when it's still on screen — the source of every rich
  // detail the snapshot doesn't carry (a voice note's length, a poll's question, a
  // flight's route). Absent is fine; ReplyQuote falls back to the snapshot.
  const msgById = useMemo(() => {
    const map = new Map();
    decMsgs.forEach((m) => { if (m.id) map.set(m.id, m); });
    return map;
  }, [decMsgs]);
  msgByIdRef.current = msgById;

  /* ✦ IS ALLO AI WORKING RIGHT NOW?
     🟥 IT IS NOT ENOUGH TO READ THE FIELD, because the answer changes with no
        data change: a marker whose client died simply ages out, and nothing
        will arrive to re-render this. So it is re-asked on a slow tick while it
        is true, and only while it is true — an idle chat schedules nothing. */
  /* 🟢 WHEN THE NEWEST ASSISTANT MESSAGE LANDED. An answer newer than the
     marker has answered it — the exact signal that ends a request, whatever
     happened to the write that was supposed to clear the field. */
  const lastAiMessageAt = useMemo(() => {
    let best = 0;
    for (const m of decMsgs || []) {
      if (!m || m.isSkeleton) continue;
      if (m.senderId !== 'allo-ai-bot' && !m.isAI) continue;
      const ms = tsOf(m);
      if (ms > best) best = ms;
    }
    return best;
  }, [decMsgs]);

  /* 🟢 WHEN THIS CLIENT FIRST SAW THE CURRENT MARKER. The grace is measured
     from here, not from the marker's own stamp — which on the asking client is
     an unresolved server timestamp for exactly the moment the grace is about. */
  const aiSinceRef = useRef(0);
  const [aiThinking, setAiThinking] = useState(false);
  useEffect(() => {
    if (!chat?.aiPending) { aiSinceRef.current = 0; setAiThinking(false); return undefined; }
    if (!aiSinceRef.current) aiSinceRef.current = Date.now();
    const read = () => setAiThinking(aiPendingShow(chat, {
      lastAiAt: lastAiMessageAt, since: aiSinceRef.current,
    }));
    read();
    /* 🟥 A TICK OF FIVE SECONDS CANNOT SERVE A GRACE OF LESS THAN ONE. The
       marker arrives one state change ago and the answer has not arrived yet,
       so without a timer for the grace itself the indicator would appear
       whenever some unrelated update happened to repaint — which is worse than
       showing it instantly, because it is arbitrary. */
    const soon = setTimeout(read, AI_PENDING_GRACE_MS + 30);
    /* …and the slow tick stays, for the marker whose client died: that answer
       changes with no data change, and nothing will arrive to re-render it. */
    const id = setInterval(read, 5000);
    return () => { clearTimeout(soon); clearInterval(id); };
  }, [chat?.aiPending, chat?.id, lastAiMessageAt]);

  /* 🟢 A ROW KEEPS ITS IDENTITY when nothing about it changed — the source message,
     its grouping and its late flag — so the memoised Bubble above can skip it. This
     rebuild runs on every snapshot and used to hand EVERY row a fresh object. */
  const groupedRowCache = useRef(new Map());
  /* 🧩 THE VISIT: only what was already here when the chat opened is grouped into runs
     (services/mediaRuns) — what arrives while you are in it stays single until next time */
  /* rule 3: what was unread when the chat opened stays single, and so does my own until
     the other side has seen it — judged when the chat opens (services/mediaRuns) */
  const mkRunVisit = () => newRunVisit({
    myUid,
    unreadAtOpen: Number(chat.unreadCounts?.[myUid]) || 0,
    seenByOthers: (m) => deriveReceiptStatus(m, chat, myUid) === 'read',
    settled: (m) => isSettledIn(settledFor(chat.id), m),   // rule 5: grouped before → grouped again
  });
  const runVisitRef = useRef(null);
  if (!runVisitRef.current) runVisitRef.current = mkRunVisit();
  const runCacheRef = useRef(new Map());
  const runChatRef = useRef(chat.id);
  const grouped = useMemo(() => {
    const prevRows = groupedRowCache.current;
    const nextRows = new Map();
    const all0 = [...decMsgs, ...pending].map((m) => (
      !m.linkMetadata && m.clientTempId && linkPatch[m.clientTempId]
        ? { ...m, linkMetadata: linkPatch[m.clientTempId] }
        : m
    ));
    /* a new chat in the same window is a new visit — decided here, in the same pass,
       so its first frame is never drawn with the last chat's visit */
    let runFresh = false;
    if (runChatRef.current !== chat.id) { runChatRef.current = chat.id; runVisitRef.current = mkRunVisit(); runCacheRef.current = new Map(); runFresh = true; }
    /* the runs are built newest-first, the phone's order; this list reads oldest-first.
       The pass that switched chats may still hold the last chat's rows — it never closes the baseline. */
    admitRows(runVisitRef.current, all0.slice().reverse(), decMsgs.length > 0 && !runFresh);
    const all = buildMediaRuns(all0.slice().reverse(), {
      kindOf: (m) => runKindInVisit(runVisitRef.current, m),
      cache: runCacheRef.current,
    }).slice().reverse();
    settleRuns(chat.id, all);   // rule 5: what is drawn together stays together on later visits
    const out = []; let lastDay = '';
    const connected = (a, b) =>
      !!a && !!b && a.senderId === b.senderId &&
      a.type !== 'system' && b.type !== 'system' &&
      Math.abs(tsOf(a) - tsOf(b)) < 60000;

    /* one day label per row — the next row's was being formatted a second time */
    const days = all.map((m) => dayLabel(m.timestamp));
    all.forEach((m, i) => {
      const d = days[i];
      const older = i > 0 ? all[i - 1] : null;
      const newer = i < all.length - 1 ? all[i + 1] : null;
      const dayBreak = d && d !== lastDay;
      if (dayBreak) { out.push({ _sep: d, id: `sep-${d}-${m.id}` }); lastDay = d; }

      // a day separator breaks the run above it
      const connOlder = !dayBreak && connected(m, older);
      const connNewer = connected(m, newer) && days[i + 1] === d;

      let groupPosition = 'single';
      if (connNewer && connOlder) groupPosition = 'middle';
      else if (connNewer && !connOlder) groupPosition = 'top';
      else if (!connNewer && connOlder) groupPosition = 'bottom';

      /* 🟢 LATE ARRIVAL. `older` is all[i - 1], which in this oldest-first list is
         the row drawn directly ABOVE — the only neighbour that can make this
         bubble's time look wrong. This loop already builds a new object per row,
         so the flag is free. (The phone asks the same question at i + 1, because
         its list is newest-first and its FlatList is inverted.) */
      const _late = showsLateSendTime(m, older);
      const _was = prevRows.get(m.id);
      const row = (_was && _was.src === m && _was.row._groupPosition === groupPosition && _was.row._lateSend === _late)
        ? _was.row
        : { ...m, _groupPosition: groupPosition, _lateSend: _late };
      if (m.id) nextRows.set(m.id, { src: m, row });
      out.push(row);
    });
    groupedRowCache.current = nextRows;
    return out;
  }, [decMsgs, pending, linkPatch, chat.id]);

  const canSend = !!text.trim() || tray.length > 0;

  return (
    <div
      className={`allo-chat ${(toolsMode || infoOpen) ? 'tools-open' : ''} ${infoOpen ? 'info-open' : ''} ${apClasses(appearance)}`}
      style={{ ...(infoOpen ? { '--tools-w': `${infoW}px` } : {}), ...(apStyle(appearance) || {}) }}
      ref={rootRef}
      onDragEnter={onDragEnter}
      onDragOver={(e) => { if (isFileDrag(e) || (onDropChat && isChatDrag(e))) e.preventDefault(); }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="chat-header">
        {/* 🟥 SHOWN BY CSS, NOT BY window.innerWidth. This read the width once, at
            render — so the button was decided by whatever the window happened to
            be when the chat mounted and never changed afterwards. Rotate a phone,
            drag a desktop window narrow, or open a chat before the layout settles
            and you get the wrong answer with no way back to the list, because
            React has no reason to re-render on a resize. The media query in
            mobile.css owns this now, and it is right at every width, always. */}
        <button className="icon-btn chat-back" onClick={onBack} aria-label="Back to chats"><Back /></button>
        <Avatar profile={profile} chat={chat} peerUid={otherUid} size="sm" myUid={myUid}
          /* 🧑‍🎨 live in the header: warmed on open, gated like the word "online" */
          live={{ online: !!online, canSeeOnline: !!presenceVis.canSeeOnline, viewKey: `header:${otherUid || chat?.id}`, priority: 2, warm: true }} />
        {/* 💬 their status, hanging under their picture — tapping it quotes the
            status so you can answer it, which is what it is for. The phone
            builds the identical replyTo, so a reply sent from either client
            renders the same on both. */}
        {showAbout && !!about && (
          <button
            className="about-bubble"
            title="Reply to this status"
            onClick={() => { setReplySrc(aboutReplyTarget(otherUid, about.text)); setShowAbout(false); }}
          >
            {about.text}
          </button>
        )}
        {/* 🟢 the header IS the door to chat info — like the app */}
        <div className="who who-click" onClick={openInfo} role="button" tabIndex={0} title="Chat info"
          onKeyDown={(e) => { if (e.key === 'Enter') openInfo(); }}>
          <div className="name">
            {chatTitle}{noteSelf && <Verified />}
            {!chat?.isGroup && otherUid ? <SupporterBadge uid={otherUid} size={15} /> : null}
            {officialChat && (
              <span className="official-badge" title="Official Allo account">
                <Verified width={15} height={15} />
              </span>
            )}
            {/* 🔗 the same mark the chat list carries, so a temporary chat reads
                as temporary from either surface */}
            {guestChat && (
              <span className={`temp-chip ${guestOver ? 'dead' : ''}`}
                title={guestOver ? 'This temporary chat has ended' : 'Temporary chat'}>
                <Timer width={11} height={11} />
              </span>
            )}
          </div>
          {/* 🟥 THE GREEN BELONGS TO THE WORD "online", NOT TO THE WHOLE LINE. On a
              guest chat the row now carries two facts, and colouring the row green
              painted the countdown green too — which reads as "the link is online".
              Guest chats get the colour on the word itself (below); every other
              chat keeps the row class it always had, so nothing else moves. */}
          <div className={`status ${online && presenceVis.canSeeOnline && !noteSelf && !guestChat ? 'online' : ''}`}>
            {chat.isGroup
              /* 🟢 the phone's own subtitle, in the phone's own priority order: the
                 restriction outranks the roster, and only one line is ever shown. */
              ? groupSubtitle(chat, myUid)
              : noteSelf
                ? 'Message yourself'
                : blockedByMe
                  ? 'You blocked this user'
                  : blockedMe
                    ? 'This user blocked you'
                    : guestChat
                      /* 🟥 THE COUNTDOWN REPLACED THE PRESENCE INSTEAD OF JOINING IT.
                         A guest chat showed `temporary · 17 hours left` and nothing
                         else — so the one line that tells you whether the person is
                         actually there was the only chat in the app that never had
                         it, and because the row still took the green `online` class
                         the countdown itself turned green, which read as "the LINK
                         is online". Both facts, in the order you need them: are they
                         here, and how long have we got. */
                      ? (guestOver
                        ? 'temporary chat ended'
                        : (
                          <>
                            {(online && presenceVis.canSeeOnline) ? (
                              <><span className="on">online</span>{' · '}</>
                            ) : presenceVis.canSeeLastSeen && lastSeenLabel(lastSeen) ? (
                              <>{lastSeenLabel(lastSeen)}{' · '}</>
                            ) : null}
                            {`temporary · ${guestLeftLabel(chat.expiresAt)}`}
                          </>
                        ))
                      : (online && presenceVis.canSeeOnline)
                        ? 'online'
                        : (presenceVis.canSeeLastSeen ? lastSeenLabel(lastSeen) : '')}
          </div>
        </div>
        {/* only in split view — a single pane has no "other" chat to fall back to */}
        {onClose && (
          <button className="icon-btn" onClick={onClose} title="Close this pane"><X /></button>
        )}
        {/* 🟢 CALL BUTTONS — live. Three states per button:
              • call with THIS chat (here or on the phone) → its kind glows green,
                the other kind dims; clicking green on a PHONE call pulls it here
              • call with ANOTHER chat → both alert "you're in an active call"
              • idle → dial */}
        {/* 🔗 …and never for a temporary guest chat. The other side is a browser
            holding a link, not an Allo account: there is no app to ring, no push
            channel a call could arrive on, and the conversation itself ends when
            the link does. So the pair is absent rather than present-and-failing —
            the same choice the phone's header, chat info and call picker make. */}
        {/* 💬 …and never for the official Allo account either. There is nobody
            on the other end to ring: it is a queue, not a person, and a phone
            icon that always failed would be worse than no phone icon. */}
        {/* 👥 A GROUP GETS THE SAME PAIR, gated on the group's own rule.
            🟥 THE GATE IS `permissions.makeCalls !== false`, OR being an admin —
               the same sentence ChatModal:2824 enforces on the phone, so an admin
               turning "Start calls" off from either client disables the buttons on
               both. It is one field on the chat document and both clients read it
               live, which is what "synced" means here: there is nothing to sync,
               because there is only one copy. */}
        {/* 🟢 A LIVE CALL REPLACES THE PAIR WITH ONE GREEN BUTTON, exactly as
            the phone's chat info does. `activeCall` is the server's own field, so
            a call started on a phone shows up here without either client telling
            the other anything — which is what makes picking it up on the other
            device possible at all.
            🟥 AND IT IS NOT GATED ON `makeCalls`. That permission governs who may
               START a call; the call is already happening, and refusing to let a
               member join one they can hear their group in would be a different
               rule that nobody asked for. */}
        {!noteSelf && chat.isGroup && !iLeftGroup && chat.activeCall?.participants?.length > 0 && (() => {
          const mine = getGroupCallState();
          const inThis = mine.status === 'active' && mine.groupId === chat.id;
          return (
            <button
              className="hdr-join"
              onClick={() => { if (!inThis) joinGroupCall(chat); }}
              title={inThis ? 'You are in this call' : 'Join the group call'}
            >
              {chat.activeCall.callType === 'voice'
                ? <Phone width={15} height={15} />
                : <Video width={15} height={15} />}
              {inThis ? 'In call' : 'Join'}
              <i>{chat.activeCall.participants.length}</i>
            </button>
          );
        })()}
        {!noteSelf && chat.isGroup && !iLeftGroup && !(chat.activeCall?.participants?.length > 0) && (() => {
          const blocked = groupCallBlockedReason(chat, myUid);
          const busy = callState.status !== 'idle' || getGroupCallState().status !== 'idle';
          const press = (kind) => {
            if (blocked) return;
            if (busy) { window.alert('You are in an active call. End it before starting another.'); return; }
            startGroupCall(chat, kind === 'video', myProfile);
          };
          return (
            <>
              <button
                className={`icon-btn${blocked ? ' call-dim' : ''}`}
                disabled={!!blocked}
                onClick={() => press('voice')}
                title={blocked || 'Voice call'}
                aria-label="Group voice call"
              ><Phone /></button>
              <button
                className={`icon-btn${blocked ? ' call-dim' : ''}`}
                disabled={!!blocked}
                onClick={() => press('video')}
                title={blocked || 'Video call'}
                aria-label="Group video call"
              ><Video /></button>
            </>
          );
        })()}
        {!noteSelf && !chat.isGroup && !guestChat && !officialChat && (() => {
          const busyHere = callState.status !== 'idle';
          const withThis = busyHere && callState.peerUid === otherUid;
          const elsewhereThis = !busyHere && callState.elsewhere?.peerUid === otherUid;
          const liveKind = withThis ? callState.callType
            : elsewhereThis ? callState.elsewhere.callType : null;
          const press = (kind) => {
            if (withThis) return;                      // already on it, right here
            if (elsewhereThis && kind === liveKind) { pullCallHere(); return; }  // continue here
            if (busyHere || callState.elsewhere) { window.alert('You are in an active call. End it before starting another.'); return; }
            if (iLeftGroup) return;
            /* 🚫 A BLOCK: the blocker is offered the door back, the blocked is told. */
            const nm = profile?.fullName || profile?.username || 'this person';
            if (blockedByMe) {
              if (!window.confirm(`You blocked ${nm}. Unblock and call?`)) return;
              unblockUser(myUid, otherUid).then(() => startCall(otherUid, kind === 'video', profile)).catch(() => window.alert('Could not unblock. Please try again.'));
              return;
            }
            if (blockedMe) { window.alert(`You can’t call ${nm} right now.`); return; }
            startCall(otherUid, kind === 'video', profile);
          };
          const cls = (kind) => `icon-btn${liveKind === kind ? ' call-live' : liveKind ? ' call-dim' : ''}`;
          return (
            <>
              <button className={cls('voice')} onClick={() => press('voice')}
                title={liveKind === 'voice' ? (withThis ? 'On a voice call' : 'Continue this call here') : 'Voice call'}
                aria-label="Voice call"><Phone /></button>
              <button className={cls('video')} onClick={() => press('video')}
                title={liveKind === 'video' ? (withThis ? 'On a video call' : 'Continue this call here') : 'Video call'}
                aria-label="Video call"><Video /></button>
            </>
          );
        })()}
        <div className="hdr-menu-wrap">
          <button className="icon-btn" onClick={() => setHdrMenu((s) => !s)} aria-label="Chat menu"><Menu /></button>
          {hdrMenu && (
            <>
              {/* click-away veil under the dropdown */}
              <div className="hdr-veil" onClick={() => setHdrMenu(false)} />
              <div className="hdr-menu">
                <button onClick={() => { setHdrMenu(false); openTools('starred'); }}>
                  <StarFill width={15} height={15} /> Starred messages
                </button>
                <button onClick={() => { setHdrMenu(false); openTools('search'); }}>
                  <SearchIcon width={15} height={15} /> Search
                </button>
                <button onClick={() => { setHdrMenu(false); openTools('appearance'); }}>
                  <ImageIcon width={15} height={15} /> Wallpaper &amp; appearance
                </button>
                {!chat.isGroup && !noteSelf && otherUid && (
                  <button
                    className={blockedByMe ? '' : 'danger'}
                    onClick={() => { setHdrMenu(false); setBlockAsk(blockedByMe ? 'unblock' : 'block'); }}
                  >
                    <Slash width={15} height={15} /> {blockedByMe ? 'Unblock user' : 'Block user'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="msg-scroll" ref={scrollRef} onScroll={trackScroll}>
        {/* 🟢 THE FIRST ROW OF THE THREAD, not a panel over it — it scrolls with the
            conversation and hides nothing. Rendered from the chat + link documents
            rather than from a message, so "Clear chat" cannot remove it. */}
        {guestChat
          ? <GuestCard chat={chat} link={guestLink} who={chatTitle} mine />
          : <div className="enc-banner"><Lock /> Messages are end-to-end encrypted. Only your devices can read them.</div>}
        <MediaCtx.Provider value={mediaCtx}>
        {grouped.map((m) => m._sep
          ? <div className="day-sep" key={m.id}><span className="day-chip">{m._sep}</span></div>
          : isHiddenSystem(m)
          /* control records and side-effect rows (delete_command, vote_action,
             reaction_action) are machinery — they must not occupy a line at all */
          ? null
          : isCallMessage(m)
          /* 🟥 THE CALL BRANCH NEEDS THE LINE TOO. A missed call counts as unread
             (countsAsUnread), so it can perfectly well BE the first unread thing — and
             with the divider only emitted on the bubble branch, that case rendered no
             line at all while the mark sat frozen and invisible for the whole session. */
          ? (
          <React.Fragment key={`f-${m.id}`}>
            <UnreadSep mark={unreadMark} id={m.id} />
            <CallRow m={m} myUid={myUid} chatId={chat.id} onOpenCallChat={setCallChatId} />
          </React.Fragment>
          )
          : m.systemType === 'group_invite'
          /* the invitation card is a system row with buttons, so it has to be
             matched BEFORE the generic branch swallows it */
          ? (
          <React.Fragment key={`f-${m.id}`}>
            <UnreadSep mark={unreadMark} id={m.id} />
            <GroupInviteCard
              m={m} chatId={chat.id} myUid={myUid} myName={myName}
              myImage={myProfile?.profileImage}
              onOpenChatId={onOpenChatId}
            />
          </React.Fragment>
          )
          : isSystemMessage(m)
          ? (
          <React.Fragment key={`f-${m.id}`}>
            <UnreadSep mark={unreadMark} id={m.id} />
            <SystemRow m={m} myUid={myUid} />
          </React.Fragment>
          )
          : m.type === 'media-run'
          /* 🧩 a run of single photos/videos/documents — each item still its own message */
          ? (
          <React.Fragment key={`f-${m.id}`}>
            {(m.items || []).some((it) => it.id === unreadMark?.anchorId) && <UnreadSep mark={unreadMark} id={unreadMark.anchorId} />}
            <RunRow
              run={m}
              mine={m.senderId === myUid}
              chat={chat}
              myUid={myUid}
              receiptsOn={!guestChat || rrOn}
              groupPosition={m._groupPosition}
              nameOf={senderNameOf}
              onOpenRun={openRun}
              onForward={startForward}
              onDelete={setDelTarget}
              onReply={startReply}
              onReact={openReactions}
              flashId={flashId}
            />
          </React.Fragment>
          )
          : (
          <React.Fragment key={`f-${m.id}`}>
          <UnreadSep mark={unreadMark} id={m.id} />
            <Bubble
              /* ✦ TAPPING THE MENTION ARMS THE COMPOSER rather than navigating.
                 On the phone it opens the assistant's own chat; this client
                 does not have one, and inventing a dead end would be worse than
                 doing the useful thing — which is to let you ask the next
                 question here, where the answer is going to appear anyway. */
              onAsk={() => { setAiMode(true); }}
              key={m.id}
              m={m}
              mine={m.senderId === myUid}
              onOpen={openMedia}
              groupPosition={m._groupPosition}
              chat={chat}
              myUid={myUid}
              myName={myName}
              onReply={startReply}
              onForward={startForward}
              onJump={jumpTo}
              nameOf={senderNameOf}
              quoted={m.replyTo ? msgById.get(m.replyTo.messageId) : null}
              flash={flashId === m.id}
              onRetry={() => setRetryTick((t) => t + 1)}
              onRetrySend={retrySend}
              onDiscard={discardSend}
              starred={starredSet.has(m.id)}
              onStar={onStarMsg}
              onDelete={setDelTarget}
              onAlbumItem={onAlbumItem}
              vibeGate={vibeGate}
              /* mutual receipts, guest chats only — see the note in Ticks */
              receiptsOn={!guestChat || rrOn}
              onOpenReactions={openReactions}
            />
          </React.Fragment>
          )
        )}
        </MediaCtx.Provider>
        <ReactionSheet
          open={!!rxSheet}
          m={rxSheet ? msgById.get(rxSheet.id) : null}
          agg={rxSheet?.agg || null}
          myUid={myUid}
          nameOf={senderNameOf}
          onPick={pickReaction}
          onClose={closeReactions}
          /* the "+": the sheet closes and the composer's emoji picker opens in reaction mode */
          onMore={(mm) => { closeReactions(); setReactionEmojiTarget(mm); setShowEmoji(true); }}
        />
        {/* ✦ ALLO AI IS THINKING — read off the chat document, so this shows on
            every device in the conversation rather than only on the one that
            asked. The phone concatenates its version into the message array,
            where two sort rules used to fight over it; here it sits after the
            list, where it cannot be sorted into the wrong place at all. */}
        {aiThinking && <AiThinking mine={chat?.aiPending?.by === myUid} />}
        {!anyBlock && <TypingBubble typers={typers} chat={chat} myUid={myUid} profiles={{ [otherUid]: profile }} />}
      </div>

      {infoOpen && (
        <ChatInfoPane
          chat={chat}
          chatTitle={chatTitle}
          subtitle={chat.isGroup
            ? groupSubtitle(chat, myUid)
            : (profile?.username ? `@${profile.username}` : '')}
          profile={profile}
          myUid={myUid}
          myName={myName}
          myProfile={myProfile}
          myPrivacy={myProfile?.privacySettings}
          otherUid={otherUid}
          msgs={decMsgs}
          mediaCtx={mediaCtx}
          nameOf={senderNameOf}
          onOpenMedia={openMedia}
          onClose={() => setInfoOpen(false)}
          onGroupCreated={(gid) => { setInfoOpen(false); onOpenChatId?.(gid); }}
          allChats={allChats}
          blocking={blocking}
          width={infoW}
          onWidth={setInfoW}
          defaultW={INFO_W}
        />
      )}

      {toolsMode === 'appearance' && (
        <AppearancePanel
          chat={chat}
          chatTitle={chatTitle}
          profile={profile}
          myUid={myUid}
          settings={appearance}
          onChange={changeAppearance}
          onReset={() => setAppearance(resetAppearance(chat.id))}
          onApplyAll={() => { applyToAllChats(chat.id, appearance); }}
          onClose={() => setToolsMode(null)}
        />
      )}

      {toolsMode && toolsMode !== 'appearance' && (
        <ChatTools
          mode={toolsMode}
          onModeChange={setToolsMode}
          onClose={() => setToolsMode(null)}
          msgs={decMsgs}
          starred={starredSet}
          myUid={myUid}
          nameOf={senderNameOf}
          onJump={(id) => { jumpTo(id); }}
          onUnstar={(id) => toggleStar(myUid, chat.id, id, true).catch(() => {})}
        />
      )}

      {blockAsk && (
        <div className="del-veil" onClick={() => setBlockAsk(null)}>
          <div className="del-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="del-ico"><Slash width={20} height={20} /></div>
            <b>{blockAsk === 'block' ? `Block ${chatTitle}?` : `Unblock ${chatTitle}?`}</b>
            <span className="del-sub">
              {blockAsk === 'block'
                ? 'They won’t be able to call you or message you, and you won’t be able to message them. This applies on your phone too.'
                : 'You’ll be able to message and call each other again, on every device.'}
            </span>
            <button
              className={`del-btn ${blockAsk === 'block' ? 'danger' : ''}`}
              onClick={async () => {
                setBlockAsk(null);
                if (blockAsk === 'block') await blockUser(myUid, otherUid).catch(() => {});
                else await unblockUser(myUid, otherUid).catch(() => {});
              }}
            >
              {blockAsk === 'block' ? 'Block' : 'Unblock'}
            </button>
            <button className="del-btn ghost" onClick={() => setBlockAsk(null)}>Cancel</button>
          </div>
        </div>
      )}

      {delTarget && (
        <DeleteSheet
          canEveryone={canDeleteForEveryone(delTarget, chat, myUid)}
          eraseMode={noteSelf}
          onForMe={() => doDeleteForMe(delTarget)}
          onForEveryone={() => doDeleteForEveryone(delTarget)}
          onCancel={() => setDelTarget(null)}
        />
      )}

      {showJump && (
        <button
          className={`jump ${jumpCount ? 'hot' : ''}`}
          onClick={scrollToEnd}
          title={jumpCount ? `${jumpCount} new message${jumpCount === 1 ? '' : 's'}` : 'Jump to latest'}
          aria-label="Jump to latest"
        >
          <ChevronsDown width={22} height={22} />
          {jumpCount > 0 && <span className="jump-badge">{jumpCount > 99 ? '99+' : jumpCount}</span>}
        </button>
      )}

      {toast && <div className="chat-toast" onClick={() => setToast('')}>{toast}</div>}
      {rxToast && (
        <button type="button" className="rx-toast" onClick={() => { const id = rxToast.id; setRxToast(null); jumpTo(id); }} aria-label={`${rxToast.who} reacted ${rxToast.emoji} — go to the message`}>
          <span className="rx-toast-emoji">{rxToast.emoji}</span>
          <span className="rx-toast-body">
            <span className="rx-toast-who"><b>{rxToast.who}</b> reacted</span>
            <span className="rx-toast-what">To: {rxToast.what}</span>
          </span>
        </button>
      )}

      {chatOver && (
        <div className="drop-veil chat">
          <div className="drop-card">
            <Forward width={30} height={30} />
            <div className="drop-title">Open side by side</div>
            <div className="drop-sub">Drop to keep both conversations open</div>
          </div>
        </div>
      )}

      {dragOver && (
        <div className="drop-veil">
          <div className="drop-card">
            <Camera width={34} height={34} />
            <div className="drop-title">Drop to send</div>
            <div className="drop-sub">Photos and videos · up to {MAX_ATTACHMENTS} at once</div>
          </div>
        </div>
      )}

      {showPoll && (
        <PollCreator
          isGroup={!!chat.isGroup}
          onClose={() => setShowPoll(false)}
          onCreate={async (cfg) => {
            /* 🟥 THE OPTIONS COUNT AS WELL AS THE QUESTION. "Who brings the cake —
               @Ali, @Nora" is a poll whose mentions live entirely in its options. */
            const _pollText = [cfg?.question || '', ...(cfg?.options || [])].join(' \n ');
            return createPoll({
              chat, myUid, myName, ...cfg, reply: await takeReply(),
              mentions: chat.isGroup ? collectMentions(_pollText, mentionMembers) : [],
              mentionsEveryone: !!chat.isGroup && textMentionsEveryone(_pollText, mentionMembers),
            });
          }}
        />
      )}

      {showLocation && (
        <LocationPicker
          onClose={() => setShowLocation(false)}
          onSend={async (place) => sendLocationMessage({
            chat, myUid, myName, lat: place.lat, lng: place.lng, name: place.name,
            reply: await takeReply(),
          })}
        />
      )}

      {showEmoji && (
        <div style={{ position: 'absolute', bottom: 78, left: 16, zIndex: 20 }}>
          {reactionEmojiTarget && (
            <div className="rx-mode-strip">
              <span>Pick a reaction</span>
              <button type="button" onClick={() => { setReactionEmojiTarget(null); setShowEmoji(false); }} aria-label="Cancel">×</button>
            </div>
          )}
          <EmojiPicker
            onEmojiClick={(e) => {
              if (reactionEmojiTarget) {
                pickReaction(reactionEmojiTarget, e.emoji);
                setReactionEmojiTarget(null);
                setShowEmoji(false);
                return;
              }
              setText((t) => t + e.emoji);
            }}
            theme={window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'}
            width={340}
            height={420}
          />
        </div>
      )}

      {tray.length > 0 && (
        <div className="tray">
          <div className="tray-strip">
            {tray.map((it) => (
              <div className="tray-item" key={it.id}>
                {it.kind === 'doc' ? (
                  <span className="tray-doc" style={{ borderColor: docStyle(it.file.name).color }}>
                    <span className="tray-doc-ext" style={{ background: docStyle(it.file.name).color }}>
                      {docStyle(it.file.name).ext || 'FILE'}
                    </span>
                    <span className="tray-doc-name">{it.file.name}</span>
                    <span className="tray-doc-size">{humanBytes(it.file.size)}</span>
                  </span>
                ) : it.kind === 'video'
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  ? <><video src={it.url} muted preload="metadata" /><span className="tray-play"><Play width={14} height={14} /></span></>
                  : <img src={it.url} alt="" />}
                {!it.prepared && <span className="tray-prep"><span className="mb-spin sm" /></span>}
                <button className="tray-x" onClick={() => removeTrayItem(it.id)} title="Remove"><X width={13} height={13} /></button>
              </div>
            ))}
            <button className="tray-add" onClick={() => fileRef.current?.click()} title="Add more">+</button>
          </div>
          <div className="tray-note">
            {tray.length} {tray.length === 1 ? 'item' : 'items'} · add a caption below, then send
          </div>
        </div>
      )}

      {replySrc && (
        <ReplyComposer
          message={replySrc}
          name={replySrc.senderId === myUid ? 'yourself' : senderNameOf(replySrc)}
          onCancel={() => setReplySrc(null)}
        />
      )}

      {!recording && link && (
        <LinkCard
          meta={link.meta}
          onClose={() => { linkDismissed.current.add(link.url); setLink(null); }}
        />
      )}

      {!recording && flight && (
        <FlightPreview
          detection={flight.detection}
          data={flight.data}
          busy={flight.busy}
          failed={flight.failed}
          onDismiss={dismissFlight}
        />
      )}

      {/* ✦ ARMED FOR ALLO AI — directly above the composer, in the same strip the
          reply quote and the link card use. */}
      {aiMode && <AiComposerCard onClose={() => setAiMode(false)} />}
      {!!aiError && (
        <div className="ai-card err">
          <div className="ai-card-t"><b>Allo AI could not answer</b><span>Your message was sent. Try asking again.</span></div>
          <button className="ai-card-x" onClick={() => setAiError(0)} aria-label="Dismiss"><X width={15} height={15} /></button>
        </div>
      )}

      {/* 💬 what was said DURING a finished call — see CallChatHistory */}
      {callChatId && (
        <CallChatHistory
          chatId={chat.id}
          callSessionId={callChatId}
          myUid={myUid}
          onClose={() => setCallChatId(null)}
        />
      )}

      {/* 💬 THE SUPPORT PANEL REPLACES THE COMPOSER, it does not sit beside it.
          Same reasoning as the admins-only lock below: a disabled textarea is a
          thing to try clicking, and the turn-taking would then need enforcing a
          second time on a write the person was invited to attempt. The server
          enforces it regardless — see /api/support/submit. */}
      {officialChat ? (
        <SupportPanel chat={chat} menu={supportMenu} lastAnswer={lastSupportAnswer} />
      ) : guestOver ? (
        <div className="composer locked">
          <Timer width={14} height={14} />
          <span>This temporary chat has ended. The conversation has been deleted.</span>
        </div>
      ) : iLeftGroup ? (
        /* the app's own words (group_exited_msg) */
        <div className="composer locked">
          <Logout width={14} height={14} />
          <span>You exited this group</span>
        </div>
      ) : anyBlock ? (
        /* the app's own words for both directions (msg_blocked_by_me / _by_them) */
        <div className="composer locked">
          <Slash width={14} height={14} />
          <span>
            {blockedByMe
              ? 'You blocked this user — unblock to send messages.'
              : 'This user has blocked you. You cannot send messages to them.'}
          </span>
          {blockedByMe && (
            <button className="lg-link" onClick={() => setBlockAsk('unblock')}>Unblock</button>
          )}
        </div>
      ) : !mayPost ? (
        /* 🟥 ADMINS ONLY. The phone doesn't grey the input out, it REPLACES it — no
           textarea, no attachment button, no microphone, nothing to type into and
           nothing to hopefully click. Anything less would be a lock the server would
           have to enforce a second time, on a write the member was invited to attempt. */
        <div className="composer locked">
          <Lock width={14} height={14} />
          <span>Only admins can send messages</span>
        </div>
      ) : recording ? (
        /* 🟢 the composer BECOMES the recorder — one strip, no popup, nothing jumps */
        <VoiceRecorder
          onCancel={() => { stopTyping(chat, myUid, myName); setRecording(false); }}
          onSend={async (out) => {
            const reply = await takeReply();
            await sendVoiceMessage({
              chat, myUid, myName, reply,
              bytes: out.bytes, durationSec: out.durationSec, waveform: out.waveform,
            });
            setRecording(false);
          }}
        />
      ) : (
      <>
      {mentionState && <MentionPicker results={mentionState.results} onPick={pickMention} />}
      <div className="composer">
        <button className="icon-btn emoji-btn" onClick={() => setShowEmoji((s) => !s)}><Smile /></button>
        <div className="field">
          <div className="ment-wrap">
          {/* ══ 🤖 THE BLUE NAME IN THE COMPOSER ════════════════════════
              🟥 A <textarea> CANNOT COLOUR PART OF ITS OWN VALUE. The only honest
                 options are a contenteditable (which breaks paste, IME, undo and
                 every keyboard shortcut a message field is expected to have) or a
                 mirror underneath. The mirror it is, with the field's own ink
                 turned transparent and both sharing every metric that decides
                 where a line wraps — see .ment-hi in theme.css.
              🟢 It exists only while a name does, so a composer with no mention is
                 byte-for-byte the one that shipped. */}
          {mentionSegs && (
            <div className="ment-hi" ref={hiRef} aria-hidden="true">
              {mentionSegs.map((seg, i) => (seg.uid
                ? <span key={i} className="m">{seg.text}</span>
                : <React.Fragment key={i}>{seg.text}</React.Fragment>))}
              {/* 🟥 A TRAILING NEWLINE IS NOT RENDERED BY A DIV and IS by a
                  textarea, so without this the last line drifts up by one row the
                  moment somebody presses shift+enter at the end. */}
              {'\u200b'}
            </div>
          )}
          <textarea
            ref={taRef}
            className={mentionSegs ? 'ment-on' : undefined}
            rows={1} placeholder={tray.length ? 'Add a caption…' : 'Type a message'} value={text}
            onScroll={(e) => { if (hiRef.current) hiRef.current.scrollTop = e.target.scrollTop; }}
            onSelect={(e) => { setCaret(e.target.selectionStart || 0); }}
            onKeyUp={(e) => { setCaret(e.target.selectionStart || 0); }}
            onClick={(e) => { setCaret(e.target.selectionStart || 0); }}
            onChange={(e) => {
        const v = e.target.value;
        /* 🟢 THE SAME TRIGGER THE PHONE USES, from the same module the send
           path asks — two regexes in two places is how a message gets the
           card and no answer, or an answer and no card. */
        if (AI_TRIGGER.test(v)) { setAiMode(true); setText(v.replace(/@ai$/i, '').trimStart()); return; }
        setText(v);
        /* 🟥 THE CARET, IN THE SAME EVENT. onSelect does not fire for ordinary
           typing in every browser, so a picker that waited for it opened a
           keystroke late — which is the keystroke somebody is looking at. */
        setCaret(e.target.selectionStart || 0);
        publishTyping(chat, myUid, myName);
      }}
            onKeyDown={(e) => {
              /* 🟥 ENTER BELONGS TO THE PICKER WHILE IT IS OPEN. Otherwise the
                 first name in the list is never reachable from the keyboard and
                 Enter sends a half-typed "@Al" instead. */
              if (mentionState && (e.key === 'Enter' || e.key === 'Tab')) {
                e.preventDefault();
                pickMention(mentionState.results[0]);
                return;
              }
              if (mentionState && e.key === 'Escape') { e.preventDefault(); setMentionOff(true); return; }
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doSend(); }
            }}
          />
          </div>
        </div>

        {/* 🟢 the four actions read as ONE control, not four loose glyphs competing with
            the text field — grouped in a rounded tray the way a toolbar should be */}
        <div className="tools">
          <button className="tool" title="Attach a file, photo or video" onClick={() => fileRef.current?.click()}><Paperclip width={19} height={19} /></button>
          <button className="tool" title="Send a location" onClick={() => setShowLocation(true)}><MapPin width={19} height={19} /></button>
          <button
            className="tool"
            title={canRecordVoice() ? 'Record a voice message' : "This browser can't record audio the phone apps can play"}
            onClick={() => { if (canRecordVoice()) { publishTyping(chat, myUid, myName, 'recording'); setRecording(true); } else flash("This browser can't record in a format the phone apps can play. Try Safari, or Chrome/Edge 130+."); }}
          ><Mic width={19} height={19} /></button>
          <button className="tool" title="Create a poll" onClick={() => setShowPoll(true)}><Poll width={19} height={19} /></button>
        </div>
        <button className="send-btn" onClick={doSend} disabled={!canSend}><Send /></button>
      </div>
      </>
      )}

      <input
        ref={fileRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}
